import { useState, useRef, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import { Wallet, BarChart3, LayoutDashboard, CalendarDays, PieChart, FolderOpen, Users, Camera, Plus, Lock, LogOut, Trash2, Pencil, Check, AlertTriangle, Circle, Save, FileText, FileSpreadsheet, Paperclip, MapPin, Ruler, ChevronDown, ChevronUp } from "lucide-react";

// ─── Supabase client ──────────────────────────────────────────────────────────
const _SURL = process.env.REACT_APP_SUPABASE_URL;
const _SKEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = (_SURL && _SURL.startsWith('https://') && _SKEY)
  ? createClient(_SURL, _SKEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        // Prepreči "Navigator LockManager" konflikte med zavihki (vzrok "another request stole it")
        lock: async (name, acquireTimeout, fn) => await fn(),
      },
    })
  : null;

// ─── helpers ──────────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 9);
const fmt  = n => Number(n||0).toLocaleString("sl-SI") + " €";
const fmtN = n => Number(n||0).toLocaleString("sl-SI");
const clamp = (v,lo,hi) => Math.max(lo, Math.min(hi, Number(v)||0));

// Number input that shows empty string when value is 0, clears on focus
function NumInput({ value, onChange, style={}, min=0, max, step }) {
  const [focused, setFocused] = useState(false);
  const [localVal, setLocalVal] = useState("");

  const displayVal = focused ? localVal : (value===0||value==="0"||value===""?"":value);

  return (
    <input
      type="number"
      min={min}
      max={max}
      step={step||"any"}
      value={displayVal}
      onFocus={e => {
        setLocalVal(value===0?"":String(value));
        setFocused(true);
        setTimeout(()=>e.target.select(),10);
      }}
      onBlur={() => {
        setFocused(false);
        if (localVal===""||localVal==="-") onChange(0);
      }}
      onChange={e => {
        setLocalVal(e.target.value);
        const n = parseFloat(e.target.value);
        if (!isNaN(n)) onChange(n);
        else if (e.target.value===""||e.target.value==="-") onChange(0);
      }}
      style={style}
    />
  );
}

const BARVE = ["#3B82F6","#10B981","#A78BFA","#F59E0B","#FB923C","#F472B6","#FBBF24","#2DD4BF","#38BDF8","#FDE047","#E879F9","#D97706","#F9A8D4"];
const IKONE = ["📋","🔍","📐","🏗️","🧱","🖌️","🏠","🚪","🔧","⚡","🔲","🪵","🛋️","📦","🔑","🌿","🪟","🛗","🧯","🔌"];
const PROJ_IKONE = ["🏠","🏡","🏗️","🔨","🏢","🏬","🏭","🏛️"];
const PROJ_BARVE = ["#3B82F6","#10B981","#8B5CF6","#F59E0B","#EF4444","#EC4899","#06B6D4","#84CC16"];

const statusColor = { v_teku:"#F59E0B", dokoncano:"#10B981", caka:"#9AA3B0", zaustavljeno:"#EF4444" };
const statusLabel = { v_teku:"V teku", dokoncano:"Dokončano", caka:"Čaka", zaustavljeno:"Zaustavljeno" };

// ─── Realistični % razdelitve stroškov gradnje ────────────────────────────────
// Vir: Slonep.net, MojMojster.net, Coinhab.si, Primerjam.si
// Brez parcele in komunalnega prispevka — skupaj = 100%
const KAT_DELEŽI = {
  "Dokumentacija & Projektiranje":      3.5,
  "Nadzor & Vodenje":                   0.8,
  "Gradbena dela":                     23.0,
  "Streha & Kritina":                   6.0,
  "Fasada & Toplotna izolacija":        5.5,
  "Stavbno pohištvo":                   8.0,
  "Vodovodne inštalacije":              2.0,
  "Ogrevanje & Prezračevanje":          4.5,
  "Elektro inštalacije":                4.0,
  "Sanitarna oprema":                   3.0,
  "Keramika":                           4.5,
  "Suhomontažna dela":                  3.0,
  "Slikopleskarska dela":               2.0,
  "Parket & Talne obloge":              4.0,
  "Pohištvo & Oprema":                  9.0,
  "Zunanja ureditev":                   5.0,
  "🟡 Nerazporejen budget":            11.2,
  // Skupaj = 100%
};

// ─── Posebne postavke s checkbox + PZI razčlenitev ───────────────────────────
const mkPostavkaEx = (naziv, budget=0, porabljeno=0, napredek=0, checked=false, hasCheck=false, isPziParent=false, isPziSub=false) =>
  ({ id:uid(), naziv, budget, porabljeno, napredek, checked, hasCheck, isPziParent, isPziSub });

// ─── data builders ────────────────────────────────────────────────────────────
const mkPostavka = (naziv, budget=0, porabljeno=0, napredek=0) => ({ id:uid(), naziv, budget, porabljeno, napredek, checked:false, hasCheck:false, isPziParent:false, isPziSub:false });
const mkKat = (naziv, ikona, barva, postavke) => ({
  id:uid(), naziv, ikona, barva,
  delez: KAT_DELEŽI[naziv] || 0,
  postavke: postavke.map(n => {
    if (Array.isArray(n)) {
      // [naziv, budget, porabljeno, napredek, hasCheck, checked, isPziParent, isPziSub]
      return mkPostavkaEx(n[0], n[1]||0, n[2]||0, n[3]||0, n[5]||false, n[4]||false, n[6]||false, n[7]||false);
    }
    return mkPostavka(n);
  })
});

// Helper: vsaka kategorija dobi posebno "skupaj" postavko (isSkupaj flag)
const mkSkupaj = naziv => ({ id:uid(), naziv:`${naziv} skupaj (celotna ponudba)`, budget:0, porabljeno:0, napredek:0, checked:false, hasCheck:false, isSkupaj:true, isPziParent:false, isPziSub:false });

// Build proper kats list
function buildKats(specs) {
  return specs.map(([naziv,ikona,barva,postavke]) => {
    const k = mkKat(naziv,ikona,barva,postavke);
    k.postavke.push(mkSkupaj(naziv));
    return k;
  });
}

function renovKats() {
  return buildKats([
  // ── OCENA STANJA ──
  // ── NAKUP ──
  ["Nakup nepremičnine","🏠","#3B82F6",[
    "Kupnina (dogovorjena cena)",
    "Davek na promet nepremičnin (2% kupnine)",
    "Notarski stroški",
    "Nepremičninski posrednik (2-3% kupnine)",
    "Preverjanje lastništva & pravni stroški",
  ]],
  // ── OCENA STANJA ──
  ["Pregled & Ocena stanja","🔍","#10B981",[
    "Gradbeni pregled (statika, vlaga, razpoke)",
    "Elektro pregled",
    "Vodovodno & kanalizacijsko stanje",
    "Pregled energetske učinkovitosti (izolacija, okna, ogrevanje)",
  ]],
  // ── PROJEKTIRANJE ──
  ["Projektna dokumentacija","📐","#3B82F6",[
    "Projekt prenove (PZI)",
    "Gradbeno dovoljenje (če potrebno)",
    "Energetska izkaznica",
  ]],
  // ── RUŠITVENA DELA ──
  ["Rušitvena & pripravljalna dela","🔨","#F87171",[
    "Rušenje predelnih sten",
    "Odstranitev starih tal",
    "Odstranitev starih inštalacij",
    "Odvoz materiala & deponija",
    "Rezanje / vrtanje (odprtine)",
  ]],
  // ── KONSTRUKCIJA ──
  ["Gradbena & konstruktivna dela","🏗️","#F59E0B",[
    "Ojačitve (nosilci, stebri)",
    "Nove predelne stene",
    "Betonska dela",
    "Ometi & izravnave",
  ]],
  // ── INŠTALACIJE ──
  ["Elektro inštalacije","⚡","#FDE047",[
    "Elektro razvod (kabli, cevi)",
    "Razdelilna omara (varovalke, FID)",
    "Stikala, vtičnice & doze",
    "Razsvetljava (notranja & zunanja)",
    "Varnostni sistemi (videofon, alarm, kamere)",
    "Fotovoltaika (FV) + baterija",
    "Pametni dom (KNX, Zigbee…)",
    "EV polnilnica",
  ]],
  ["Vodovodne & kanalizacijske inštalacije","🔧","#38BDF8",[
    "Vodovod (cevi, razvod)",
    "Kanalizacija (odtoki)",
    "Kopalnica — nova inštalacija",
    "Kuhinja — priključki",
  ]],
  ["Ogrevanje & Prezračevanje","🌡️","#DC2626",[
    "Toplotna črpalka",
    "Talno gretje (razvod & razdelilniki)",
    "Radiatorji",
    "Rekuperator / prezračevanje",
    "Kamin / peč",
    "Regulacija & pametno upravljanje",
  ]],
  // ── TOPLOTNA OVOJNICA ──
  ["Fasada & Toplotna izolacija","🧱","#FB923C",[
    "Toplotna izolacija (stiropor / kamena volna)",
    "Armirni sloj (mrežica + lepilo)",
    "Zaključni omet (dekorativni sloj)",
    "Detajli (police, profili, zaključki)",
  ]],
  ["Streha & Kritina","🏠","#FBBF24",[
    "Strešna konstrukcija",
    "Kritina",
    "Kleparska dela (obrobe, žlebovi)",
    "Strešna okna",
  ]],
  ["Okna & Vrata (stavbno pohištvo)","🚪","#2DD4BF",[
    "Okna",
    "Zunanja vrata",
    "Notranja vrata",
    "Senčila",
  ]],
  // ── ZAKLJUČNA DELA ──
  ["Keramika & Obloge","🔲","#E879F9",[
    "Keramika – material (ploščice)",
    "Polaganje keramike – tla",
    "Polaganje keramike – stene (kopalnica, WC, kuhinja)",
    "Hidroizolacija (mokri prostori)",
    "Fugiranje & silikon",
  ]],
  ["Suhomontažna dela","🏛️","#64748B",[
    "Predelne stene (knauf)",
    "Spuščeni stropi",
    "Obzidave (WC, instalacije, jaški)",
    "Obdelava špalet (okna/vrata)",
    "Zvočna / požarna zaščita",
  ]],
  ["Slikopleskarska dela","🖌️","#F472B6",[
    "Kitanje & brušenje sten in stropov",
    "Beljenje (osnovni premaz)",
    "Barvanje sten in stropov",
    "Priprava prostora (zaščita, trakovi)",
    "Dekorativne stene (tapete, strukture…)",
  ]],
  ["Parket & Talne obloge","🪵","#D97706",[
    "Parket",
    "Laminat",
    "Vinil",
    "Polaganje",
    "Brušenje & lakiranje obstoječega parketa",
    "Letvice & zaključki",
  ]],
  // ── OPREMA ──
  ["Pohištvo & Oprema","🛋️","#F9A8D4",[
    "Kuhinja – pohištvo po meri",
    "Kuhinjski aparati",
    "Kopalnica – omarice & oprema",
    "Sanitarna oprema (WC, umivalnik, tuš/kad)",
    "Dnevna soba – pohištvo",
    "Spalnica – pohištvo",
    "Vgradne omare",
    "Ostalo pohištvo po meri",
  ]],
  ["Zunanja ureditev","🌿","#86EFAC",[
    "Tlakovane površine",
    "Ograje & vrata",
    "Okolica & zasaditev",
  ]],
  ]);
}

function emptyKats() {
  return buildKats([
  // ── NIČTA FAZA ──
  ["Parcela & Komunalna ureditev","🏔️","#059669",[
    "Parcela – nakup",
    ["Komunalni prispevek", 0,0,0, false],
    ["Priključek – voda", 0,0,0, true, false],
    ["Priključek – elektrika", 0,0,0, true, false],
    "Geodet – parcelacija, posnetek, zakoličba",
    "Geomehanik – poročilo o tleh",
  ]],
  // ── PRIPRAVA ──
  ["Dokumentacija & Projektiranje","📐","#3B82F6",[
    "Idejna zasnova (IZ)",
    "DGD – dokumentacija za gradbeno dovoljenje",
    ["PZI – načrt za gradnjo", 0,0,0,false,false,true],
    ["PZI – strojne inštalacije", 0,0,0,false,false,false,true],
    ["PZI – elektro inštalacije", 0,0,0,false,false,false,true],
    ["PZI – statika & konstruktivni načrt", 0,0,0,false,false,false,true],
    ["PZI – požarna varnost", 0,0,0,false,false,false,true],
    "Uporabno dovoljenje",
  ]],
  ["Nadzor & Vodenje","🔍","#10B981",[
    "Gradbeni nadzor",
    "Vodenje projekta (PM)",
    "Koordinator varnosti (SZV)",
    "Geodetski nadzor med gradnjo",
  ]],
  // ── GRADBENA FAZA ──
  ["Gradbena dela","🏗️","#F59E0B",[
    "Zemeljska dela (izkop)",
    "Temelji & plošča",
    "Konstrukcija (zidanje, beton)",
    "Ometi (grobi, fini)",
    "Izolacija pod estrihom",
    "Tlaki / estrih",
  ]],
  ["Streha & Kritina","🏠","#FBBF24",[
    "Strešna konstrukcija (lesena konstrukcija)",
    "Kritina (opečna, pločevinasta…)",
    "Kleparska dela (obrobe, žlebovi, slemena)",
    "Strešna okna (mansardna okna)",
  ]],
  ["Fasada & Toplotna izolacija","🧱","#FB923C",[
    "Toplotna izolacija (stiropor / kamena volna)",
    "Armirni sloj (mrežica + lepilo)",
    "Zaključni omet (dekorativni sloj)",
    "Cokel / sokel (spodnji zaščitni del fasade)",
    "Detajli (police, profili, zaključki)",
  ]],
  // ── STAVBNO POHIŠTVO ──
  ["Stavbno pohištvo","🚪","#2DD4BF",[
    "Okna (balkonska vrata, panoramske steklene stene)",
    "Zunanja vrata",
    "Notranja vrata",
    "Senčila",
  ]],
  // ── INŠTALACIJE ──
  ["Vodovodne inštalacije","🔧","#38BDF8",[
    "Vodovod (cevi, razvod)",
    "Kanalizacija (odtoki)",
  ]],
  ["Ogrevanje & Prezračevanje","🌡️","#DC2626",[
    "Toplotna črpalka",
    "Talno gretje (razvod & razdelilniki)",
    "Radiatorji (če so)",
    "Rekuperator / prezračevanje",
    "Kamin / peč",
    "Kotlovnica (oprema, montaža)",
    "Regulacija & pametno upravljanje",
  ]],
  ["Elektro inštalacije","⚡","#FDE047",[
    "Elektro razvod (kabli, cevi)",
    "Razdelilna omara (varovalke, FID)",
    "Stikala, vtičnice & doze",
    "Razsvetljava (notranja & zunanja)",
    "Varnostni sistemi (videofon, alarm, kamere)",
    "Fotovoltaika (FV) + baterija",
    "Pametni dom (KNX, Zigbee…)",
    "EV polnilnica",
    "Strelovod",
  ]],
  ["Sanitarna oprema","🚿","#67E8F9",[
    "WC (školjka + kotliček, tipke)",
    "Umivalnik + armatura",
    "Tuš / kad + armatura",
    "Kopalniška omarica & ogledalo",
  ]],
  // ── ZAKLJUČNA DELA ──
  ["Keramika","🔲","#E879F9",[
    "Keramika – material (ploščice)",
    "Polaganje keramike – tla",
    "Polaganje keramike – stene (kopalnica, WC, kuhinja)",
    "Terasna keramika / balkon",
    "Hidroizolacija (mokri prostori)",
    "Fugiranje & silikon",
  ]],
  ["Suhomontažna dela","🏛️","#64748B",[
    "Predelne stene (knauf)",
    "Spuščeni stropi",
    "Obzidave (WC, instalacije, jaški)",
    "Obdelava špalet (okna/vrata)",
    "Mansarda (izolacija + knauf)",
    "Zvočna / požarna zaščita",
  ]],
  ["Slikopleskarska dela","🖌️","#F472B6",[
    "Kitanje & brušenje sten in stropov",
    "Beljenje (osnovni premaz)",
    "Barvanje sten in stropov",
    "Priprava prostora (zaščita, trakovi)",
    "Dekorativne stene (tapete, strukture, beton look…)",
    "Zaščitni premazi (les, kovina, ograje…)",
  ]],
  ["Parket & Talne obloge","🪵","#D97706",[
    "Parket",
    "Laminat",
    "Vinil",
    "Polaganje",
    "Priprava podlage",
    "Brušenje & lakiranje",
    "Letvice & zaključki",
  ]],
  // ── OPREMA & OKOLICA ──
  ["Pohištvo & Oprema","🛋️","#F9A8D4",[
    "Kuhinja – pohištvo po meri",
    "Kuhinjski aparati",
    "Kopalnica – omarice & oprema",
    "Dnevna soba – pohištvo",
    "Spalnica – pohištvo",
    "Otroška soba 1 – pohištvo",
    "Otroška soba 2 – pohištvo",
    "Vgradne omare (hodnik, shramba, garderoba)",
    "Jedilnica – miza & stoli",
    "Stopnišče – lesena obloga / ograja",
    "Ostalo pohištvo po meri",
  ]],
  ["Zunanja ureditev","🌿","#86EFAC",[
    "Tlakovane površine",
    "Ograje & vrata",
    "Zunanji objekti",
    "Okolica & zasaditev",
    "Inštalacije zunaj",
  ]],
  ["🟡 Nerazporejen budget","🎯","#D97706",[
    "Nepredvideni stroški",
    "Dodatna dela",
    "Prihodnje ponudbe",
    "Rezerva za podražitve",
  ]],
  ]);
}

// demoKats: budget samo na skupaj postavki vsake kategorije
function demoKats() {
  const DEMO_SKUPAJ = {
    "Parcela & Komunalna ureditev": 14000,
    "Dokumentacija & Projektiranje": 15000,
    "Nadzor & Vodenje": 6000,
    "Gradbena dela": 82000,
    "Fasada & Toplotna izolacija": 16000,
    "Streha & Kritina": 19000,
    "Stavbno pohištvo": 27000,
    "Vodovodne inštalacije": 12000,
    "Sanitarna oprema": 8000,
    "Ogrevanje & Prezračevanje": 18000,
    "Elektro inštalacije": 22000,
    "Keramika": 14000,
    "Suhomontažna dela": 6000,
    "Slikopleskarsko delo": 8000,
    "Parket & Talne obloge": 12000,
    "Pohištvo & Oprema": 28000,
    "Zunanja ureditev": 18000,
  };
  return emptyKats().map(k => {
    const skupajBud = DEMO_SKUPAJ[k.naziv] || 0;
    return {
      ...k,
      postavke: k.postavke.map(p =>
        p.isSkupaj ? { ...p, budget: skupajBud } : p
      )
    };
  });
}

const DOC_TIPI = [
  { id:"pogodbe",      naziv:"Pogodbe",                        ikona:"📄", opis:"Gradbene pogodbe z izvajalci" },
  { id:"predracuni",   naziv:"Predračuni & Ponudbe",           ikona:"💰", opis:"Ponudbe in predračuni izvajalcev", hasStatus:true },
  { id:"racuni",       naziv:"Računi & Plačila",               ikona:"🧾", opis:"Prejeti računi in potrdila o plačilu" },
  { id:"nacrti",       naziv:"Načrti & Tehnična dokumentacija", ikona:"📐", opis:"PZI, PGD, geodetski posnetki, statika" },
  { id:"dovoljenja",   naziv:"Dovoljenja & Uradni dokumenti",  ikona:"🏛️", opis:"Gradbeno dovoljenje, uporabno dovoljenje" },
  { id:"ostalo",       naziv:"Ostalo",                         ikona:"📋", opis:"Ostala dokumentacija" },
];

// Backwards compatibility
const DOC_KATS = DOC_TIPI;

function mkProject(naziv, lokacija, ikona, barva, demo=false, tip="gradnja") {
  return {
    id:uid(), naziv, lokacija, ikona, barva,
    tip,
    zacetek: new Date().toISOString().slice(0,10),
    konec: "",
    status: "v_teku",
    povrsina: 0,
    lastna: 0,
    kredit: 0,
    parcela: 0,
    kats: demo ? demoKats() : tip === "prenova" ? renovKats() : emptyKats(),
    docs: Object.fromEntries(DOC_TIPI.map(k=>[k.id,[]])),
    contacts: initContacts(),
  };
}

// ─── calcs ────────────────────────────────────────────────────────────────────
// Efektivni budget postavke: PZI parent → seštevek subov (če so vnešeni)
const effPostBud = p => {
  // PZI parent: vrednost je seštevek subov, ne lastna vrednost
  return p.budget || 0;
};
const effPostPor = p => p.porabljeno || 0;

// Za kategorijo: če so posamezne postavke z vrednostmi → seštevek teh,
// sicer pa skupaj postavka (vnešena ročno)
const katNp = k => {
  const skupaj = k.postavke.find(p => p.isSkupaj);
  const ostale = k.postavke.filter(p => !p.isSkupaj && !p.isPziSub);
  const pziParent = ostale.find(p => p.isPziParent);
  const pziSubs = k.postavke.filter(p => p.isPziSub);
  const pziSubBud = pziSubs.reduce((a,s)=>a+(s.budget||0),0);

  // Aktivne postavke = imajo budget ALI porabljeno ALI napredek > 0
  const aktivne = ostale.filter(p => !p.isPziParent &&
    ((p.budget||0) > 0 || (p.porabljeno||0) > 0 || (p.napredek||0) > 0));
  const pziAktiven = pziParent && ((pziParent.budget||0) > 0 || pziSubBud > 0 ||
    pziSubs.some(s => (s.porabljeno||0) > 0 || (s.napredek||0) > 0));

  if (aktivne.length > 0 || pziAktiven) {
    const pziNp = pziSubBud > 0 && pziSubs.length
      ? Math.round(pziSubs.reduce((a,s)=>a+s.napredek,0)/pziSubs.length)
      : (pziParent?.napredek || 0);
    const allNp = [...aktivne.map(p=>p.napredek), ...(pziAktiven ? [pziNp] : [])];
    return allNp.length ? Math.round(allNp.reduce((a,n)=>a+n,0)/allNp.length) : 0;
  }
  return skupaj ? skupaj.napredek : 0;
};

const katBud = k => {
  const skupaj = k.postavke.find(p => p.isSkupaj);
  const ostale = k.postavke.filter(p => !p.isSkupaj && !p.isPziSub);
  const pziSubs = k.postavke.filter(p => p.isPziSub);
  const pziParent = ostale.find(p => p.isPziParent);
  const pziSubBud = pziSubs.reduce((a,s)=>a+(s.budget||0),0);
  const anyIndBud = ostale.filter(p=>!p.isPziParent).some(p=>p.budget>0)
    || (pziParent && (pziParent.budget>0 || pziSubBud>0));
  if (anyIndBud) {
    const nonPzi = ostale.filter(p=>!p.isPziParent).reduce((a,p)=>a+(p.budget||0),0);
    const pziVal = pziParent ? (pziSubBud>0 ? pziSubBud : (pziParent.budget||0)) : 0;
    return nonPzi + pziVal;
  }
  return skupaj ? (skupaj.budget||0) : 0;
};

const katPor = k => {
  const skupaj = k.postavke.find(p => p.isSkupaj);
  const ostale = k.postavke.filter(p => !p.isSkupaj && !p.isPziSub);
  const pziSubs = k.postavke.filter(p => p.isPziSub);
  const pziParent = ostale.find(p => p.isPziParent);
  const pziSubPor = pziSubs.reduce((a,s)=>a+(s.porabljeno||0),0);
  const anyIndPor = ostale.filter(p=>!p.isPziParent).some(p=>p.porabljeno>0)
    || (pziParent && (pziParent.porabljeno>0 || pziSubPor>0));
  if (anyIndPor) {
    const nonPzi = ostale.filter(p=>!p.isPziParent).reduce((a,p)=>a+(p.porabljeno||0),0);
    const pziVal = pziParent ? (pziSubPor>0 ? pziSubPor : (pziParent.porabljeno||0)) : 0;
    return nonPzi + pziVal;
  }
  return skupaj ? (skupaj.porabljeno||0) : 0;
};
const katSt = k => {
  const ostale = k.postavke.filter(p => !p.isSkupaj);
  const skupaj = k.postavke.find(p => p.isSkupaj);

  // Aktivne postavke = tiste ki imajo budget ALI porabljeno ALI napredek
  const aktivne = ostale.filter(p => (p.budget||0) > 0 || (p.porabljeno||0) > 0 || (p.napredek||0) > 0);

  if (aktivne.length > 0) {
    if (aktivne.every(p => p.napredek === 100)) return "dokoncano";
    return "v_teku";
  }

  // Skupaj postavka
  const n = katNp(k);
  if (n === 100) return "dokoncano";
  if (n > 0) return "v_teku";
  if ((skupaj?.porabljeno||0) > 0) return "v_teku";
  return "caka";
};
const projNp  = p => p.kats.length ? Math.round(p.kats.reduce((a,k)=>a+katNp(k),0)/p.kats.length) : 0;
const projBud = p => p.kats.reduce((a,k)=>a+katBud(k),0);
const projPor = p => p.kats.reduce((a,k)=>a+katPor(k),0);

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Syne+Mono&family=Inter:wght@400;500;600;700;800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  ::-webkit-scrollbar{width:4px;height:4px}
  ::-webkit-scrollbar-track{background:#FFFFFF}
  ::-webkit-scrollbar-thumb{background:#2a2d38;border-radius:2px}
  input[type=range]{-webkit-appearance:none;appearance:none;height:3px;border-radius:2px;outline:none;cursor:pointer}
  input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;cursor:pointer;border:2px solid #FFFFFF}
  input[type=number]{-moz-appearance:textfield}
  input[type=number]::-webkit-outer-spin-button,input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none}

  /* ── Global input styles ── */
  input[type=text], input[type=email], input[type=password], input[type=number], textarea, select {
    background: rgba(0,0,0,0.056);
    border: 1px solid rgba(0,0,0,0.14);
    color: #1A1D24;
    border-radius: 8px;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  }
  input[type=text]:hover, input[type=email]:hover, input[type=password]:hover,
  input[type=number]:hover, textarea:hover, select:hover {
    background: rgba(0,0,0,0.08);
    border-color: rgba(0,0,0,0.14);
  }
  input[type=text]:focus, input[type=email]:focus, input[type=password]:focus,
  input[type=number]:focus, textarea:focus, select:focus {
    outline: none;
    background: rgba(0,0,0,0.08);
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
  }
  input::placeholder, textarea::placeholder { color: rgba(0,0,0,0.14); }

  .hov-row:hover{background:rgba(0,0,0,0.02)!important}
  .hov-card:hover{border-color:var(--hov-bright)!important;border-left-color:var(--hov-bright)!important;transform:translateY(-2px);box-shadow:0 8px 28px rgba(0,0,0,0.12)}
  .hov-kat:hover{background:rgba(0,0,0,0.032)!important}
  .hov-del:hover{color:#EF4444!important}
  .hov-edit:hover{color:#3B82F6!important}
  .T{transition:all 0.18s}
  select option{background:#FFFFFF;color:#1A1D24}
  .swipe-row{position:relative;overflow:hidden;touch-action:pan-y}
  .swipe-row .del-reveal{position:absolute;right:0;top:0;bottom:0;width:72px;background:#EF4444;display:flex;align-items:center;justify-content:center;font-size:18px;transform:translateX(72px);transition:transform 0.2s;z-index:1}
  .swipe-row:hover .del-reveal{transform:translateX(0)}
  .swipe-row .row-content{transition:transform 0.2s}
  .swipe-row:hover .row-content{transform:translateX(-72px)}
  .skupaj-row{background:rgba(0,0,0,0.032)!important;border-top:1px solid rgba(0,0,0,0.08)!important}
  .bottom-nav{scrollbar-width:none;-ms-overflow-style:none;position:relative}
  .bottom-nav::-webkit-scrollbar{display:none}
  .bottom-nav-wrap{position:fixed;left:0;right:0;bottom:0;z-index:900}
  .bottom-nav-wrap::after{content:"";position:absolute;right:0;top:0;bottom:0;width:36px;pointer-events:none;background:linear-gradient(90deg,transparent,#FFFFFF 85%)}
  .bottom-nav-wrap::before{content:"";position:absolute;left:0;top:0;bottom:0;width:14px;pointer-events:none;z-index:1;background:linear-gradient(270deg,transparent,#FFFFFF 90%)}
  @media(min-width:760px){.bottom-nav-wrap::after,.bottom-nav-wrap::before{display:none}}
`;

// ─── shared UI ────────────────────────────────────────────────────────────────
function Btn({ onClick, children, variant="ghost", style={} }) {
  const vs = {
    ghost:   { background:"rgba(0,0,0,0.04)", border:"1px solid rgba(0,0,0,0.08)",  color:"#4B5563", padding:"6px 14px" },
    primary: { background:"#3B82F6",  border:"1px solid #3B82F6",   color:"#1A1D24", padding:"6px 16px" },
    danger:  { background:"rgba(239,68,68,0.12)",  border:"1px solid rgba(239,68,68,0.3)",    color:"#DC2626", padding:"6px 14px" },
    green:   { background:"rgba(52,211,153,0.12)", border:"1px solid rgba(52,211,153,0.3)",   color:"#059669", padding:"6px 16px" },
    amber:   { background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.4)",   color:"#D97706", padding:"6px 16px" },
  };
  return <button onClick={onClick} style={{ fontFamily:"inherit",cursor:"pointer",borderRadius:8,fontSize:13,fontWeight:500,display:"inline-flex",alignItems:"center",gap:6,transition:"all 0.15s",...vs[variant],...style }}>{children}</button>;
}

function InlineEdit({ value, onSave, style={}, inputStyle={} }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const commit = () => { setEditing(false); if(val.trim()) onSave(val.trim()); else setVal(value); };
  if(editing) return <input value={val} autoFocus onChange={e=>setVal(e.target.value)} onBlur={commit}
    onKeyDown={e=>{ if(e.key==="Enter") commit(); if(e.key==="Escape"){ setEditing(false); setVal(value); } }}
    style={{ background:"rgba(0,0,0,0.064)",border:"1px solid rgba(59,130,246,0.5)",borderRadius:6,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"3px 8px",outline:"none",...inputStyle }}/>;
  return <span onClick={()=>{ setVal(value); setEditing(true); }} title="Kliknite za urejanje"
    style={{ cursor:"text",borderBottom:"1px dashed rgba(0,0,0,0.14)",paddingBottom:1,...style }}>{value}</span>;
}

function ConfirmModal({ msg, onOk, onCancel }) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.12)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(239,68,68,0.3)",borderRadius:14,padding:28,width:360}}>
        <div style={{fontSize:15,fontWeight:700,color:"#1A1D24",marginBottom:10}}>Potrdi brisanje</div>
        <div style={{fontSize:13,color:"#5A6472",marginBottom:22}}>{msg}</div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
          <Btn onClick={onCancel}>Prekliči</Btn>
          <Btn variant="danger" onClick={onOk}>Izbriši</Btn>
        </div>
      </div>
    </div>
  );
}

function KatModal({ initial, onSave, onClose }) {
  const [naziv, setNaziv] = useState(initial?.naziv||"");
  const [ikona, setIkona] = useState(initial?.ikona||"📦");
  const [barva, setBarva] = useState(initial?.barva||"#3B82F6");
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.12)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:16,padding:28,width:380}}>
        <div style={{fontSize:15,fontWeight:700,color:"#1A1D24",marginBottom:20}}>{initial?"Uredi kategorijo":"Nova kategorija"}</div>
        <label style={{fontSize:11,color:"#5A6472",letterSpacing:1}}>IME KATEGORIJE</label>
        <input value={naziv} onChange={e=>setNaziv(e.target.value)} placeholder="npr. Fasaderska dela"
          style={{display:"block",width:"100%",marginTop:6,marginBottom:16,background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.096)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"8px 12px"}}/>
        <label style={{fontSize:11,color:"#5A6472",letterSpacing:1}}>IKONA</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:6,marginBottom:16}}>
          {IKONE.map(i=><div key={i} onClick={()=>setIkona(i)} style={{width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,borderRadius:8,cursor:"pointer",background:ikona===i?"rgba(59,130,246,0.2)":"rgba(0,0,0,0.04)",border:ikona===i?"2px solid #3B82F6":"1px solid rgba(0,0,0,0.064)"}}>{i}</div>)}
        </div>
        <label style={{fontSize:11,color:"#5A6472",letterSpacing:1}}>BARVA</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:6,marginBottom:22}}>
          {BARVE.map(b=><div key={b} onClick={()=>setBarva(b)} style={{width:26,height:26,borderRadius:"50%",background:b,cursor:"pointer",outline:barva===b?`3px solid ${b}`:"none",outlineOffset:2,border:"2px solid #FFFFFF"}}/>)}
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
          <Btn onClick={onClose}>Prekliči</Btn>
          <Btn variant="primary" onClick={()=>naziv.trim()&&onSave({naziv:naziv.trim(),ikona,barva})}>{initial?"Shrani":"Dodaj"}</Btn>
        </div>
      </div>
    </div>
  );
}

function NewProjectModal({ onSave, onClose }) {
  const [naziv, setNaziv] = useState("");
  const [lokacija, setLokacija] = useState("");
  const [ikona, setIkona] = useState("🏠");
  const [barva, setBarva] = useState("#3B82F6");
  const [demo, setDemo] = useState(false);
  const [tip, setTip] = useState(""); // "gradnja" | "prenova"
  const [showBudget, setShowBudget] = useState(false);
  const [lastna, setLastna] = useState(0);
  const [kredit, setKredit] = useState(0);
  const [povrsina, setPovrsina] = useState(0);
  const [parcela, setParcela] = useState(0);

  const total = (lastna||0) + (kredit||0);

  // Izbira tipa projekta — prikaži najprej
  if (!tip) return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.12)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:"20px"}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:16,padding:32,width:"min(480px,95vw)"}}>
        <div style={{fontSize:15,fontWeight:700,color:"#1A1D24",marginBottom:8}}>Nov projekt</div>
        <div style={{fontSize:13,color:"#5A6472",marginBottom:24}}>Izberite vrsto projekta za prilagojene kategorije stroškov.</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div onClick={()=>setTip("gradnja")} style={{background:"rgba(59,130,246,0.08)",border:"2px solid rgba(59,130,246,0.3)",borderRadius:12,padding:"20px 16px",cursor:"pointer",textAlign:"center",transition:"all 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(59,130,246,0.15)"}
            onMouseLeave={e=>e.currentTarget.style.background="rgba(59,130,246,0.08)"}>
            <div style={{fontSize:32,marginBottom:10}}>🏗️</div>
            <div style={{fontSize:14,fontWeight:700,color:"#1A1D24",marginBottom:6}}>Gradim</div>
            <div style={{fontSize:11,color:"#5A6472",lineHeight:1.5}}>Nova gradnja hiše ali objekta</div>
          </div>
          <div onClick={()=>{setTip("prenova");setIkona("🔨");}} style={{background:"rgba(251,146,60,0.08)",border:"2px solid rgba(251,146,60,0.3)",borderRadius:12,padding:"20px 16px",cursor:"pointer",textAlign:"center",transition:"all 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(251,146,60,0.15)"}
            onMouseLeave={e=>e.currentTarget.style.background="rgba(251,146,60,0.08)"}>
            <div style={{fontSize:32,marginBottom:10}}>🔨</div>
            <div style={{fontSize:14,fontWeight:700,color:"#1A1D24",marginBottom:6}}>Prenovljam</div>
            <div style={{fontSize:11,color:"#5A6472",lineHeight:1.5}}>Adaptacija ali prenova obstoječega objekta</div>
          </div>
        </div>
        <button onClick={onClose} style={{marginTop:20,background:"transparent",border:"none",color:"#5A6472",fontSize:13,cursor:"pointer",fontFamily:"inherit",width:"100%"}}>Prekliči</button>
      </div>
    </div>
  );

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.12)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,overflowY:"auto",padding:"20px 0"}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:16,padding:28,width:"min(460px,95vw)"}}>
        <div style={{fontSize:15,fontWeight:700,color:"#1A1D24",marginBottom:20}}>Nov projekt</div>

        {/* Osnovno */}
        <label style={{fontSize:11,color:"#5A6472",letterSpacing:1}}>IME PROJEKTA *</label>
        <input value={naziv} onChange={e=>setNaziv(e.target.value)} placeholder="npr. Stanovanjska hiša Novak"
          style={{display:"block",width:"100%",marginTop:6,marginBottom:14,background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.096)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"9px 12px"}}/>
        <label style={{fontSize:11,color:"#5A6472",letterSpacing:1}}>LOKACIJA</label>
        <input value={lokacija} onChange={e=>setLokacija(e.target.value)} placeholder="npr. Maribor, center"
          style={{display:"block",width:"100%",marginTop:6,marginBottom:14,background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.096)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"9px 12px"}}/>
        <label style={{fontSize:11,color:"#5A6472",letterSpacing:1}}>IKONA</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:6,marginBottom:14}}>
          {PROJ_IKONE.map(i=><div key={i} onClick={()=>setIkona(i)} style={{width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,borderRadius:8,cursor:"pointer",background:ikona===i?"rgba(59,130,246,0.2)":"rgba(0,0,0,0.04)",border:ikona===i?"2px solid #3B82F6":"1px solid rgba(0,0,0,0.064)"}}>{i}</div>)}
        </div>
        <label style={{fontSize:11,color:"#5A6472",letterSpacing:1}}>BARVA</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:6,marginBottom:18}}>
          {PROJ_BARVE.map(b=><div key={b} onClick={()=>setBarva(b)} style={{width:28,height:28,borderRadius:"50%",background:b,cursor:"pointer",outline:barva===b?`3px solid ${b}`:"none",outlineOffset:2,border:"2px solid #FFFFFF"}}/>)}
        </div>

        {/* Quick Setup toggle */}
        <div onClick={()=>setShowBudget(s=>!s)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",marginBottom:showBudget?0:14,padding:"10px 14px",background:showBudget?"rgba(59,130,246,0.08)":"rgba(0,0,0,0.032)",borderRadius:showBudget?"8px 8px 0 0":8,border:`1px solid ${showBudget?"rgba(59,130,246,0.3)":"rgba(0,0,0,0.064)"}`}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:15}}>💰</span>
            <div>
              <div style={{fontSize:13,color:"#1A1D24",fontWeight:500}}>Quick Setup — Budget & površina</div>
              <div style={{fontSize:11,color:"#5A6472",marginTop:1}}>{total>0?`${fmt(total)} · ${povrsina>0?fmtN(povrsina)+" m²":"brez m²"}`:"Opcijsko — nastavite zdaj ali pozneje"}</div>
            </div>
          </div>
          <span style={{fontSize:11,color:"#3B82F6",transform:showBudget?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.2s"}}>▾</span>
        </div>

        {showBudget&&(
          <div style={{background:"rgba(59,130,246,0.05)",border:"1px solid rgba(59,130,246,0.2)",borderTop:"none",borderRadius:"0 0 8px 8px",padding:"14px",marginBottom:14}}>
            {/* Financiranje */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              <div>
                <div style={{fontSize:11,color:"#059669",marginBottom:5}}>Lastna sredstva €</div>
                <NumInput min={0} value={lastna} onChange={v=>setLastna(v)}
                  style={{width:"100%",background:"rgba(0,0,0,0.056)",border:"1px solid rgba(52,211,153,0.3)",borderRadius:7,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"7px 10px"}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:"#D97706",marginBottom:5}}>Kredit €</div>
                <NumInput min={0} value={kredit} onChange={v=>setKredit(v)}
                  style={{width:"100%",background:"rgba(0,0,0,0.056)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:7,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"7px 10px"}}/>
              </div>
            </div>
            {/* Skupaj prikaz */}
            {total>0&&(
              <div style={{background:"rgba(59,130,246,0.1)",borderRadius:6,padding:"7px 10px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:11,color:"#5A6472"}}>Skupni budget</span>
                <span style={{fontSize:13,fontWeight:700,color:"#3B82F6"}}>{fmt(total)}</span>
              </div>
            )}
            {/* Površina + parcela (parcela samo pri gradnji) */}
            <div style={{display:"grid",gridTemplateColumns:tip==="prenova"?"1fr":"1fr 1fr",gap:10}}>
              <div>
                <div style={{fontSize:11,color:"#7C3AED",marginBottom:5}}>📐 Površina m²</div>
                <NumInput min={0} value={povrsina} onChange={v=>setPovrsina(v)}
                  style={{width:"100%",background:"rgba(0,0,0,0.056)",border:"1px solid rgba(167,139,250,0.3)",borderRadius:7,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"7px 10px"}}/>
              </div>
              {tip!=="prenova"&&<div>
                <div style={{fontSize:11,color:"#059669",marginBottom:5}}>🏔️ Parcela in komunalni prispevek €</div>
                <NumInput min={0} value={parcela} onChange={v=>setParcela(v)}
                  style={{width:"100%",background:"rgba(0,0,0,0.056)",border:"1px solid rgba(52,211,153,0.2)",borderRadius:7,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"7px 10px"}}/>
              </div>}
            </div>
            {total>0&&povrsina>0&&(
              <div style={{fontSize:11,color:"#7C3AED",marginTop:8,textAlign:"right"}}>
                {fmt(Math.round((total-(tip==="prenova"?0:parcela))/povrsina))} / m²{tip!=="prenova"?" (brez parcele)":""}
              </div>
            )}
            <div style={{fontSize:11,color:"#5A6472",marginTop:8}}>
              {tip==="prenova"
                ? "Pri prenovi budget razporedite sami v Budget & m² — vsaka prenova je drugačna."
                : "Budget se samodejno razdeli po kategorijah. Kasneje ga lahko prilagodite."}
            </div>
          </div>
        )}

        {/* Demo checkbox */}
        <div onClick={()=>setDemo(!demo)} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",marginBottom:20,padding:"10px 14px",background:"rgba(0,0,0,0.032)",borderRadius:8,border:"1px solid rgba(0,0,0,0.064)"}}>
          <div style={{width:18,height:18,borderRadius:4,background:demo?"#3B82F6":"transparent",border:demo?"2px solid #3B82F6":"2px solid #9AA3B0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:"#fff",flexShrink:0}}>{demo?"✓":""}</div>
          <div><div style={{fontSize:13,color:"#1A1D24",fontWeight:500}}>Zapolni z demo podatki</div><div style={{fontSize:11,color:"#5A6472",marginTop:1}}>Vzorčne kategorije in vrednosti za prikaz — vse lahko po potrebi prilagodite ali izbrišete.</div></div>
        </div>

        <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
          <Btn onClick={onClose}>Prekliči</Btn>
          <Btn variant="primary" onClick={()=>{
            if(!naziv.trim()) return;
            onSave(naziv.trim(), lokacija.trim(), ikona, barva, demo, {
              lastna: lastna||0,
              kredit: kredit||0,
              povrsina: povrsina||0,
              parcela: parcela||0,
              tip,
            });
          }}>Ustvari projekt</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Budget distributor modal ─────────────────────────────────────────────────
function BudgetModal({ project, onSave, onClose }) {
  const tip = project.tip || (project.kats.some(k=>k.naziv==="Pregled & Ocena stanja") ? "prenova" : "gradnja");
  const isPrenova = tip === "prenova";
  const [lastna, setLastna] = useState(project.lastna||0);
  const [kredit, setKredit] = useState(project.kredit||0);
  const [povrsina, setPovrsina] = useState(project.povrsina||0);
  const [parcelaCena, setParcelaCena] = useState(project.parcela||0);
  const [deleži, setDeleži] = useState(() => {
    const d = {};
    project.kats.forEach(k => { d[k.id] = k.delez ?? (KAT_DELEŽI[k.naziv]||0); });
    return d;
  });
  const [zneski, setZneski] = useState({});

  const PARCELA_ID = project.kats.find(k=>k.naziv==="Parcela & Komunalna ureditev")?.id;

  const total = (Number(lastna)||0) + (Number(kredit)||0);
  const parcelaVal = isPrenova ? 0 : (Number(parcelaCena)||0);
  const distributable = Math.max(0, total - parcelaVal);

  const skupaj = Object.entries(deleži)
    .filter(([id]) => id !== PARCELA_ID)
    .reduce((a,[,v])=>a+(Number(v)||0), 0);
  const ok = isPrenova || Math.abs(skupaj - 100) < 0.01;

  const apply = () => {
    const newKats = project.kats.map(k => {
      if (k.id === PARCELA_ID) {
        return {
          ...k, delez: 0,
          postavke: k.postavke.map(p => p.isSkupaj ? { ...p, budget: parcelaVal } : { ...p, budget: 0 })
        };
      }
      // Pri prenovi ne razporejamo % — pustimo budget kot je (0)
      if (isPrenova) {
        const katBudget = zneski[k.id] != null ? zneski[k.id] : 0;
        return {
          ...k, delez: 0,
          postavke: k.postavke.map(p => p.isSkupaj ? { ...p, budget: katBudget } : { ...p, budget: 0 })
        };
      }
      const d = Number(deleži[k.id])||0;
      const katBudget = zneski[k.id] != null ? zneski[k.id] : Math.round(distributable * d / 100);
      return {
        ...k, delez: d,
        postavke: k.postavke.map(p => p.isSkupaj
          ? { ...p, budget: katBudget }
          : { ...p, budget: 0 }
        )
      };
    });
    onSave({ lastna: Number(lastna)||0, kredit: Number(kredit)||0, povrsina: Number(povrsina)||0, parcela: parcelaVal, kats: newKats });
  };

  const normalizeDelezi = () => {
    const ids = Object.keys(deleži).filter(id=>id!==PARCELA_ID);
    const s = ids.reduce((a,id)=>a+(Number(deleži[id])||0),0);
    if(s===0) return;
    const factor = 100/s;
    const nd = {...deleži};
    let running = 0;
    ids.forEach((id,i) => {
      if(i===ids.length-1) nd[id] = Math.round((100-running)*10)/10;
      else { const v = Math.round((Number(deleži[id])||0)*factor*10)/10; nd[id]=v; running+=v; }
    });
    setDeleži(nd);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.12)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:16,width:"min(640px,100%)",maxHeight:"90vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {/* header */}
        <div style={{padding:"24px 28px 18px",borderBottom:"1px solid rgba(0,0,0,0.056)"}}>
          <div style={{fontSize:15,fontWeight:700,color:"#1A1D24",marginBottom:4}}>💰 Budget & Financiranje</div>
          <div style={{fontSize:13,color:"#5A6472"}}>Določite vire financiranja, površino in razdelitev budgeta po fazah</div>
        </div>

        <div style={{overflowY:"auto",padding:"20px 28px",flex:1}}>
          {/* Financiranje */}
          <div style={{marginBottom:22}}>
            <div style={{fontSize:13,fontWeight:600,color:"#1A1D24",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
              <span style={{background:"rgba(59,130,246,0.2)",border:"1px solid rgba(59,130,246,0.3)",borderRadius:6,padding:"2px 8px",fontSize:11,color:"#2563EB"}}>VIRA FINANCIRANJA</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
              <div style={{background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)",borderRadius:10,padding:"14px"}}>
                <div style={{fontSize:11,color:"#059669",letterSpacing:1,marginBottom:8,fontFamily:"'Syne Mono',monospace"}}>LASTNA SREDSTVA</div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <NumInput min={0} value={lastna} onChange={v=>setLastna(v)}
                    style={{flex:1,background:"transparent",border:"none",color:"#1A1D24",fontFamily:"inherit",fontSize:18,fontWeight:700,width:0}}/>
                  <span style={{color:"#059669",fontSize:13,fontWeight:600}}>€</span>
                </div>
                <div style={{fontSize:11,color:"#5A6472",marginTop:4}}>{total>0?Math.round((Number(lastna)||0)/total*100):0}% od skupaj</div>
              </div>
              <div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:10,padding:"14px"}}>
                <div style={{fontSize:11,color:"#D97706",letterSpacing:1,marginBottom:8,fontFamily:"'Syne Mono',monospace"}}>KREDIT / POSOJILO</div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <NumInput min={0} value={kredit} onChange={v=>setKredit(v)}
                    style={{flex:1,background:"transparent",border:"none",color:"#1A1D24",fontFamily:"inherit",fontSize:18,fontWeight:700,width:0}}/>
                  <span style={{color:"#D97706",fontSize:13,fontWeight:600}}>€</span>
                </div>
                <div style={{fontSize:11,color:"#5A6472",marginTop:4}}>{total>0?Math.round((Number(kredit)||0)/total*100):0}% od skupaj</div>
              </div>
              <div style={{background:"rgba(0,0,0,0.032)",border:"1px solid rgba(0,0,0,0.08)",borderRadius:10,padding:"14px"}}>
                <div style={{fontSize:11,color:"#5A6472",letterSpacing:1,marginBottom:8,fontFamily:"'Syne Mono',monospace"}}>SKUPAJ BUDGET</div>
                <div style={{fontSize:18,fontWeight:700,color:"#1A1D24"}}>{fmt(total)}</div>
                <div style={{fontSize:11,color:"#5A6472",marginTop:4}}>= lastna + kredit</div>
              </div>
            </div>
          </div>

          {/* Površina */}
          <div style={{marginBottom:22}}>
            <div style={{fontSize:13,fontWeight:600,color:"#1A1D24",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
              <span style={{background:"rgba(167,139,250,0.2)",border:"1px solid rgba(167,139,250,0.3)",borderRadius:6,padding:"2px 8px",fontSize:11,color:"#7C3AED"}}>POVRŠINA OBJEKTA</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
              <div style={{background:"rgba(167,139,250,0.08)",border:"1px solid rgba(167,139,250,0.2)",borderRadius:10,padding:"14px"}}>
                <div style={{fontSize:11,color:"#7C3AED",letterSpacing:1,marginBottom:8,fontFamily:"'Syne Mono',monospace"}}>NETO POVRŠINA (m²)</div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <NumInput min={0} value={povrsina} onChange={v=>setPovrsina(v)}
                    style={{flex:1,background:"transparent",border:"none",color:"#1A1D24",fontFamily:"inherit",fontSize:18,fontWeight:700,width:0}}/>
                  <span style={{color:"#7C3AED",fontSize:13,fontWeight:600}}>m²</span>
                </div>
              </div>
              <div style={{background:"rgba(0,0,0,0.032)",border:"1px solid rgba(0,0,0,0.08)",borderRadius:10,padding:"14px"}}>
                <div style={{fontSize:11,color:"#5A6472",letterSpacing:1,marginBottom:8,fontFamily:"'Syne Mono',monospace"}}>CENA NA m²</div>
                <div style={{fontSize:18,fontWeight:700,color:(Number(povrsina)>0&&total>0)?"#7C3AED":"#9AA3B0"}}>
                  {(Number(povrsina)>0&&total>0) ? fmtN(Math.round(total/Number(povrsina)))+" €" : "— €"}
                </div>
                <div style={{fontSize:11,color:"#5A6472",marginTop:4}}>budget ÷ m²</div>
              </div>
              <div style={{background:"rgba(0,0,0,0.032)",border:"1px solid rgba(0,0,0,0.08)",borderRadius:10,padding:"14px"}}>
                <div style={{fontSize:11,color:"#5A6472",letterSpacing:1,marginBottom:8,fontFamily:"'Syne Mono',monospace"}}>PORABLJENO / m²</div>
                <div style={{fontSize:18,fontWeight:700,color:(Number(povrsina)>0&&projPor(project)>0)?"#10B981":"#9AA3B0"}}>
                  {(Number(povrsina)>0&&projPor(project)>0) ? fmtN(Math.round(projPor(project)/Number(povrsina)))+" €" : "— €"}
                </div>
                <div style={{fontSize:11,color:"#5A6472",marginTop:4}}>dejanski stroški</div>
              </div>
            </div>
          </div>

          {/* Razdelitev po % */}
          <div>
            {!isPrenova&&<div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{background:"rgba(245,158,11,0.2)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:6,padding:"2px 8px",fontSize:11,color:"#D97706",fontFamily:"'Syne Mono',monospace"}}>RAZDELITEV PO KATEGORIJAH</span>
                <div style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color: ok?"#10B981":skupaj>100?"#EF4444":"#F59E0B"}}>
                  <span style={{display:"inline-flex"}}>{ok?<Check size={14}/>:skupaj>100?<AlertTriangle size={14}/>:<Circle size={12}/>}</span>
                  <span style={{fontWeight:600}}>{skupaj.toFixed(1)}% / 100%</span>
                  {!ok&&<span style={{color:"#5A6472"}}>({skupaj>100?"preveč":"premalo"} za {Math.abs(100-skupaj).toFixed(1)}%)</span>}
                </div>
              </div>
              <button onClick={normalizeDelezi} style={{background:"rgba(59,130,246,0.12)",border:"1px solid rgba(59,130,246,0.3)",color:"#2563EB",borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Poravnaj na 100%</button>
            </div>}
            <div style={{fontSize:11,color:"#5A6472",marginBottom:10}}>
              {isPrenova
                ? "Sami določite znesek za vsako kategorijo glede na obseg vašega projekta."
                : "Priporočeni % temeljijo na slovenski gradbeni praksi. Prilagodite po potrebi."}
            </div>
            {!isPrenova&&<div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.25)",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:11,color:"#D97706",display:"flex",alignItems:"flex-start",gap:8}}>
              <span style={{fontSize:13,flexShrink:0}}>ℹ️</span>
              <span><b>Parcela je ločena postavka.</b> Vnesite njen znesek spodaj — odšteje se od skupnega budgeta, preostanek pa se razdeli po fazah gradnje. Znesek se prenese tudi v kategorijo Parcela.</span>
            </div>}

            {/* Parcela ločena postavka — samo pri gradnji */}
            {!isPrenova&&<div style={{background:"rgba(110,231,183,0.06)",border:"1px solid rgba(110,231,183,0.2)",borderRadius:10,padding:"14px",marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:600,color:"#059669",marginBottom:10}}>🏔️ Parcela & Komunalni prispevek — ločeni strošek</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                <div style={{background:"rgba(0,0,0,0.032)",borderRadius:8,padding:"12px"}}>
                  <div style={{fontSize:11,color:"#5A6472",letterSpacing:1,marginBottom:6,fontFamily:"'Syne Mono',monospace"}}>PARCELA + KOMUNALE (€)</div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <NumInput min={0} value={parcelaCena} onChange={v=>setParcelaCena(v)}
                      style={{flex:1,background:"transparent",border:"none",color:"#1A1D24",fontFamily:"inherit",fontSize:18,fontWeight:700,width:0,padding:0}}/>
                    <span style={{color:"#059669",fontSize:13,fontWeight:600}}>€</span>
                  </div>
                  <div style={{fontSize:11,color:"#5A6472",marginTop:4}}>ročni vnos</div>
                </div>
                <div style={{background:"rgba(0,0,0,0.032)",borderRadius:8,padding:"12px"}}>
                  <div style={{fontSize:11,color:"#5A6472",letterSpacing:1,marginBottom:6,fontFamily:"'Syne Mono',monospace"}}>SKUPNI BUDGET</div>
                  <div style={{fontSize:18,fontWeight:700,color:"#1A1D24"}}>{fmt(total)}</div>
                  <div style={{fontSize:11,color:"#5A6472",marginTop:4}}>lastna + kredit</div>
                </div>
                <div style={{background:`${parcelaVal>0?"rgba(59,130,246,0.08)":"rgba(0,0,0,0.032)"}`,borderRadius:8,padding:"12px",border:parcelaVal>0?"1px solid rgba(59,130,246,0.2)":"none"}}>
                  <div style={{fontSize:11,color:parcelaVal>0?"#2563EB":"#9AA3B0",letterSpacing:1,marginBottom:6,fontFamily:"'Syne Mono',monospace"}}>ZA RAZDELITEV</div>
                  <div style={{fontSize:18,fontWeight:700,color:parcelaVal>0?"#3B82F6":"#1A1D24"}}>{fmt(distributable)}</div>
                  <div style={{fontSize:11,color:"#5A6472",marginTop:4}}>budget − parcela</div>
                </div>
              </div>
            </div>}

            {!isPrenova&&<div style={{fontSize:11,color:"#5A6472",marginBottom:10}}>
              % se računa od <b style={{color:"#3B82F6"}}>{fmt(distributable)}</b> (budget brez parcele) · Prilagodite po potrebi.
            </div>}
            <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:10,overflow:"hidden"}}>
              <div style={{display:"grid",gridTemplateColumns:isPrenova?"1fr 110px 70px":"1fr 70px 80px 90px",padding:"8px 14px",borderBottom:"1px solid rgba(0,0,0,0.048)",fontSize:11,color:"#5A6472",letterSpacing:1.5,fontFamily:"'Syne Mono',monospace"}}>
                <span>KATEGORIJA</span>
                {!isPrenova&&<span style={{textAlign:"center"}}>%</span>}
                <span style={{textAlign:"right"}}>ZNESEK €</span>
                <span style={{textAlign:"right"}}>€/m²</span>
              </div>
              {project.kats.filter(k=>k.naziv!=="Parcela & Komunalna ureditev").map(k => {
                const d = Number(deleži[k.id])||0;
                // If manual znesek set, use it; otherwise compute from %
                const manualZ = zneski[k.id];
                const znesek = isPrenova
                  ? (manualZ||0)
                  : (manualZ!=null ? manualZ : Math.round(distributable * d / 100));
                const eur_m2 = (Number(povrsina)>0&&znesek>0) ? Math.round(znesek/Number(povrsina)) : null;
                const isManual = manualZ != null;
                return (
                  <div key={k.id} style={{display:"grid",gridTemplateColumns:isPrenova?"1fr 110px 70px":"1fr 90px 110px 70px",padding:"8px 14px",borderBottom:"1px solid rgba(0,0,0,0.032)",alignItems:"center",background:isManual?"rgba(52,211,153,0.03)":"transparent"}}>
                    <div style={{display:"flex",alignItems:"center",gap:7}}>
                      <span style={{fontSize:13}}>{k.ikona}</span>
                      <div>
                        <div style={{fontSize:11,color:"#1A1D24",fontWeight:500}}>{k.naziv}</div>
                        {!isPrenova&&<div style={{height:3,background:"rgba(0,0,0,0.048)",borderRadius:2,marginTop:3,width:80}}>
                          <div style={{width:Math.min(100,d)+"%",height:"100%",background:isManual?"#10B981":k.barva,borderRadius:2}}/>
                        </div>}
                      </div>
                      {isManual&&<span style={{fontSize:11,color:"#10B981",background:"rgba(52,211,153,0.1)",borderRadius:3,padding:"1px 4px",whiteSpace:"nowrap"}}>ročno</span>}
                    </div>
                    {/* % input — samo pri gradnji */}
                    {!isPrenova&&<div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:3}}>
                      <NumInput min={0} max={100} step={0.1} value={isManual ? (distributable>0?Math.round(manualZ/distributable*1000)/10:0) : (deleži[k.id]??0)}
                        onChange={v=>{
                          setZneski(prev=>{const n={...prev};delete n[k.id];return n;});
                          setDeleži(prev=>({...prev,[k.id]:v}));
                        }}
                        style={{width:46,background:"rgba(0,0,0,0.056)",border:`1px solid ${isManual?"rgba(52,211,153,0.3)":"rgba(0,0,0,0.08)"}`,borderRadius:6,color:isManual?"#059669":k.barva,fontFamily:"'Syne Mono',monospace",fontSize:13,fontWeight:700,textAlign:"center",padding:"3px 4px"}}/>
                      <span style={{fontSize:11,color:"#5A6472"}}>%</span>
                    </div>}
                    {/* Znesek input */}
                    <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:4}}>
                      <NumInput min={0} value={isPrenova ? (zneski[k.id]||0) : znesek}
                        onChange={v=>{
                          const pct = distributable>0 ? Math.round(v/distributable*1000)/10 : 0;
                          setZneski(prev=>({...prev,[k.id]:Number(v)||0}));
                          if(!isPrenova) setDeleži(prev=>({...prev,[k.id]:pct}));
                        }}
                        style={{width:94,background:isManual?"rgba(52,211,153,0.08)":"rgba(0,0,0,0.04)",border:`1px solid ${isManual?"rgba(52,211,153,0.3)":"rgba(0,0,0,0.08)"}`,borderRadius:6,color:isManual?"#059669":"#1A1D24",fontFamily:"inherit",fontSize:13,textAlign:"right",padding:"3px 7px"}}/>
                      {isManual&&<button onClick={()=>{setZneski(prev=>{const n={...prev};delete n[k.id];return n;});}} title="Počisti" style={{background:"transparent",border:"none",color:"#5A6472",fontSize:11,cursor:"pointer",padding:"0 2px",lineHeight:1}} className="hov-del T">✕</button>}
                    </div>
                    <div style={{textAlign:"right",fontSize:11,color:eur_m2?"#7C3AED":"#9AA3B0"}}>{eur_m2?fmtN(eur_m2)+" €":"—"}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{padding:"16px 28px",borderTop:"1px solid rgba(0,0,0,0.056)",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,background:"#FFFFFF"}}>
          <div style={{fontSize:13}}>
            {isPrenova ? (()=>{
              const vneseno = Object.values(zneski).reduce((a,v)=>a+(v||0),0);
              const razlika = total - vneseno;
              return <div style={{display:"flex",flexDirection:"column",gap:3}}>
                <span style={{color:"#5A6472"}}>Vneseno: <span style={{color:"#1A1D24",fontWeight:600}}>{fmt(vneseno)}</span> / {fmt(total)}</span>
                <span style={{color:Math.abs(razlika)<100?"#10B981":razlika>0?"#F59E0B":"#EF4444",fontWeight:600}}>
                  {Math.abs(razlika)<100?"✓ Vse razdeljeno":razlika>0?`Še za razdeliti: ${fmt(razlika)}`:`Preveč za ${fmt(Math.abs(razlika))}`}
                </span>
              </div>;
            })() : (()=>{
              const manualTotal = Object.values(zneski).reduce((a,v)=>a+(v||0),0);
              const pctTotal = Object.entries(deleži).filter(([id])=>id!==PARCELA_ID&&zneski[id]==null).reduce((a,[,v])=>a+(Number(v)||0),0);
              const manualCount = Object.keys(zneski).length;
              const allocatedFromPct = Math.round(distributable * pctTotal / 100);
              const totalAllocated = manualTotal + allocatedFromPct;
              const remaining = distributable - totalAllocated;
              if (manualCount > 0) {
                return <div style={{display:"flex",flexDirection:"column",gap:3}}>
                  <span style={{color:"#5A6472"}}>
                    <span style={{color:"#10B981"}}>{manualCount} ročno</span> · {fmt(manualTotal)} fiksno + {pctTotal.toFixed(1)}% od preostanka
                  </span>
                  <span style={{color:Math.abs(remaining)<100?"#10B981":remaining>0?"#F59E0B":"#EF4444",fontWeight:600}}>
                    {Math.abs(remaining)<100?"✓ Vse razdeljeno":remaining>0?`Še za razdeliti: ${fmt(remaining)}`:`Preveč za ${fmt(Math.abs(remaining))}`}
                  </span>
                </div>;
              }
              const remainingPct = Math.round((100 - skupaj) * 10) / 10;
              const remainingAmt = Math.round(distributable * (100 - skupaj) / 100);
              return <div style={{display:"flex",flexDirection:"column",gap:3}}>
                <span style={{color:ok?"#10B981":skupaj>100?"#DC2626":"#D97706",fontWeight:600}}>
                  {ok ? "✓ Razdelitev je pravilna (100%)" : `${skupaj.toFixed(1)}% / 100%`}
                </span>
                {!ok && <span style={{color:"#5A6472"}}>
                  {skupaj < 100
                    ? `Še za razdeliti: ${fmt(Math.abs(remainingAmt))} (${Math.abs(remainingPct).toFixed(1)}%)`
                    : `Preveč za: ${fmt(Math.abs(remainingAmt))} (${Math.abs(remainingPct).toFixed(1)}%)`}
                </span>}
              </div>;
            })()}
          </div>
          <div style={{display:"flex",gap:10}}>
            <Btn onClick={onClose}>Prekliči</Btn>
            <Btn variant="primary" onClick={apply}>Shrani & Razdeli budget</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ projects, onOpen, onNew, onDelete }) {
  const total = projects.reduce((a,p)=>a+projBud(p),0);
  return (
    <div style={{flex:1,overflowY:"auto",padding:"36px 40px"}}>
      <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:32}}>
        <div>
          <h1 style={{fontSize:28,fontWeight:800,color:"#1A1D24",letterSpacing:-0.5}}>Projekti</h1>
          <p style={{fontSize:13,color:"#5A6472",marginTop:4}}>{projects.length} projekt{projects.length===1?"":"i"} · skupni budget {fmt(total)}</p>
        </div>
        <Btn variant="primary" style={{fontSize:13,padding:"9px 20px"}} onClick={onNew}><span style={{fontSize:15}}>＋</span> Nov projekt</Btn>
      </div>
      {projects.length===0&&(
        <div style={{textAlign:"center",padding:"80px 40px",color:"#5A6472"}}>
          <div style={{fontSize:60,marginBottom:16}}>🏗️</div>
          <div style={{fontSize:15,color:"#5A6472",marginBottom:8}}>Ni projektov</div>
          <div style={{fontSize:13,marginBottom:24}}>Ustvarite prvi projekt in začnite slediti gradnji.</div>
          <Btn variant="primary" onClick={onNew}>＋ Ustvari projekt</Btn>
        </div>
      )}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
        {projects.map(p=>{
          const np=projNp(p);
          const bud=projBud(p);
          const por=projPor(p);
          const preostanek=bud-por;
          const porabaBudPct = bud>0 ? Math.round(por/bud*100) : 0;
          const cenaM2 = (p.povrsina>0&&bud>0) ? Math.round(bud/p.povrsina) : null;
          const porM2  = (p.povrsina>0&&por>0) ? Math.round(por/p.povrsina) : null;
          const st=np===100?"dokoncano":np>0?"v_teku":"caka";
          const budOver = por>bud && bud>0;

          return (
            <div key={p.id} className="hov-card T" style={{"--hov":p.barva,background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:14,overflow:"hidden",cursor:"pointer"}} onClick={()=>onOpen(p.id)}>
              {/* Top accent */}
              <div style={{height:3,background:p.barva}}/>
              <div style={{padding:"14px 14px 12px"}}>

                {/* Header */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:9,minWidth:0}}>
                    <div style={{width:36,height:36,background:`${p.barva}18`,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",fontSize:19,border:`1px solid ${p.barva}30`,flexShrink:0}}>{p.ikona}</div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:700,color:"#1A1D24",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={p.naziv}>{p.naziv}</div>
                      <div style={{display:"flex",gap:6,marginTop:2,flexWrap:"wrap"}}>
                        {p.lokacija&&<span style={{fontSize:11,color:"#5A6472"}}>{p.lokacija}</span>}
                        {p.povrsina>0&&<span style={{fontSize:11,color:"#5A6472"}}>· {fmtN(p.povrsina)} m²</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:4,alignItems:"center",flexShrink:0,marginLeft:6}} onClick={e=>e.stopPropagation()}>
                    <div style={{background:statusColor[st]+"22",color:statusColor[st],fontSize:11,padding:"2px 6px",borderRadius:4,fontWeight:600,whiteSpace:"nowrap"}}>{statusLabel[st]}</div>
                    <button className="hov-del T" onClick={()=>onDelete(p.id,p.naziv)} style={{background:"transparent",border:"none",color:"#9AA3B0",fontSize:13,cursor:"pointer",padding:"2px 4px"}}><Trash2 size={14}/></button>
                  </div>
                </div>

                {/* Napredek gradnje */}
                <div style={{marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:11,color:"#5A6472"}}>Napredek gradnje</span>
                    <span style={{fontSize:11,fontWeight:700,color:np===100?"#10B981":p.barva}}>{np}%</span>
                  </div>
                  <div style={{background:"rgba(0,0,0,0.056)",height:5,borderRadius:3}}>
                    <div style={{width:np+"%",height:"100%",background:np===100?"#10B981":p.barva,borderRadius:3}}/>
                  </div>
                </div>

                {/* Poraba budgeta */}
                <div style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:11,color:"#5A6472"}}>Poraba budgeta</span>
                    <span style={{fontSize:11,fontWeight:700,color:budOver?"#EF4444":porabaBudPct>80?"#F59E0B":"#059669"}}>{porabaBudPct}%{budOver?" ⚠":""}</span>
                  </div>
                  <div style={{background:"rgba(0,0,0,0.056)",height:5,borderRadius:3}}>
                    <div style={{width:Math.min(100,porabaBudPct)+"%",height:"100%",background:budOver?"#EF4444":porabaBudPct>80?"#F59E0B":"#10B981",borderRadius:3}}/>
                  </div>
                </div>

                {/* Stats - compact 2 col table */}
                <div style={{background:"rgba(0,0,0,0.016)",border:"1px solid rgba(0,0,0,0.048)",borderRadius:8,overflow:"hidden",marginBottom:8}}>
                  {[
                    ["Budget",        bud>0?fmt(bud):"—",          "#4B5563"],
                    ["Porabljeno",    por>0?fmt(por):"—",           budOver?"#DC2626":por>0?"#059669":"#9AA3B0"],
                    ["Budget / m²",   cenaM2?fmtN(cenaM2)+" €":"—","#7C3AED"],
                    ["Poraba / m²",   porM2?fmtN(porM2)+" €":"—",  "#059669"],
                  ].map(([label,val,color],i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 10px",borderBottom:i<3?"1px solid rgba(0,0,0,0.032)":"none"}}>
                      <span style={{fontSize:11,color:"#5A6472"}}>{label}</span>
                      <span style={{fontSize:11,fontWeight:600,color}}>{val}</span>
                    </div>
                  ))}
                </div>

                {/* Preostanek */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:budOver?"rgba(239,68,68,0.08)":"rgba(52,211,153,0.06)",border:`1px solid ${budOver?"rgba(239,68,68,0.2)":"rgba(52,211,153,0.15)"}`,borderRadius:7,padding:"6px 10px"}}>
                  <span style={{fontSize:11,color:budOver?"#DC2626":"#059669"}}>{budOver?"Prekoračitev":"Preostanek"}</span>
                  <span style={{fontSize:13,fontWeight:800,color:budOver?"#EF4444":"#10B981"}}>{fmt(Math.abs(preostanek))}</span>
                </div>

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RenameInline – click to open full modal ──────────────────────────────────
function RenameInline({ value, onSave, barva, isSkupaj }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {open && <RenameModal value={value} onSave={onSave} onClose={()=>setOpen(false)}/>}
      <span onClick={()=>setOpen(true)} title="Kliknite za preimenovanje"
        style={{fontSize:13,color:"#1A1D24",fontWeight:isSkupaj?700:500,cursor:"text",
          borderBottom:"1px dashed rgba(0,0,0,0.12)",paddingBottom:1,
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"100%"}}>
        {value}
      </span>
    </>
  );
}

// ─── Status dot button ────────────────────────────────────────────────────────
function StatusDot({ napredek, onChange, barva }) {
  const [showSlider, setShowSlider] = useState(false);
  const st = napredek === 0 ? "caka" : napredek === 100 ? "dokoncano" : "v_teku";

  const handleClick = (key, val) => {
    if (key === "v_teku") {
      // If already in_progress, toggle slider; if switching to it, set 50% and show slider
      if (st === "v_teku") {
        setShowSlider(s => !s);
      } else {
        onChange(50);
        setShowSlider(true);
      }
    } else {
      onChange(val);
      setShowSlider(false);
    }
  };

  const dots = [
    { key:"caka",      label:"Ni začeto",  color:"#5A6472", icon:"○" },
    { key:"v_teku",    label:"V teku",     color:"#F59E0B", icon:"◐" },
    { key:"dokoncano", label:"Zaključeno", color:"#10B981", icon:"✓" },
  ];
  const cur = dots.find(d => d.key === st);

  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-start",gap:5}}>
      {/* 3 state buttons */}
      <div style={{display:"flex",gap:5,alignItems:"center"}}>
        {dots.map(d => {
          const active = st === d.key;
          return (
            <button key={d.key} title={d.label} onClick={() => handleClick(d.key, d.key==="caka"?0:d.key==="dokoncano"?100:50)}
              style={{
                height:30, borderRadius:7, border:`2px solid ${active ? d.color : "rgba(0,0,0,0.08)"}`,
                background: active ? `${d.color}22` : "rgba(0,0,0,0.024)",
                color: active ? d.color : "#9AA3B0",
                cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
                gap:5, padding:"0 9px", fontSize:11, fontWeight: active ? 700 : 400,
                transition:"all 0.15s", whiteSpace:"nowrap"
              }}>
              <span style={{fontSize:13}}>{d.icon}</span>
              <span>{d.label}</span>
              {d.key==="v_teku" && active && <span style={{marginLeft:2,opacity:0.7}}>({napredek}%)</span>}
            </button>
          );
        })}
      </div>
      {/* Slider - only when v_teku and showSlider */}
      {showSlider && st === "v_teku" && (
        <div style={{display:"flex",alignItems:"center",gap:8,paddingLeft:4,width:"100%",maxWidth:320}}>
          <span style={{fontSize:11,color:"#5A6472",whiteSpace:"nowrap"}}>Natančen %:</span>
          <input type="range" min={1} max={99} value={napredek || 50}
            onChange={e => onChange(Number(e.target.value))}
            style={{flex:1,accentColor:barva,background:`linear-gradient(to right,${barva} ${napredek}%,rgba(0,0,0,0.08) ${napredek}%)`}}/>
          <span style={{fontSize:13,color:"#1A1D24",fontWeight:700,minWidth:32,textAlign:"right"}}>{napredek}%</span>
        </div>
      )}
    </div>
  );
}

// ─── Rename modal (big input) ─────────────────────────────────────────────────
function RenameModal({ value, onSave, onClose }) {
  const [val, setVal] = useState(value);
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.12)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.096)",borderRadius:14,padding:28,width:"min(480px,90vw)"}}>
        <div style={{fontSize:13,fontWeight:700,color:"#1A1D24",marginBottom:14}}>Preimenuj postavko</div>
        <input value={val} autoFocus onChange={e=>setVal(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter") { if(val.trim()) onSave(val.trim()); onClose(); } if(e.key==="Escape") onClose(); }}
          style={{width:"100%",background:"rgba(0,0,0,0.056)",border:"1px solid rgba(59,130,246,0.5)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:15,padding:"10px 14px",marginBottom:18}}/>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
          <Btn onClick={onClose}>Prekliči</Btn>
          <Btn variant="primary" onClick={()=>{ if(val.trim()) onSave(val.trim()); onClose(); }}>Shrani</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Napredek view ────────────────────────────────────────────────────────────
function NapredekView({ kats, project, onKatClick, onSetPost }) {
  return (
    <div>
      <div style={{marginBottom:20}}><h2 style={{fontSize:20,fontWeight:700,color:"#1A1D24"}}>Napredek del</h2>
        <p style={{fontSize:13,color:"#5A6472",marginTop:3}}>Kliknite gumbe za status · ⋯ za natančen slider</p>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {kats.map(k=>{
          const np=katNp(k);
          // skupaj on top, then rest
          const skupajP = k.postavke.filter(p=>p.isSkupaj);
          const ostale  = k.postavke.filter(p=>!p.isSkupaj);
          return (
            <div key={k.id} style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:12,overflow:"hidden"}}>
              {/* Kat header */}
              <div style={{padding:"12px 16px",borderBottom:"1px solid rgba(0,0,0,0.048)",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}
                onClick={()=>onKatClick(k.id)}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:15}}>{k.ikona}</span>
                  <span style={{fontSize:13,fontWeight:600,color:"#1A1D24"}}>{k.naziv}</span>
                  {k.delez>0&&<span style={{fontSize:11,color:k.barva,background:`${k.barva}18`,borderRadius:4,padding:"1px 5px",fontFamily:"'Syne Mono',monospace"}}>{k.delez}%</span>}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:13,fontWeight:700,color:np===100?"#10B981":np>0?k.barva:"#9AA3B0"}}>{np}%</span>
                  <div style={{width:60,background:"rgba(0,0,0,0.048)",height:4,borderRadius:2}}>
                    <div style={{width:np+"%",height:"100%",background:np===100?"#10B981":k.barva,borderRadius:2}}/>
                  </div>
                </div>
              </div>
              {/* Skupaj postavke first */}
              {skupajP.map(p=>(
                <div key={p.id} style={{padding:"10px 16px",borderBottom:"1px solid rgba(0,0,0,0.032)",background:"rgba(0,0,0,0.024)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:7,flex:1,minWidth:0}}>
                    <span style={{fontSize:11,background:`${k.barva}22`,color:k.barva,borderRadius:4,padding:"1px 6px",fontWeight:700,whiteSpace:"nowrap",flexShrink:0}}>SKUPAJ</span>
                    <span style={{fontSize:11,color:"#1A1D24",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={p.naziv}>{p.naziv}</span>
                  </div>
                  <StatusDot napredek={p.napredek} barva={k.barva} onChange={v=>onSetPost(k.id,p.id,"napredek",v)}/>
                </div>
              ))}
              {/* Ostale postavke */}
              {ostale.map(p=>(
                <div key={p.id} style={{padding:"8px 16px",borderBottom:"1px solid rgba(0,0,0,0.024)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                  <span style={{fontSize:11,color:"#4B5563",flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={p.naziv}>{p.naziv}</span>
                  <StatusDot napredek={p.napredek} barva={k.barva} onChange={v=>onSetPost(k.id,p.id,"napredek",v)}/>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── PostavkeSection – collapsible individual items ───────────────────────────
function PostavkeSection({ k, ostaleP, hasPosamezne, project, setPost, updKat, addPost, delPost, setConfirm, fmt, fmtN }) {
  const [open, setOpen] = useState(hasPosamezne);
  const [pziOpen, setPziOpen] = useState(false);

  // Smart setPost: handles PZI parent ↔ sub autoclear — atomic single update
  const setPostPzi = (kid, pid, field, v) => {
    if (field === "naziv" || field === "napredek") { setPost(kid, pid, field, v); return; }
    const p = ostaleP.find(x => x.id === pid);
    if (!p) { setPost(kid, pid, field, v); return; }

    const val = Number(v) || 0;

    if (p.isPziSub && val > 0) {
      // Vnos v podpostavko → pobriši parent budget/porabljeno atomično
      const parent = ostaleP.find(x => x.isPziParent);
      if (parent && (parent.budget > 0 || parent.porabljeno > 0)) {
        // Atomic: update sub + clear parent in one updKat call
        updKat(kid, k2 => ({
          ...k2,
          postavke: k2.postavke.map(px => {
            if (px.id === pid) return { ...px, [field]: val };
            if (px.id === parent.id) return { ...px, budget: 0, porabljeno: 0 };
            return px;
          })
        }));
        return;
      }
    }

    if (p.isPziParent && val > 0) {
      // Vnos v parent → pobriši vse sube atomično
      const subIds = new Set(ostaleP.filter(x => x.isPziSub).map(x => x.id));
      updKat(kid, k2 => ({
        ...k2,
        postavke: k2.postavke.map(px => {
          if (px.id === pid) return { ...px, [field]: val };
          if (subIds.has(px.id)) return { ...px, budget: 0, porabljeno: 0 };
          return px;
        })
      }));
      return;
    }

    setPost(kid, pid, field, v);
  };

  return (
    <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:12,overflow:"hidden"}}>
      {/* Header / toggle */}
      <div onClick={()=>setOpen(o=>!o)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",cursor:"pointer",background:open?"rgba(0,0,0,0.016)":"transparent",borderBottom:open?"1px solid rgba(0,0,0,0.048)":"none",transition:"background 0.15s"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:24,height:24,borderRadius:6,background:open?"rgba(59,130,246,0.2)":"rgba(0,0,0,0.048)",border:`1px solid ${open?"rgba(59,130,246,0.4)":"rgba(0,0,0,0.08)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:open?"#2563EB":"#9AA3B0",transition:"all 0.15s",flexShrink:0}}>{open?"−":"+"}</div>
          <div>
            <span style={{fontSize:13,color:"#1A1D24",fontWeight:600}}>Posamezne postavke</span>
            <span style={{fontSize:11,color:"#5A6472",marginLeft:8}}>{ostaleP.length} postavk{hasPosamezne?` · skupaj ${fmt(ostaleP.filter(p=>!p.isPziSub).reduce((a,p)=>a+(p.budget||0),0))}`:""}</span>
          </div>
          {hasPosamezne&&<span style={{fontSize:11,color:"#F59E0B",background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:4,padding:"1px 7px",marginLeft:4}}>aktivno</span>}
        </div>
        <span style={{fontSize:13,color:"#5A6472",transform:open?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.2s"}}>▾</span>
      </div>

      {open&&(
        <div>
          {ostaleP.length===0&&<div style={{padding:"20px 16px",textAlign:"center",color:"#5A6472",fontSize:13}}>Ni postavk. Dodajte jih z gumbom spodaj.</div>}
          {ostaleP.map((p,idx)=>{
            // Skip PZI subs — they're rendered inside their parent
            if (p.isPziSub) return null;
            const oc=p.porabljeno>p.budget&&p.budget>0;
            const p_m2=(project.povrsina>0&&p.budget>0)?Math.round(p.budget/project.povrsina):null;
            // Get PZI subs for this parent
            const pziSubs = p.isPziParent ? ostaleP.filter(s=>s.isPziSub) : [];
            const pziSubBud = pziSubs.reduce((a,s)=>a+(s.budget||0),0);
            const pziSubPor = pziSubs.reduce((a,s)=>a+(s.porabljeno||0),0);
            const pziHasValues = pziSubBud > 0 || pziSubPor > 0;

            return (
              <div key={p.id}>
                <div className="hov-row" style={{padding:"12px 16px",borderBottom:"1px solid rgba(0,0,0,0.032)"}}>
                  {/* Row 1: name + expand (for PZI) + trash */}
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:7,flex:1,minWidth:0}}>
                      {p.isPziParent&&(
                        <button onClick={()=>setPziOpen(o=>!o)} style={{background:pziOpen?"rgba(59,130,246,0.15)":"rgba(0,0,0,0.048)",border:`1px solid ${pziOpen?"rgba(59,130,246,0.3)":"rgba(0,0,0,0.08)"}`,color:pziOpen?"#2563EB":"#9AA3B0",borderRadius:5,width:20,height:20,fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontWeight:700}}>
                          {pziOpen?"−":"+"}
                        </button>
                      )}
                      <RenameInline value={p.naziv} onSave={n=>setPostPzi(k.id,p.id,"naziv",n)} barva={k.barva} isSkupaj={false}/>
                      {p.isPziParent&&pziSubs.length>0&&(
                        <span style={{fontSize:11,color:"#3B82F6",background:"rgba(59,130,246,0.1)",borderRadius:4,padding:"1px 6px",whiteSpace:"nowrap",flexShrink:0}}>
                          {pziHasValues?"seštevek":"razčlenitev"}
                        </span>
                      )}
                    </div>
                    <button className="hov-del T" title="Izbriši" onClick={()=>setConfirm({msg:<>Izbriši <b style={{color:"#fff"}}>{p.naziv}</b>?</>,ok:()=>delPost(k.id,p.id)})} style={{background:"transparent",border:"none",color:"#9AA3B0",fontSize:13,cursor:"pointer",padding:"2px 4px",lineHeight:1,flexShrink:0}}><Trash2 size={14}/></button>
                  </div>
                  {/* Row 2: budget / porabljeno */}
                  <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:5}}>
                      <span style={{fontSize:11,color:"#5A6472"}}>Budget:</span>
                      {p.isPziParent && pziHasValues
                        ? <span style={{fontSize:13,fontWeight:700,color:"#1A1D24",display:"flex",alignItems:"center",gap:5}}>
                            {fmt(pziSubBud)}
                            <span style={{fontSize:11,color:"#3B82F6",background:"rgba(59,130,246,0.1)",borderRadius:3,padding:"1px 5px"}}>= seštevek</span>
                          </span>
                        : <NumInput min={0} value={p.budget} onChange={v=>setPostPzi(k.id,p.id,"budget",v)} style={{width:100,background:"rgba(0,0,0,0.048)",border:"1px solid rgba(0,0,0,0.096)",borderRadius:6,color:"#1A1D24",fontFamily:"inherit",fontSize:13,textAlign:"right",padding:"4px 7px"}}/>
                      }
                      {!(p.isPziParent && pziHasValues) && <span style={{fontSize:11,color:"#5A6472"}}>€</span>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:5}}>
                      <span style={{fontSize:11,color:"#5A6472"}}>Porabljeno:</span>
                      {p.isPziParent && pziHasValues
                        ? <span style={{fontSize:13,fontWeight:700,color:pziSubPor>pziSubBud&&pziSubBud>0?"#DC2626":"#059669",display:"flex",alignItems:"center",gap:5}}>
                            {fmt(pziSubPor)}
                            <span style={{fontSize:11,color:"#3B82F6",background:"rgba(59,130,246,0.1)",borderRadius:3,padding:"1px 5px"}}>= seštevek</span>
                          </span>
                        : <NumInput min={0} value={p.porabljeno} onChange={v=>setPostPzi(k.id,p.id,"porabljeno",v)} style={{width:100,background:"rgba(0,0,0,0.048)",border:`1px solid ${oc?"rgba(239,68,68,0.4)":"rgba(0,0,0,0.096)"}`,borderRadius:6,color:oc?"#DC2626":"#059669",fontFamily:"inherit",fontSize:13,textAlign:"right",padding:"4px 7px"}}/>
                      }
                      {!(p.isPziParent && pziHasValues) && <span style={{fontSize:11,color:oc?"#DC2626":"#9AA3B0"}}>€{oc?" ⚠":""}</span>}
                    </div>
                    {p_m2&&!p.isPziParent&&<span style={{fontSize:11,color:"#5A6472"}}>📐 {fmtN(p_m2)} €/m²</span>}
                  </div>
                  {/* Row 3: status */}
                  <StatusDot napredek={p.napredek} barva={k.barva} onChange={v=>setPostPzi(k.id,p.id,"napredek",v)}/>
                </div>

                {/* PZI sub-items - expanded */}
                {p.isPziParent&&pziOpen&&pziSubs.map(s=>{
                  const sOc=s.porabljeno>s.budget&&s.budget>0;
                  return (
                    <div key={s.id} style={{background:"rgba(59,130,246,0.04)",borderBottom:"1px solid rgba(0,0,0,0.024)",padding:"9px 16px 9px 44px",borderLeft:"2px solid rgba(59,130,246,0.2)"}}>
                      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:7}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,flex:1,minWidth:0}}>
                          <span style={{fontSize:11,color:"#3B82F6",flexShrink:0}}>↳</span>
                          <RenameInline value={s.naziv} onSave={n=>setPostPzi(k.id,s.id,"naziv",n)} barva="#3B82F6" isSkupaj={false}/>
                        </div>
                        <button className="hov-del T" onClick={()=>setConfirm({msg:<>Izbriši <b style={{color:"#fff"}}>{s.naziv}</b>?</>,ok:()=>delPost(k.id,s.id)})} style={{background:"transparent",border:"none",color:"#9AA3B0",fontSize:13,cursor:"pointer",padding:"2px 4px",lineHeight:1,flexShrink:0}}><Trash2 size={14}/></button>
                      </div>
                      <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:7}}>
                        <div style={{display:"flex",alignItems:"center",gap:5}}>
                          <span style={{fontSize:11,color:"#5A6472"}}>Budget:</span>
                          <NumInput min={0} value={s.budget} onChange={v=>setPostPzi(k.id,s.id,"budget",v)} style={{width:95,background:"rgba(0,0,0,0.04)",border:"1px solid rgba(0,0,0,0.08)",borderRadius:6,color:"#1A1D24",fontFamily:"inherit",fontSize:13,textAlign:"right",padding:"3px 6px"}}/>
                          <span style={{fontSize:11,color:"#5A6472"}}>€</span>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:5}}>
                          <span style={{fontSize:11,color:"#5A6472"}}>Porabljeno:</span>
                          <NumInput min={0} value={s.porabljeno} onChange={v=>setPostPzi(k.id,s.id,"porabljeno",v)} style={{width:95,background:"rgba(0,0,0,0.04)",border:`1px solid ${sOc?"rgba(239,68,68,0.4)":"rgba(0,0,0,0.08)"}`,borderRadius:6,color:sOc?"#DC2626":"#059669",fontFamily:"inherit",fontSize:13,textAlign:"right",padding:"3px 6px"}}/>
                          <span style={{fontSize:11,color:sOc?"#DC2626":"#9AA3B0"}}>€</span>
                        </div>
                      </div>
                      <StatusDot napredek={s.napredek} barva="#3B82F6" onChange={v=>setPostPzi(k.id,s.id,"napredek",v)}/>
                    </div>
                  );
                })}
              </div>
            );
          })}
          <div style={{padding:"10px 16px",borderTop:ostaleP.length?"1px solid rgba(0,0,0,0.032)":"none"}}>
            <button onClick={()=>addPost(k.id)} style={{background:"rgba(52,211,153,0.1)",border:"1px solid rgba(52,211,153,0.25)",color:"#059669",borderRadius:7,padding:"7px 16px",fontSize:13,fontFamily:"inherit",cursor:"pointer",fontWeight:500,display:"flex",alignItems:"center",gap:6}}>＋ Dodaj postavko</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── KontaktiView ─────────────────────────────────────────────────────────────
// Structure: contacts = array of groups { id, label, ikona, members: [{id,ime,email,tel}] }
const DEFAULT_CONTACT_GROUPS = [
  { id:"gradbinec",  label:"Gradbinec",         ikona:"🏗️" },
  { id:"elektricar", label:"Električar",         ikona:"⚡"  },
  { id:"vodovodar",  label:"Vodovodar",           ikona:"🔧"  },
  { id:"tesar",      label:"Tesar / Kritinar",   ikona:"🏠"  },
  { id:"fasader",    label:"Fasader / Slikopleskar", ikona:"🧱" },
  { id:"arhitekt",   label:"Arhitekt",            ikona:"📐"  },
  { id:"upravna",    label:"Upravna enota",       ikona:"🏛️"  },
  { id:"banka",      label:"Banka / Kredit",      ikona:"🏦"  },
  { id:"nadzornik",  label:"Nadzornik",           ikona:"🔍"  },
  { id:"keramicar",  label:"Keramičar",           ikona:"🔲"  },
  { id:"parketar",   label:"Parketar",            ikona:"🪵"  },
  { id:"pohistvo",   label:"Pohištveni studio",   ikona:"🛋️"  },
  { id:"drug",       label:"Drugo",               ikona:"👤"  },
];

const mkMember = () => ({ id:uid(), ime:"", email:"", tel:"" });

function initContacts() {
  return DEFAULT_CONTACT_GROUPS.map(g => ({ ...g, members:[mkMember()] }));
}

function KontaktiView({ contacts, onUpdate }) {
  const [openGroup, setOpenGroup] = useState(null);
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  const groups = contacts;

  const updGroup = (gid, fn) => onUpdate(groups.map(g => g.id===gid ? fn(g) : g));
  const updMember = (gid, mid, field, val) =>
    updGroup(gid, g => ({ ...g, members: g.members.map(m => m.id===mid ? {...m,[field]:val} : m) }));
  const addMember = gid => updGroup(gid, g => ({ ...g, members:[...g.members, mkMember()] }));
  const delMember = (gid, mid) => updGroup(gid, g => ({ ...g, members: g.members.filter(m=>m.id!==mid) }));

  const confirmAddGroup = () => {
    if (!newGroupName.trim()) return;
    onUpdate([...groups, { id:uid(), label:newGroupName.trim(), ikona:"👤", members:[mkMember()] }]);
    setNewGroupName("");
    setAddingGroup(false);
  };

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:700,color:"#1A1D24"}}>Kontakti izvajalcev</h2>
          <p style={{fontSize:13,color:"#5A6472",marginTop:3}}>Dodajte kontakte izvajalcev za pošiljanje dokumentov in pridobivanje ponudb</p>
        </div>
        {!addingGroup&&(
          <button onClick={()=>setAddingGroup(true)} style={{background:"rgba(59,130,246,0.15)",border:"1px solid rgba(59,130,246,0.3)",color:"#2563EB",borderRadius:8,padding:"7px 14px",fontSize:13,fontFamily:"inherit",cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontWeight:600,whiteSpace:"nowrap"}}>
            ＋ Dodaj izvajalca
          </button>
        )}
      </div>

      {/* Inline add group form */}
      {addingGroup&&(
        <div style={{background:"rgba(59,130,246,0.08)",border:"1px solid rgba(59,130,246,0.3)",borderRadius:10,padding:"12px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:13}}>👤</span>
          <input value={newGroupName} onChange={e=>setNewGroupName(e.target.value)} autoFocus
            placeholder="Ime izvajalca (npr. Krovač, Geodet...)"
            onKeyDown={e=>{ if(e.key==="Enter") confirmAddGroup(); if(e.key==="Escape"){ setAddingGroup(false); setNewGroupName(""); } }}
            style={{flex:1,background:"rgba(0,0,0,0.056)",border:"1px solid rgba(59,130,246,0.4)",borderRadius:7,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"6px 10px"}}/>
          <button onClick={confirmAddGroup} style={{background:"rgba(59,130,246,0.2)",border:"1px solid rgba(59,130,246,0.4)",color:"#2563EB",borderRadius:7,padding:"6px 14px",fontSize:13,fontFamily:"inherit",cursor:"pointer",fontWeight:600,whiteSpace:"nowrap"}}>Dodaj</button>
          <button onClick={()=>{ setAddingGroup(false); setNewGroupName(""); }} style={{background:"transparent",border:"1px solid rgba(0,0,0,0.08)",color:"#5A6472",borderRadius:7,padding:"6px 10px",fontSize:13,fontFamily:"inherit",cursor:"pointer"}}>✕</button>
        </div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {groups.map(g=>{
          const hasAny = g.members.some(m=>m.ime||m.email||m.tel);
          const hasEmail = g.members.some(m=>m.email);
          const isOpen = openGroup===g.id;
          return (
            <div key={g.id} style={{background:"#FFFFFF",border:`1px solid ${hasAny?"rgba(59,130,246,0.25)":"rgba(0,0,0,0.056)"}`,borderRadius:12,overflow:"hidden"}}>
              {/* Group header */}
              <div onClick={()=>setOpenGroup(isOpen?null:g.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",cursor:"pointer",background:isOpen?"rgba(0,0,0,0.016)":"transparent"}}>
                <div style={{width:34,height:34,background:hasAny?"rgba(59,130,246,0.12)":"rgba(0,0,0,0.04)",borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,border:hasAny?"1px solid rgba(59,130,246,0.25)":"1px solid rgba(0,0,0,0.064)",flexShrink:0}}>{g.ikona}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:"#1A1D24"}}>{g.label}</div>
                  <div style={{fontSize:11,color:"#5A6472",marginTop:1}}>
                    {g.members.filter(m=>m.ime||m.email).length > 0
                      ? g.members.filter(m=>m.ime||m.email).map(m=>m.ime||m.email).join(", ")
                      : "Dodaj kontakt za to kategorijo"}
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  {(()=>{
                    if (!hasAny) return null;
                    const hasTel = g.members.some(m=>m.tel);
                    const manjkaEmail = !hasEmail;
                    const manjkaTel = !hasTel;
                    if (!manjkaEmail && !manjkaTel) return (
                      <div style={{display:"flex",alignItems:"center",gap:5,background:"rgba(52,211,153,0.1)",borderRadius:5,padding:"1px 8px"}}>
                        <div style={{width:7,height:7,borderRadius:"50%",background:"#10B981",boxShadow:"0 0 5px rgba(52,211,153,0.5)"}}/>
                        <span style={{fontSize:11,color:"#10B981"}}>Pripravljen</span>
                      </div>
                    );
                    if (manjkaEmail && manjkaTel) return (
                      <span style={{fontSize:11,background:"rgba(239,68,68,0.1)",color:"#DC2626",borderRadius:5,padding:"1px 7px"}}>Manjkajo podatki</span>
                    );
                    if (manjkaEmail) return (
                      <span style={{fontSize:11,background:"rgba(245,158,11,0.1)",color:"#F59E0B",borderRadius:5,padding:"1px 7px"}}>Manjka e-mail</span>
                    );
                    if (manjkaTel) return (
                      <span style={{fontSize:11,background:"rgba(245,158,11,0.1)",color:"#F59E0B",borderRadius:5,padding:"1px 7px"}}>Manjka telefon</span>
                    );
                  })()}
                  <span style={{fontSize:13,color:"#5A6472",transform:isOpen?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.2s"}}>▾</span>
                </div>
              </div>

              {/* Expanded: members */}
              {isOpen&&(
                <div style={{borderTop:"1px solid rgba(0,0,0,0.048)"}}>
                  {g.members.map((m,i)=>(
                    <div key={m.id} style={{padding:"12px 16px",borderBottom:"1px solid rgba(0,0,0,0.032)",background:"rgba(0,0,0,0.008)"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                        <span style={{fontSize:11,color:"#5A6472",fontFamily:"'Syne Mono',monospace",letterSpacing:1}}>IZVAJALEC {i+1}</span>
                        {g.members.length>1&&(
                          <button onClick={()=>delMember(g.id,m.id)} style={{background:"transparent",border:"none",color:"#5A6472",fontSize:13,cursor:"pointer",padding:"2px 5px"}} className="hov-del T"><Trash2 size={14}/></button>
                        )}
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                        <div>
                          <div style={{fontSize:11,color:"#5A6472",marginBottom:3}}>IME / PODJETJE</div>
                          <input value={m.ime} onChange={e=>updMember(g.id,m.id,"ime",e.target.value)} placeholder="Janez Novak"
                            style={{width:"100%",background:"rgba(0,0,0,0.048)",border:"1px solid rgba(0,0,0,0.08)",borderRadius:6,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"5px 8px"}}/>
                        </div>
                        <div>
                          <div style={{fontSize:11,color:"#5A6472",marginBottom:3}}>E-MAIL</div>
                          <input type="email" value={m.email} onChange={e=>updMember(g.id,m.id,"email",e.target.value)} placeholder="janez@primer.si"
                            style={{width:"100%",background:"rgba(0,0,0,0.048)",border:"1px solid rgba(0,0,0,0.08)",borderRadius:6,color:"#3B82F6",fontFamily:"inherit",fontSize:13,padding:"5px 8px"}}/>
                        </div>
                        <div>
                          <div style={{fontSize:11,color:"#5A6472",marginBottom:3}}>TELEFON</div>
                          <input value={m.tel} onChange={e=>updMember(g.id,m.id,"tel",e.target.value)} placeholder="041 123 456"
                            style={{width:"100%",background:"rgba(0,0,0,0.048)",border:"1px solid rgba(0,0,0,0.08)",borderRadius:6,color:"#10B981",fontFamily:"inherit",fontSize:13,padding:"5px 8px"}}/>
                        </div>
                      </div>
                      {(m.email||m.tel)&&(
                        <div style={{display:"flex",gap:6,marginTop:8}}>
                          {m.email&&<a href={`mailto:${m.email}`} style={{fontSize:11,background:"rgba(59,130,246,0.1)",border:"1px solid rgba(59,130,246,0.2)",color:"#2563EB",borderRadius:5,padding:"3px 10px",textDecoration:"none",fontFamily:"inherit"}}>E-mail</a>}
                          {m.tel&&<a href={`tel:${m.tel.replace(/\s/g,"")}`} style={{fontSize:11,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)",color:"#059669",borderRadius:5,padding:"3px 10px",textDecoration:"none",fontFamily:"inherit"}}>Pokliči</a>}
                        </div>
                      )}
                    </div>
                  ))}
                  {/* Add member button */}
                  <div style={{padding:"10px 16px"}}>
                    <button onClick={()=>addMember(g.id)} style={{background:"rgba(59,130,246,0.08)",border:"1px dashed rgba(59,130,246,0.3)",color:"#3B82F6",borderRadius:7,padding:"6px 14px",fontSize:13,fontFamily:"inherit",cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                      ＋ Dodaj izvajalca v {g.label}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── PREDNASTAVJENI PREJEMNIKI (za DocsView) ──────────────────────────────────
const DEFAULT_RECIPIENTS = DEFAULT_CONTACT_GROUPS.map(g=>({...g, email:"", tel:"", ime:""}));

// ─── RecipientPicker – category → members ─────────────────────────────────────
function RecipientPicker({ contacts, chosenRec, toggleRec, onAddChosen }) {
  const [openCat, setOpenCat] = useState(null);
  const [quickEmail, setQuickEmail] = useState("");

  const filledGroups = contacts.filter(g => g.members.some(m=>m.ime||m.email||m.tel));

  if (filledGroups.length === 0) return (
    <div style={{fontSize:13,color:"#5A6472",padding:"12px",background:"rgba(0,0,0,0.016)",borderRadius:8,textAlign:"center"}}>
      ⚠ Ni vnešenih kontaktov. Pojdite v zavihek <b style={{color:"#3B82F6"}}>Kontakti</b>.
    </div>
  );

  const handleQuickEmail = () => {
    const emails = quickEmail.split(/[;,\n]/).map(e=>e.trim()).filter(e=>e.includes("@"));
    if (emails.length === 0) return;
    emails.forEach(e => onAddChosen(e));
    setQuickEmail("");
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {filledGroups.map(g=>{
        const members = g.members.filter(m=>m.ime||m.email||m.tel);
        const allUids = members.filter(m=>m.email).map(m=>`${g.id}_${m.id}`);
        const chosenCount = allUids.filter(u=>chosenRec.has(u)).length;
        const anyChosen = chosenCount > 0;
        const allChosen = allUids.length > 0 && allUids.every(u=>chosenRec.has(u));
        // Auto-expand if only 1 member
        const singleMember = members.length === 1;
        const isOpen = singleMember || openCat===g.id;

        const toggleAll = (e) => {
          e.stopPropagation();
          if (allChosen) allUids.forEach(u=>chosenRec.has(u)&&toggleRec(u));
          else allUids.forEach(u=>!chosenRec.has(u)&&toggleRec(u));
        };

        return (
          <div key={g.id} style={{border:`1px solid ${anyChosen?"rgba(59,130,246,0.4)":isOpen&&!singleMember?"rgba(0,0,0,0.096)":"rgba(0,0,0,0.056)"}`,borderRadius:9,overflow:"hidden",transition:"border-color 0.15s"}}>
            {/* Category header — only clickable if multiple members */}
            <div
              onClick={singleMember ? undefined : ()=>setOpenCat(isOpen?null:g.id)}
              style={{display:"flex",alignItems:"center",gap:9,padding:"9px 12px",cursor:singleMember?"default":"pointer",background:isOpen&&!singleMember?"rgba(0,0,0,0.024)":"transparent"}}>
              <span style={{fontSize:15,flexShrink:0}}>{g.ikona}</span>
              <span style={{fontSize:13,fontWeight:600,color:anyChosen?"#1A1D24":"#4B5563",flex:1}}>{g.label}</span>
              <div style={{display:"flex",alignItems:"center",gap:7}} onClick={e=>e.stopPropagation()}>
                {/* Select all checkbox for category */}
                {allUids.length > 0 && (
                  <div onClick={toggleAll} style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",padding:"2px 6px",borderRadius:5,background:"rgba(0,0,0,0.04)",border:"1px solid rgba(0,0,0,0.064)"}}>
                    <div style={{width:13,height:13,borderRadius:3,flexShrink:0,background:allChosen?"#3B82F6":anyChosen?"rgba(59,130,246,0.4)":"transparent",border:allChosen?"2px solid #3B82F6":"2px solid #9AA3B0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff"}}>{allChosen?"✓":anyChosen?"–":""}</div>
                    <span style={{fontSize:11,color:"#5A6472",whiteSpace:"nowrap"}}>Vsi</span>
                  </div>
                )}
                {anyChosen&&<span style={{fontSize:11,background:"rgba(59,130,246,0.2)",color:"#2563EB",borderRadius:4,padding:"1px 6px",fontWeight:700}}>{chosenCount}/{allUids.length}</span>}
                {!singleMember&&<span style={{fontSize:11,color:"#5A6472",transform:isOpen?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.2s"}}>▾</span>}
              </div>
            </div>

            {/* Members — shown if open or single */}
            {isOpen&&(
              <div style={{borderTop:"1px solid rgba(0,0,0,0.048)",background:"rgba(0,0,0,0.016)"}}>
                {members.map(m=>{
                  const muid=`${g.id}_${m.id}`;
                  const sel=chosenRec.has(muid);
                  const hasEmail=!!m.email;
                  return (
                    <div key={m.id} onClick={()=>hasEmail&&toggleRec(muid)} style={{
                      display:"flex",alignItems:"center",gap:10,padding:"8px 14px",
                      cursor:hasEmail?"pointer":"default",
                      borderBottom:"1px solid rgba(0,0,0,0.032)",
                      background:sel?"rgba(59,130,246,0.08)":"transparent",
                      opacity:hasEmail?1:0.55
                    }}>
                      <div style={{width:15,height:15,borderRadius:4,flexShrink:0,background:sel?"#3B82F6":"transparent",border:sel?"2px solid #3B82F6":"2px solid #9AA3B0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff"}}>{sel?"✓":""}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:sel?600:400,color:sel?"#1A1D24":"#4B5563"}}>{m.ime||"(brez imena)"}</div>
                        {m.email&&<div style={{fontSize:11,color:"#3B82F6",marginTop:1}}>{m.email}</div>}
                        {m.tel&&<div style={{fontSize:11,color:"#10B981",marginTop:1}}>{m.tel}</div>}
                        {!m.email&&<div style={{fontSize:11,color:"#EF4444",marginTop:1}}>⚠ ni e-maila</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Quick add email */}
      <div style={{marginTop:4,display:"flex",alignItems:"center",gap:7}}>
        <input value={quickEmail} onChange={e=>setQuickEmail(e.target.value)}
          placeholder="Vpišite e-mail in pritisnite Enter (več z ; ali ,)"
          onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); handleQuickEmail(); } }}
          style={{flex:1,background:"rgba(0,0,0,0.04)",border:"1px solid rgba(0,0,0,0.08)",borderRadius:7,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"6px 10px"}}/>
        <button onClick={handleQuickEmail} style={{background:"rgba(59,130,246,0.15)",border:"1px solid rgba(59,130,246,0.3)",color:"#2563EB",borderRadius:7,padding:"6px 12px",fontSize:13,fontFamily:"inherit",cursor:"pointer",whiteSpace:"nowrap"}}>＋ Dodaj</button>
      </div>
    </div>
  );
}

function DocsView({ docs, activeDokKat, setActiveDokKat, dragOver, setDragOver, handleFiles, removeDoc, updateDoc, fileInputRef, project, contacts }) {
  const [openTip, setOpenTip] = useState(activeDokKat||null);
  const [selected, setSelected] = useState(new Set());
  const [showEmail, setShowEmail] = useState(false);
  const [chosenRec, setChosenRec] = useState(new Set());
  const [manualEmails, setManualEmails] = useState([]);
  const [subject, setSubject] = useState(`Dokumentacija – ${project.naziv}`);
  const [body, setBody] = useState(`Pozdravljeni,\n\nv priponki vam pošiljamo dokumentacijo za projekt ${project.naziv}.\n\nLep pozdrav`);

  const allMembers = (contacts||[]).flatMap(g =>
    g.members.filter(m=>m.ime||m.email).map(m=>({ ...m, groupLabel:g.label, groupIkona:g.ikona, uid:`${g.id}_${m.id}` }))
  );

  const toggleRec = uid => setChosenRec(prev => { const n=new Set(prev); n.has(uid)?n.delete(uid):n.add(uid); return n; });

  // Inicializiraj vse tipe z praznimi polji
  const migratedDocs = Object.fromEntries(DOC_TIPI.map(t=>[t.id, []]));
  // Kopiraj obstoječe dokumente
  DOC_TIPI.forEach(t => {
    if (docs[t.id]?.length) migratedDocs[t.id] = [...docs[t.id]];
  });
  // Migracija starih dokumentov v ostalo
  const oldIds = ["pzi","pgd","gd","geodetski","pogodbe_staro"];
  oldIds.forEach(oldId => {
    if (docs[oldId]?.length) migratedDocs.ostalo = [...migratedDocs.ostalo, ...docs[oldId]];
  });

  const totalDocs = DOC_TIPI.reduce((a,t)=>(migratedDocs[t.id]?.length||0)+a, 0);

  const sendEmail = (files) => {
    const toList = [...chosenRec].map(uid => allMembers.find(m=>m.uid===uid)?.email).filter(Boolean);
    toList.push(...manualEmails);
    if (toList.length === 0) { alert("Izberite vsaj enega prejemnika."); return; }
    const fileNames = files.map(f=>f.naziv).join(", ");
    const fullBody = `${body}\n\nPriložene datoteke: ${fileNames}`;
    window.open(`mailto:${toList.join(",")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fullBody)}`);
  };

  const STATUSI = [
    { id:"potrjeno",   label:"Izbrano",           color:"#10B981", bg:"rgba(52,211,153,0.12)"  },
    { id:"cakam",      label:"Čakam dopolnitev",  color:"#F59E0B", bg:"rgba(245,158,11,0.12)"  },
    { id:"ni_izbrano", label:"Ni izbrano",         color:"#EF4444", bg:"rgba(239,68,68,0.12)"  },
  ];

  return (
    <div>
      <div style={{marginBottom:14}}>
        <h2 style={{fontSize:18,fontWeight:700,color:"#1A1D24"}}>Dokumenti</h2>
        <p style={{fontSize:13,color:"#5A6472",marginTop:3}}>Naložite dokumente in jih po potrebi pošljite izvajalcem · {totalDocs} dokumentov</p>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {DOC_TIPI.map(tip => {
          const tipDocs = migratedDocs[tip.id] || [];
          const isOpen = openTip === tip.id;
          return (
            <div key={tip.id} style={{background:"#FFFFFF",border:`1px solid ${tipDocs.length>0?"rgba(59,130,246,0.25)":"rgba(0,0,0,0.056)"}`,borderRadius:12,overflow:"hidden"}}>
              {/* Header */}
              <div onClick={()=>setOpenTip(isOpen?null:tip.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",cursor:"pointer"}}>
                <div style={{width:34,height:34,background:"rgba(0,0,0,0.04)",borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{tip.ikona}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:"#1A1D24"}}>{tip.naziv}</div>
                  <div style={{fontSize:11,color:"#5A6472",marginTop:1}}>{tipDocs.length>0?`${tipDocs.length} dokumentov`:tip.opis}</div>
                </div>
                {tipDocs.length>0&&<span style={{fontSize:11,background:"rgba(59,130,246,0.15)",color:"#2563EB",borderRadius:10,padding:"2px 8px",fontWeight:600}}>{tipDocs.length}</span>}
                <span style={{fontSize:13,color:"#5A6472",transform:isOpen?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.2s"}}>▾</span>
              </div>

              {/* Vsebina */}
              {isOpen&&(
                <div style={{borderTop:"1px solid rgba(0,0,0,0.048)",padding:"12px 16px"}}>
                  {/* Upload area */}
                  <div
                    onDragOver={e=>{e.preventDefault();setDragOver(tip.id);}}
                    onDragLeave={()=>setDragOver(null)}
                    onDrop={e=>{e.preventDefault();setDragOver(null);handleFiles(tip.id,e.dataTransfer.files);}}
                    onClick={()=>{fileInputRef.current.dataset.katid=tip.id;fileInputRef.current.click();}}
                    style={{border:`2px dashed ${dragOver===tip.id?"rgba(59,130,246,0.6)":"rgba(0,0,0,0.08)"}`,borderRadius:8,padding:"12px",textAlign:"center",cursor:"pointer",marginBottom:12,background:dragOver===tip.id?"rgba(59,130,246,0.05)":"transparent"}}>
                    <div style={{fontSize:13,color:"#5A6472"}}>Povlecite datoteke ali kliknite za nalaganje</div>
                  </div>

                  {/* Dokumenti */}
                  {tipDocs.length===0&&<div style={{fontSize:12,color:"#5A6472",textAlign:"center",padding:"8px 0"}}>Ni dokumentov</div>}
                  {tipDocs.map(f=>(
                    <div key={f.id} style={{background:"rgba(0,0,0,0.024)",border:"1px solid rgba(0,0,0,0.056)",borderRadius:8,padding:"10px 12px",marginBottom:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:tip.hasStatus?8:0}}>
                        <span style={{fontSize:14,flexShrink:0}}>{f.tip==="PDF"?<FileText size={15}/>:f.tip==="DOCX"||f.tip==="DOC"?<FileText size={15}/>:f.tip==="XLSX"||f.tip==="XLS"?<FileSpreadsheet size={15}/>:<Paperclip size={14}/>}</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:600,color:"#1A1D24",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={f.naziv}>{f.naziv}</div>
                          <div style={{fontSize:11,color:"#5A6472",marginTop:1}}>{f.tip} · {f.datum} · {f.velikost?(f.velikost/1024/1024).toFixed(1)+" MB":""}</div>
                        </div>
                        <div style={{display:"flex",gap:6,flexShrink:0}}>
                          {f.url&&<a href={f.url} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:"#3B82F6",textDecoration:"none"}}>↗ Odpri</a>}
                          <button onClick={()=>removeDoc(tip.id,f.id)} className="hov-del T" style={{background:"transparent",border:"none",color:"#5A6472",fontSize:13,cursor:"pointer",padding:"0 2px"}}><Trash2 size={14}/></button>
                        </div>
                      </div>

                      {/* Kategorija dropdown */}
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                        <span style={{fontSize:11,color:"#5A6472",whiteSpace:"nowrap"}}>Kategorija:</span>
                        <select value={f.katId||""} onChange={e=>updateDoc(tip.id,f.id,{katId:e.target.value})}
                          style={{maxWidth:220,background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.12)",borderRadius:6,color:f.katId?"#1A1D24":"#5A6472",fontFamily:"inherit",fontSize:11,padding:"3px 8px",cursor:"pointer"}}>
                          <option value="">— Izberi kategorijo —</option>
                          {project.kats.map(k=>(
                            <option key={k.id} value={k.id}>{k.ikona} {k.naziv}</option>
                          ))}
                        </select>
                      </div>

                      {/* Status gumbi za predračune */}
                      {tip.hasStatus&&(
                        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          {STATUSI.map(s=>(
                            <button key={s.id} onClick={()=>updateDoc(tip.id,f.id,{status:f.status===s.id?null:s.id})}
                              style={{fontSize:11,padding:"3px 10px",borderRadius:20,border:`1px solid ${f.status===s.id?s.color:"rgba(0,0,0,0.08)"}`,background:f.status===s.id?s.bg:"transparent",color:f.status===s.id?s.color:"#5A6472",cursor:"pointer",fontFamily:"inherit",fontWeight:f.status===s.id?600:400}}>
                              {s.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Pošlji email */}
                  {tipDocs.length>0&&(
                    <button onClick={()=>setShowEmail(showEmail===tip.id?null:tip.id)}
                      style={{marginTop:4,background:"rgba(59,130,246,0.1)",border:"1px solid rgba(59,130,246,0.3)",color:"#2563EB",borderRadius:7,padding:"6px 14px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                      Pošlji po e-pošti
                    </button>
                  )}

                  {/* Email panel */}
                  {showEmail===tip.id&&(
                    <div style={{marginTop:10,background:"rgba(0,0,0,0.024)",border:"1px solid rgba(0,0,0,0.064)",borderRadius:8,padding:"12px"}}>
                      <div style={{fontSize:11,fontWeight:600,color:"#1A1D24",marginBottom:8}}>Prejemniki</div>
                      {allMembers.filter(m=>m.email).map(m=>(
                        <div key={m.uid} onClick={()=>toggleRec(m.uid)} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",cursor:"pointer"}}>
                          <div style={{width:16,height:16,borderRadius:4,border:`1px solid ${chosenRec.has(m.uid)?"#3B82F6":"rgba(0,0,0,0.14)"}`,background:chosenRec.has(m.uid)?"#3B82F6":"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#1A1D24",flexShrink:0}}>{chosenRec.has(m.uid)?"✓":""}</div>
                          <span style={{fontSize:12,color:"#1A1D24"}}>{m.ime||m.email}</span>
                          <span style={{fontSize:11,color:"#5A6472"}}>{m.email}</span>
                        </div>
                      ))}
                      <button onClick={()=>sendEmail(tipDocs)} style={{marginTop:10,background:"rgba(59,130,246,0.2)",border:"1px solid rgba(59,130,246,0.4)",color:"#2563EB",borderRadius:7,padding:"7px 16px",fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
                        Pošlji →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <input ref={fileInputRef} type="file" multiple style={{display:"none"}}
        onChange={e=>{const kid=fileInputRef.current.dataset.katid;handleFiles(kid,e.target.files);e.target.value="";}}/>
    </div>
  );
}

// ─── Share Modal — deljenje projekta s partnerjem ───────────────────────────────
function ShareModal({ project, onClose }) {
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadMembers = async () => {
    if (!supabase || !project._dbId) { setLoading(false); return; }
    setLoading(true);
    try {
      // RPC vrne člane skupaj z emaili (auth.users ni javno berljiv)
      const { data, error } = await supabase.rpc('list_project_members', { p_project: project._dbId });
      if (!error && data) setMembers(data);
    } catch(e) {}
    setLoading(false);
  };

  useEffect(() => { loadMembers(); }, []);

  const povabi = async () => {
    setMsg(null);
    const mail = email.trim().toLowerCase();
    if (!mail || !mail.includes("@")) { setMsg({t:"err",x:"Vnesite veljaven e-mail."}); return; }
    setBusy(true);
    try {
      // poišči uporabnika po emailu prek varne RPC funkcije
      const { data: uid, error: e1 } = await supabase.rpc('get_user_id_by_email', { p_email: mail });
      if (e1 || !uid) {
        setMsg({t:"err",x:"Ta oseba še nima računa na MojaGradnja. Naj se najprej registrira, nato jo povabite."});
        setBusy(false); return;
      }
      if (uid === project._ownerId) {
        setMsg({t:"err",x:"To ste vi (lastnik projekta)."});
        setBusy(false); return;
      }
      const { error: e2 } = await supabase
        .from('project_members')
        .insert({ project_id: project._dbId, user_id: uid, role });
      if (e2) {
        if (e2.code === '23505') setMsg({t:"err",x:"Ta oseba je že dodana."});
        else setMsg({t:"err",x:"Napaka pri dodajanju."});
      } else {
        setMsg({t:"ok",x:"Oseba je bila dodana."});
        setEmail("");
        loadMembers();
        // Pošlji email povabila (neblokirajoče — če spodleti, dodajanje vseeno velja)
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const inviterEmail = session?.user?.email || "";
          supabase.functions.invoke('send-invite-email', {
            body: { toEmail: mail, projectName: project.naziv, inviterEmail, role, isNewUser: false }
          }).catch(()=>{});
        } catch(_) {}
      }
    } catch(e) { setMsg({t:"err",x:"Napaka pri povezavi."}); }
    setBusy(false);
  };

  const spremeniVlogo = async (id, newRole) => {
    await supabase.from('project_members').update({ role: newRole }).eq('id', id);
    loadMembers();
  };
  const odstrani = async (id) => {
    await supabase.from('project_members').delete().eq('id', id);
    loadMembers();
  };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#FFFFFF",borderRadius:20,padding:0,width:"100%",maxWidth:520,maxHeight:"86vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 24px 70px rgba(0,0,0,0.28)"}}>
        <div style={{padding:"20px 24px",borderBottom:"1px solid rgba(0,0,0,0.07)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:"#1A1D24",display:"flex",alignItems:"center",gap:9}}><Users size={19}/> Deli projekt</div>
            <div style={{fontSize:13,color:"#5A6472",marginTop:3}}>{project.naziv}</div>
          </div>
          <button onClick={onClose} style={{background:"rgba(0,0,0,0.05)",border:"none",borderRadius:9,width:32,height:32,cursor:"pointer",fontSize:18,color:"#5A6472"}}>×</button>
        </div>

        <div style={{padding:"20px 24px",overflowY:"auto"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#1A1D24",marginBottom:8}}>Povabi osebo</div>
          <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
            <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="email@primer.si" type="email"
              style={{flex:1,minWidth:180,height:40,padding:"0 13px",borderRadius:11,border:"1px solid rgba(0,0,0,0.12)",fontSize:14,fontFamily:"inherit",outline:"none",color:"#1A1D24"}}/>
            <select value={role} onChange={e=>setRole(e.target.value)}
              style={{height:40,padding:"0 12px",borderRadius:11,border:"1px solid rgba(0,0,0,0.12)",fontSize:14,fontFamily:"inherit",cursor:"pointer",color:"#1A1D24",background:"#fff"}}>
              <option value="editor">Urejevalec</option>
              <option value="viewer">Gledalec</option>
            </select>
          </div>
          <div style={{fontSize:12,color:"#5A6472",marginBottom:6}}>
            {role==="editor" ? "Urejevalec lahko dodaja stroške, ureja faze in nalaga dokumente." : "Gledalec lahko samo spremlja projekt, ne more ničesar spremeniti."}
          </div>
          <button onClick={povabi} disabled={busy} className="T" style={{width:"100%",height:42,background:busy?"rgba(59,130,246,0.5)":"#3B82F6",color:"#fff",border:"none",borderRadius:11,fontSize:14,fontWeight:700,cursor:busy?"default":"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            <Plus size={16}/> {busy?"Dodajam…":"Povabi"}
          </button>
          {msg && <div style={{marginTop:10,padding:"9px 12px",borderRadius:10,fontSize:13,fontWeight:600,
            background:msg.t==="ok"?"rgba(16,185,129,0.1)":"rgba(239,68,68,0.09)",
            color:msg.t==="ok"?"#059669":"#DC2626"}}>{msg.x}</div>}

          <div style={{fontSize:13,fontWeight:700,color:"#1A1D24",margin:"22px 0 10px"}}>Osebe z dostopom</div>
          {loading ? (
            <div style={{fontSize:13,color:"#5A6472",padding:"12px 0"}}>Nalagam…</div>
          ) : members.length===0 ? (
            <div style={{fontSize:13,color:"#5A6472",padding:"14px",background:"rgba(0,0,0,0.03)",borderRadius:11,textAlign:"center"}}>Projekt še ni deljen z nikomer.</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {members.map(m => (
                <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"rgba(0,0,0,0.03)",borderRadius:11}}>
                  <div style={{width:34,height:34,borderRadius:"50%",background:"rgba(59,130,246,0.15)",border:"1px solid rgba(59,130,246,0.3)",display:"flex",alignItems:"center",justifyContent:"center",color:"#2563EB",fontWeight:700,fontSize:14,flexShrink:0}}>
                    {m.email ? m.email[0].toUpperCase() : "?"}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:"#1A1D24",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.email || m.user_id.slice(0,8)+"…"}</div>
                  </div>
                  <select value={m.role} onChange={e=>spremeniVlogo(m.id, e.target.value)}
                    style={{height:32,padding:"0 8px",borderRadius:8,border:"1px solid rgba(0,0,0,0.12)",fontSize:12,fontFamily:"inherit",cursor:"pointer",color:"#1A1D24",background:"#fff"}}>
                    <option value="editor">Urejevalec</option>
                    <option value="viewer">Gledalec</option>
                  </select>
                  <button onClick={()=>odstrani(m.id)} className="T" style={{background:"transparent",border:"none",color:"#9AA3B0",cursor:"pointer",padding:4,display:"flex"}}><Trash2 size={15}/></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Project View ──────────────────────────────────────────────────────────────
function ProjectView({ project, onUpdate, userId }) {
  const [pogled, setPogled] = useState("stroskovnik");
  const [aktivnaKat, setAktivnaKat] = useState(null);
  const [katModal, setKatModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [showBudget, setShowBudget] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState(null);
  const [viewerWarn, setViewerWarn] = useState(false);
  const [myRole, setMyRole] = useState(project._role); // prava vloga, osvežena iz baze
  const [dragOver, setDragOver] = useState(false);
  const isOwner = myRole === 'owner';               // samo lastnik deli in briše
  const canEdit = myRole !== 'viewer';              // viewer ne sme urejati
  // Za deljene projekte naloži pravo vlogo + email lastnika (sveže ob vsakem odprtju)
  useEffect(() => {
    if (project._role === 'owner' || !supabase || !project._dbId) return;
    (async () => {
      try {
        const { data } = await supabase.rpc('get_email_by_user_id', { p_uid: project._ownerId });
        if (data) setOwnerEmail(data);
      } catch(_) {}
      try {
        const { data: mem } = await supabase.rpc('my_role_in_project', { p_project: project._dbId });
        if (mem) setMyRole(mem);
      } catch(_) {}
    })();
  }, [project._dbId, project._ownerId, project._role]);
  const [activeDokKat, setActiveDokKat] = useState("pogodbe");
  const fileInputRef = useRef();
  const docInputRef = useRef();

  const kats = (project.tip === "prenova" || project.kats.some(k=>k.naziv==="Pregled & Ocena stanja"))
    ? project.kats.filter(k => k.naziv !== "Parcela & Komunalna ureditev")
    : project.kats;
  const docs = project.docs;
  const skupniNp  = projNp(project);
  const skupniBud = projBud(project);
  const skupniPor = projPor(project);
  const totalDocs = Object.values(docs).reduce((a,v)=>a+v.length,0);
  const cenaM2 = (project.povrsina>0&&skupniBud>0) ? Math.round(skupniBud/project.povrsina) : null;
  const porM2  = (project.povrsina>0&&skupniPor>0)  ? Math.round(skupniPor/project.povrsina)  : null;

  const upd = fn => {
    if (!canEdit) {
      // Gledalec ne more urejati — pokaži kratko opozorilo namesto tihe zavrnitve
      setViewerWarn(true);
      setTimeout(() => setViewerWarn(false), 2200);
      return;
    }
    onUpdate(p=>({...p,...fn(p)}));
  };
  const setKats = fn => upd(p=>({kats:fn(p.kats)}));
  const updKat = (kid,fn) => setKats(ks=>ks.map(k=>k.id===kid?fn(k):k));

  const addKat = ({naziv,ikona,barva}) => setKats(ks=>[...ks, mkKat(naziv,ikona,barva,[])]);
  const editKatMeta = (kid,data) => updKat(kid,k=>({...k,...data}));
  const deleteKat = kid => { setKats(ks=>ks.filter(k=>k.id!==kid)); if(aktivnaKat===kid) setAktivnaKat(null); };
  const moveKat = (kid, smer) => setKats(ks => {
    const i = ks.findIndex(k=>k.id===kid);
    if (i<0) return ks;
    if (smer==="gor" && i===0) return ks;
    if (smer==="dol" && i===ks.length-1) return ks;
    const novo = [...ks];
    const j = smer==="gor" ? i-1 : i+1;
    [novo[i], novo[j]] = [novo[j], novo[i]];
    return novo;
  });
  const renameKat = (kid,naziv) => updKat(kid,k=>({...k,naziv}));

  const addPost = kid => updKat(kid,k=>({...k,postavke:[...k.postavke,mkPostavka("Nova postavka")]}));
  const delPost = (kid,pid) => updKat(kid,k=>({...k,postavke:k.postavke.filter(p=>p.id!==pid)}));
  const setPost = (kid,pid,field,raw) => {
    const val = field==="naziv"?raw:clamp(raw,0,field==="napredek"?100:99999999);
    updKat(kid,k=>({...k,postavke:k.postavke.map(p=>p.id===pid?{...p,[field]:val}:p)}));
  };

  const handleFiles = async (kid, files) => {
    const filesArr = Array.from(files);
    if (!supabase || !userId) {
      // Brez Supabase — lokalno (izgine po osvežitvi)
      const nf = filesArr.map(f=>({id:uid(),naziv:f.name,velikost:f.size,tip:f.name.split(".").pop().toUpperCase(),datum:new Date().toLocaleDateString("sl-SI"),url:URL.createObjectURL(f)}));
      upd(p=>({docs:{...p.docs,[kid]:[...p.docs[kid],...nf]}}));
      return;
    }
    for (const f of filesArr) {
      const ext = f.name.split('.').pop();
      const fname = `${uid()}.${ext}`;
      const path = `${userId}/${project.id}/${kid}/${fname}`;
      const { error } = await supabase.storage.from('project-docs').upload(path, f);
      if (error) { continue; }
      const { data: urlData } = await supabase.storage.from('project-docs').createSignedUrl(path, 31536000); // 1 leto
      const newDoc = { id:uid(), naziv:f.name, velikost:f.size, tip:ext.toUpperCase(), datum:new Date().toLocaleDateString("sl-SI"), url:urlData?.signedUrl, storagePath:path };
      upd(p=>({docs:{...p.docs,[kid]:[...(p.docs[kid]||[]),newDoc]}}));
    }
  };

  const removeDoc = async (kid, fid) => {
    const doc = project.docs[kid]?.find(f=>f.id===fid);
    if (doc?.storagePath && supabase) {
      await supabase.storage.from('project-docs').remove([doc.storagePath]);
    }
    upd(p=>({docs:{...p.docs,[kid]:(p.docs[kid]||[]).filter(f=>f.id!==fid)}}));
  };

  const updateDoc = (kid, fid, changes) => {
    upd(p=>({docs:{...p.docs,[kid]:p.docs[kid].map(f=>f.id===fid?{...f,...changes}:f)}}));
  };

  const saveBudget = data => {
    upd(() => ({ lastna:data.lastna, kredit:data.kredit, povrsina:data.povrsina, parcela:data.parcela||0, kats:data.kats }));
    setShowBudget(false);
  };

  const aktivnaKatObj = aktivnaKat ? kats.find(k=>k.id===aktivnaKat) : null;

  return (
    <div style={{display:"flex",flex:1,overflow:"hidden",flexDirection:"column"}}>
      {/* project info card */}
      <div style={{background:"#FFFFFF",borderBottom:"1px solid rgba(0,0,0,0.048)",flexShrink:0,overflowX:"auto"}}>
        <div style={{margin:"10px 14px 12px",padding:"13px 16px",background:"rgba(0,0,0,0.024)",border:"1px solid rgba(0,0,0,0.064)",borderRadius:18,display:"flex",alignItems:"center",gap:14,minWidth:"max-content"}}>
          <div style={{width:48,height:48,borderRadius:13,background:"rgba(0,0,0,0.04)",border:"1px solid rgba(0,0,0,0.072)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>{project.ikona}</div>
          <div style={{flexShrink:0}}>
            <div style={{fontSize:19,fontWeight:800,color:"#1A1D24",whiteSpace:"nowrap",letterSpacing:-0.3}}>{project.naziv}</div>
            <div style={{display:"flex",gap:9,marginTop:4,alignItems:"center"}}>
              {project.lokacija&&<span style={{fontSize:12,color:"#5A6472",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:4}}><MapPin size={12}/>{project.lokacija}</span>}
              {project.lokacija&&cenaM2&&<span style={{color:"rgba(0,0,0,0.12)"}}>|</span>}
              {cenaM2&&<span style={{fontSize:12,color:"#7C3AED",fontWeight:600,whiteSpace:"nowrap",background:"rgba(196,181,253,0.08)",border:"1px solid rgba(196,181,253,0.18)",borderRadius:999,padding:"2px 10px",display:"inline-flex",alignItems:"center",gap:5}}><Ruler size={11}/>{fmtN(cenaM2)} €/m²</span>}
            </div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0,marginLeft:"auto"}}>
            {project.lastna>0&&<div style={{fontSize:13,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.22)",color:"#059669",borderRadius:999,padding:"7px 15px",fontWeight:700,whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:8}}><span style={{width:8,height:8,borderRadius:"50%",background:"#10B981",display:"inline-block"}}/>{fmt(project.lastna)}</div>}
            {project.kredit>0&&<div style={{fontSize:13,background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.22)",color:"#D97706",borderRadius:999,padding:"7px 15px",fontWeight:700,whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:8}}><span style={{width:8,height:8,borderRadius:"50%",background:"#F59E0B",display:"inline-block"}}/>{fmt(project.kredit)}</div>}
            {!isOwner&&<span style={{fontSize:12,background:"rgba(124,58,237,0.08)",border:"1px solid rgba(124,58,237,0.2)",color:"#7C3AED",borderRadius:999,padding:"6px 12px",fontWeight:700,whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:6}}><Users size={13}/>{canEdit?"Deljeno · urejate":"Deljeno · ogled"}{ownerEmail?` · ${ownerEmail}`:""}</span>}
            {isOwner&&<button onClick={()=>setShowShare(true)} className="T" style={{background:"rgba(0,0,0,0.04)",border:"1px solid rgba(0,0,0,0.096)",color:"#1A1D24",borderRadius:999,padding:"0 16px",height:36,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center",gap:7,whiteSpace:"nowrap"}}><Users size={15}/>Deli</button>}
            {canEdit&&<button onClick={()=>setShowBudget(true)} className="T" style={{background:"rgba(0,0,0,0.04)",border:"1px solid rgba(0,0,0,0.096)",color:"#1A1D24",borderRadius:999,padding:"0 16px",height:36,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center",gap:7,whiteSpace:"nowrap"}}><Wallet size={15}/>Budget</button>}
            <div style={{width:44,height:44,position:"relative",flexShrink:0}}>
              <svg viewBox="0 0 44 44" style={{transform:"rotate(-90deg)",width:44,height:44}}>
                <circle cx="22" cy="22" r="17" fill="none" stroke="rgba(0,0,0,0.056)" strokeWidth="3.5"/>
                <circle cx="22" cy="22" r="17" fill="none" stroke={project.barva} strokeWidth="3.5" strokeDasharray={`${2*Math.PI*17*skupniNp/100} ${2*Math.PI*17}`} strokeLinecap="round"/>
              </svg>
              <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#1A1D24"}}>{skupniNp}%</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{display:"flex",flex:1,overflow:"hidden"}}>
        {/* No sidebar - all tabs full width */}

        {/* main */}
        <main style={{flex:1,overflowY:"auto",padding:"22px 26px calc(100px + env(safe-area-inset-bottom))"}}>
          {confirm&&<ConfirmModal msg={confirm.msg} onOk={()=>{confirm.ok();setConfirm(null);}} onCancel={()=>setConfirm(null)}/>}
          {katModal&&<KatModal initial={katModal==="new"?null:kats.find(k=>k.id===katModal)}
            onSave={data=>{katModal==="new"?addKat(data):editKatMeta(katModal,data);setKatModal(null);}}
            onClose={()=>setKatModal(null)}/>}
          {showBudget&&<BudgetModal project={project} onSave={saveBudget} onClose={()=>setShowBudget(false)}/>}
          {showShare&&<ShareModal project={project} onClose={()=>setShowShare(false)}/>}
          {viewerWarn&&(
            <div style={{position:"fixed",bottom:90,left:"50%",transform:"translateX(-50%)",zIndex:3000,background:"#1A1D24",color:"#fff",padding:"12px 20px",borderRadius:12,fontSize:14,fontWeight:600,boxShadow:"0 8px 30px rgba(0,0,0,0.3)",display:"flex",alignItems:"center",gap:9,maxWidth:"90vw"}}>
              <Users size={16}/> Ste v načinu ogleda — urejanje ni mogoče.
            </div>
          )}

          {/* ── STROŠKOVNIK grid ── */}
          {pogled==="stroskovnik"&&!aktivnaKat&&(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                <div>
                  <h2 style={{fontSize:20,fontWeight:700,color:"#1A1D24"}}>Stroškovnik</h2>
                  {cenaM2&&<p style={{fontSize:13,color:"#7C3AED",marginTop:3}}>📐 {fmtN(cenaM2)} €/m² · {fmtN(project.povrsina)} m²{porM2?` · porabljeno ${fmtN(porM2)} €/m²`:""}</p>}
                </div>
                <div style={{display:"flex",gap:8}}>
                  {canEdit&&<Btn variant="amber" onClick={()=>setShowBudget(true)}>💰 Budget & m²</Btn>}
                  {canEdit&&<Btn variant="primary" onClick={()=>setKatModal("new")}>+ Nova kategorija</Btn>}
                </div>
              </div>

              {/* Opomba vrstni red */}
              <div style={{fontSize:11,color:"#9AA3B0",marginBottom:10}}>
                ▲▼ gumbi spremenijo vrstni red prikaza — ne vplivajo na terminski plan.
              </div>
              {project.tip==="prenova"&&(()=>{
                const pregledKat = kats.find(k=>k.naziv==="Pregled & Ocena stanja");
                if(!pregledKat) return null;
                const np = katNp(pregledKat);
                const por = katPor(pregledKat);
                if(np>0||por>0) return null; // že začeto — ne prikazuj
                return (
                  <div style={{background:"rgba(52,211,153,0.07)",border:"1px solid rgba(52,211,153,0.25)",borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:18}}>🔍</span>
                      <div>
                        <div style={{fontSize:13,fontWeight:600,color:"#059669"}}>Priporočamo začeti tukaj</div>
                        <div style={{fontSize:11,color:"#5A6472",marginTop:2}}>Pred začetkom del opravite strokovno oceno stanja objekta — odkrijte skrite napake in natančneje ocenite stroške.</div>
                      </div>
                    </div>
                    <button onClick={()=>setAktivnaKat(pregledKat.id)} style={{background:"rgba(52,211,153,0.15)",border:"1px solid rgba(52,211,153,0.3)",color:"#059669",borderRadius:7,padding:"6px 14px",fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:600,whiteSpace:"nowrap"}}>
                      Odpri kategorijo →
                    </button>
                  </div>
                );
              })()}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12}}>
                {kats.map(k=>{
                  const np=katNp(k); const bud=katBud(k); const por=katPor(k);
                  const bud_m2=(project.povrsina>0&&bud>0)?Math.round(bud/project.povrsina):null;
                  const budPct = bud>0 ? Math.min(Math.round(por/bud*100),999) : 0;
                  const budOver = por>bud && bud>0;
                  const prekoracitev = por - bud;
                  const prekoracitvaPct = bud>0 ? (prekoracitev/bud*100) : 0;
                  // Relativni pragovi glede na budget kategorije
                  const jeRdeca = budOver && prekoracitvaPct > 10;
                  const jeRumena = !jeRdeca && budPct >= 90 && np < 100 && bud > 0;
                  const budColor = jeRdeca ? "#EF4444" : jeRumena ? "#F59E0B" : "#10B981";
                  const delta = np - budPct;
                  const expectedSpend = bud>0 ? Math.round(bud*np/100) : 0;
                  const diffEur = por - expectedSpend;
                  const diffPct = bud>0 ? Math.abs(diffEur)/bud*100 : 0;

                  // Delta barva — relativni pragovi glede na budget kategorije
                  const deltaColor = diffEur<=0 ? "#10B981" :  // pod planom → zeleno
                                     diffPct<=8 ? "#10B981" :   // do 8% → zeleno
                                     diffPct<=15 ? "#F59E0B" :  // 8-15% → oranžno
                                     "#EF4444";                  // >15% → rdeče
                  const deltaIcon = delta > 10 ? "↑" : delta < -10 ? "↓" : "≈";
                  const deltaStr = bud>0&&por>0 ? `${delta>=0?"+":""}${delta}%  (${diffEur>0?"+":"−"}${fmt(Math.abs(diffEur))})` : null;

                  const shortStatus = jeRdeca ? {txt:"Over budget", color:"#EF4444", bg:"rgba(239,68,68,0.12)"} :
                                      jeRumena ? {txt:"Pozor", color:"#F59E0B", bg:"rgba(245,158,11,0.12)"} :
                                      np===100 ? {txt:"OK ✓", color:"#059669", bg:"rgba(52,211,153,0.08)"} :
                                      np>0 ? {txt:"V teku", color:"#2563EB", bg:"rgba(59,130,246,0.08)"} :
                                      {txt:"Ni začeto", color:"#5A6472", bg:"rgba(0,0,0,0.04)"};

                  const cardBorder = jeRdeca ? "rgba(239,68,68,0.4)" :
                                     jeRumena ? "rgba(245,158,11,0.35)" :
                                     np===100 ? "rgba(52,211,153,0.35)" :
                                     np>0 && diffEur>0 && diffPct>15 ? "rgba(239,68,68,0.4)" :
                                     np>0 && diffEur>0 && diffPct>5 ? "rgba(245,158,11,0.35)" :
                                     np>0 ? "rgba(59,130,246,0.35)" :
                                     "rgba(0,0,0,0.056)";

                  const cardBorderBright = jeRdeca ? "#EF4444" :
                                           jeRumena ? "#F59E0B" :
                                           np===100 ? "#10B981" :
                                           np>0 && diffEur>0 && diffPct>15 ? "#EF4444" :
                                           np>0 && diffEur>0 && diffPct>5 ? "#F59E0B" :
                                           np>0 ? "#3B82F6" :
                                           "rgba(0,0,0,0.14)";
                                     jeRumena ? "rgba(245,158,11,0.35)" :
                                     np===100 ? "rgba(52,211,153,0.35)" :
                                     np>0 ? "rgba(59,130,246,0.35)" :
                                     "rgba(0,0,0,0.056)";

                  return (
                    <div key={k.id} className="hov-card T" style={{"--hov":cardBorder,"--hov-bright":cardBorderBright,background:"#FFFFFF",border:`1px solid ${cardBorder}`,borderLeft:`3px solid ${cardBorder}`,borderRadius:12,padding:"14px 16px",cursor:"pointer"}}
                      onClick={()=>setAktivnaKat(k.id)}>

                      {/* Header */}
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,gap:8}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0,flex:1}}>
                          <div style={{width:32,height:32,background:`${k.barva}18`,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,border:`1px solid ${k.barva}30`,flexShrink:0}}>{k.ikona}</div>
                          <div style={{minWidth:0}}>
                            <div title={k.naziv} style={{fontSize:13,fontWeight:600,color:"#1A1D24",overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",lineHeight:1.3}}>{k.naziv}</div>
                            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3,flexWrap:"wrap"}}>
                              <span style={{fontSize:11,color:shortStatus.color,background:shortStatus.bg,borderRadius:20,padding:"1px 7px",fontWeight:600,whiteSpace:"nowrap"}}>{shortStatus.txt}</span>
                              {(k.delez>0||bud_m2)&&<span style={{fontSize:11,color:"#5A6472"}}>·</span>}
                              <span style={{fontSize:11,color:"#5A6472",whiteSpace:"nowrap"}}>
                                {k.delez>0?`${k.delez}% `:""}
                                {bud_m2?`${fmtN(bud_m2)} €/m²`:""}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0,marginLeft:6}} onClick={e=>e.stopPropagation()}>
                          <div style={{display:"flex",flexDirection:"column",gap:0}}>
                            <button onClick={()=>moveKat(k.id,"gor")} disabled={kats.indexOf(k)===0}
                              style={{background:"transparent",border:"none",color:kats.indexOf(k)===0?"#C2C8D0":"#9AA3B0",cursor:kats.indexOf(k)===0?"default":"pointer",lineHeight:0,padding:0,display:"flex"}}><ChevronUp size={15}/></button>
                            <button onClick={()=>moveKat(k.id,"dol")} disabled={kats.indexOf(k)===kats.length-1}
                              style={{background:"transparent",border:"none",color:kats.indexOf(k)===kats.length-1?"#C2C8D0":"#9AA3B0",cursor:kats.indexOf(k)===kats.length-1?"default":"pointer",lineHeight:0,padding:0,display:"flex"}}><ChevronDown size={15}/></button>
                          </div>
                          <button className="hov-edit T" onClick={()=>setKatModal(k.id)} style={{background:"transparent",border:"none",color:"#5A6472",fontSize:13,cursor:"pointer",padding:"2px 4px"}}><Pencil size={14}/></button>
                          <button className="hov-del T" onClick={()=>setConfirm({msg:<>Izbriši <b style={{color:"#fff"}}>{k.naziv}</b>?</>,ok:()=>deleteKat(k.id)})} style={{background:"transparent",border:"none",color:"#5A6472",fontSize:13,cursor:"pointer",padding:"2px 4px"}}><Trash2 size={14}/></button>
                        </div>
                      </div>

                      {/* ── Napredek gradnje ── */}
                      <div style={{marginBottom:10}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                          <span style={{fontSize:11,color:"#5A6472"}}>Napredek gradnje</span>
                          <span style={{fontSize:11,fontWeight:500,color:np===100?"#10B981":np>0?"#1A1D24":"#5A6472"}}>
                            {np>0?np+"%":"Ni začeto"}
                          </span>
                        </div>
                        <div style={{background:"rgba(0,0,0,0.064)",height:5,borderRadius:3,overflow:"visible",position:"relative"}}>
                          <div style={{width:np+"%",height:"100%",background:np===100?"#10B981":k.barva,borderRadius:3,transition:"width 0.4s"}}/>
                          {bud>0&&por>0&&budPct>2&&budPct<98&&(
                            <div style={{position:"absolute",top:-3,left:budPct+"%",width:3,height:11,background:"rgba(0,0,0,0.14)",borderRadius:2,transform:"translateX(-1px)",boxShadow:"0 0 4px rgba(0,0,0,0.12)"}}/>
                          )}
                        </div>
                      </div>

                      {/* ── Poraba — GLAVNA METRIKA ── */}
                      <div style={{background:"rgba(0,0,0,0.032)",borderRadius:8,padding:"10px 12px"}}>
                        <div style={{fontSize:22,fontWeight:700,color:budColor,lineHeight:1.1}}>
                          {bud>0?budPct+"%":"—"}
                        </div>
                        <div style={{fontSize:12,color:"#5A6472",marginTop:2}}>
                          {fmt(por)}{bud>0?` / ${fmt(bud)}`:""}
                          {jeRdeca&&<span style={{color:"#DC2626",marginLeft:6}}>+{fmt(por-bud)}</span>}
                        </div>
                        <div style={{background:"rgba(0,0,0,0.064)",height:4,borderRadius:2,marginTop:8,overflow:"hidden"}}>
                          <div style={{width:Math.min(100,budPct)+"%",height:"100%",background:budColor,borderRadius:2,transition:"width 0.4s"}}/>
                        </div>
                      </div>

                      {/* ── Delta / Preostanek ── */}
                      {bud>0&&por>0&&(
                        <div style={{paddingTop:7,marginTop:6,borderTop:"1px solid rgba(0,0,0,0.04)",display:"flex",alignItems:"center",gap:6}}>
                          {np===100 ? (
                            <>
                              {(() => {
                                const absPct = bud>0 ? Math.abs(diffEur)/bud*100 : 0;
                                if (diffEur<0) return <><span style={{fontSize:13,color:"#10B981"}}>↓</span><span style={{fontSize:11,fontWeight:600,color:"#10B981"}}>Pod planom</span></>;
                                if (absPct<=2) return <span style={{fontSize:11,fontWeight:600,color:"#10B981"}}>✓ Po planu</span>;
                                if (absPct<=8) return <><span style={{fontSize:13,color:"#10B981"}}>↑</span><span style={{fontSize:11,fontWeight:600,color:"#10B981"}}>Rahlo nad planom</span></>;
                                if (absPct<=15) return <><span style={{fontSize:13,color:"#F59E0B"}}>↑</span><span style={{fontSize:11,fontWeight:600,color:"#F59E0B"}}>Nad planom</span></>;
                                return <><span style={{fontSize:13,color:"#EF4444"}}>⚠</span><span style={{fontSize:11,fontWeight:600,color:"#EF4444"}}>Prekoračen budget</span></>;
                              })()}
                            </>
                          ) : (
                            <>
                              {bud-por>=0 ? (
                                <>
                                  <span style={{fontSize:11,color:"#5A6472"}}>Za porabiti še</span>
                                  <span style={{fontSize:11,fontWeight:600,color:"#5A6472"}}>{fmt(bud-por)}</span>
                                </>
                              ) : (
                                <>
                                  <span style={{fontSize:13,color:"#EF4444"}}>⚠</span>
                                  <span style={{fontSize:11,fontWeight:600,color:"#EF4444"}}>{fmt(Math.abs(bud-por))} nad budgetom</span>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── STROŠKOVNIK detail ── */}
          {pogled==="stroskovnik"&&aktivnaKat&&aktivnaKatObj&&(()=>{
            const k=aktivnaKatObj;
            const skupajP = k.postavke.find(p=>p.isSkupaj);
            const ostaleP = k.postavke.filter(p=>!p.isSkupaj);
            const pziSubsD = ostaleP.filter(p=>p.isPziSub);
            const pziParentD = ostaleP.find(p=>p.isPziParent);
            // Budget: use individual if any set, else skupaj
            const anyIndBudD = ostaleP.filter(p=>!p.isPziSub&&!p.isPziParent).some(p=>p.budget>0)
              || (pziParentD && (pziParentD.budget>0 || pziSubsD.some(s=>s.budget>0)));
            // Porabljeno: use individual if any set, else skupaj  
            const anyIndPorD = ostaleP.filter(p=>!p.isPziSub&&!p.isPziParent).some(p=>p.porabljeno>0)
              || (pziParentD && (pziParentD.porabljeno>0 || pziSubsD.some(s=>s.porabljeno>0)));
            const hasPosamezne = anyIndBudD;
            const effBud = katBud(k);
            const effPor = katPor(k);
            const np = katNp(k);
            const bud_m2=(project.povrsina>0&&effBud>0)?Math.round(effBud/project.povrsina):null;
            const por_m2=(project.povrsina>0&&effPor>0)?Math.round(effPor/project.povrsina):null;

            // When a posamezna postavka gets a value → clear skupaj budget
            const setPostWithAutoclear = (kid, pid, field, raw) => {
              if (field === "porabljeno") {
                const val = Number(raw) || 0;
                const p = k.postavke.find(x => x.id === pid);
                if (p) {
                  const newNapredek = val > 0 && p.napredek === 0 ? 50   // ni začeto → v teku
                                    : val === 0 && p.napredek > 0 && p.napredek < 100 ? 0  // pobriše → ni začeto
                                    : p.napredek; // ne spremeni zaključenega
                  if (newNapredek !== p.napredek) {
                    updKat(kid, k2 => ({
                      ...k2,
                      postavke: k2.postavke.map(x =>
                        x.id === pid ? { ...x, porabljeno: val, napredek: newNapredek } : x
                      )
                    }));
                    return;
                  }
                }
              }
              setPost(kid, pid, field, raw);
            };

            return (
              <div>
                {/* Breadcrumb navigation */}
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:18}}>
                  <button onClick={()=>setAktivnaKat(null)} className="T"
                    style={{display:"flex",alignItems:"center",gap:6,background:"rgba(0,0,0,0.04)",border:"1px solid rgba(0,0,0,0.08)",color:"#4B5563",cursor:"pointer",fontFamily:"inherit",fontSize:13,padding:"6px 14px",borderRadius:8,fontWeight:500}}>
                    <span style={{fontSize:15}}>←</span>
                    <span>Vse kategorije</span>
                  </button>
                  <span style={{color:"#5A6472",fontSize:15}}>›</span>
                  <div style={{display:"flex",alignItems:"center",gap:7,minWidth:0}}>
                    <span style={{fontSize:18,flexShrink:0}}>{k.ikona}</span>
                    <span title={k.naziv} style={{fontSize:15,fontWeight:700,color:"#1A1D24",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{k.naziv}</span>
                  </div>
                  <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center"}}>
                    {k.delez>0&&<span style={{fontSize:11,color:k.barva,background:`${k.barva}18`,borderRadius:5,padding:"2px 7px",fontFamily:"'Syne Mono',monospace",fontWeight:700}}>{k.delez}%</span>}
                    {bud_m2&&<span style={{fontSize:11,color:"#7C3AED",background:"rgba(167,139,250,0.12)",borderRadius:5,padding:"2px 7px"}}>📐 {fmtN(bud_m2)} €/m²</span>}
                    <div style={{display:"flex",flexDirection:"column",gap:1}}>
                      <button onClick={()=>moveKat(k.id,"gor")} disabled={kats.indexOf(k)===0}
                        style={{background:"transparent",border:"none",color:kats.indexOf(k)===0?"#C2C8D0":"#9AA3B0",cursor:kats.indexOf(k)===0?"default":"pointer",lineHeight:0,padding:0,display:"flex"}}><ChevronUp size={15}/></button>
                      <button onClick={()=>moveKat(k.id,"dol")} disabled={kats.indexOf(k)===kats.length-1}
                        style={{background:"transparent",border:"none",color:kats.indexOf(k)===kats.length-1?"#C2C8D0":"#9AA3B0",cursor:kats.indexOf(k)===kats.length-1?"default":"pointer",lineHeight:0,padding:0,display:"flex"}}><ChevronDown size={15}/></button>
                    </div>
                    <button className="hov-edit T" onClick={()=>setKatModal(k.id)} style={{background:"transparent",border:"none",color:"#5A6472",fontSize:13,cursor:"pointer",padding:"2px 5px"}}><Pencil size={14}/></button>
                    <button className="hov-del T" onClick={()=>setConfirm({msg:<>Izbriši <b style={{color:"#fff"}}>{k.naziv}</b>?</>,ok:()=>deleteKat(k.id)})} style={{background:"transparent",border:"none",color:"#5A6472",fontSize:13,cursor:"pointer",padding:"2px 5px"}}><Trash2 size={14}/></button>
                  </div>
                </div>
                {/* Budget summary bar */}
                <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:10,padding:"10px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:20,flexWrap:"wrap"}}>
                  <div style={{fontSize:11,color:"#5A6472"}}>Budget: <b style={{color:"#1A1D24",fontSize:13}}>{fmt(effBud)}</b></div>
                  <div style={{fontSize:11,color:"#5A6472"}}>Porabljeno: <b style={{color:effPor>effBud&&effBud>0?"#DC2626":"#059669",fontSize:13}}>{fmt(effPor)}</b></div>
                  {por_m2&&<div style={{fontSize:11,color:"#7C3AED"}}>📐 {fmtN(por_m2)} €/m²</div>}
                  <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:80,background:"rgba(0,0,0,0.048)",height:4,borderRadius:2}}>
                      <div style={{width:np+"%",height:"100%",background:np===100?"#10B981":k.barva,borderRadius:2,transition:"width 0.3s"}}/>
                    </div>
                    <span style={{fontSize:13,fontWeight:700,color:np===100?"#10B981":k.barva}}>{np}%</span>
                  </div>
                </div>

                <div style={{display:"flex",flexDirection:"column",gap:10}}>

                  {/* ── SKUPAJ KARTICA (vedno na vrhu) ── */}
                  {skupajP&&(
                    <div style={{background:"#FFFFFF",border:`1px solid ${k.barva}40`,borderRadius:12,overflow:"hidden"}}>
                      <div style={{padding:"12px 16px",background:`${k.barva}0C`}}>
                        {/* Title row */}
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:11,background:`${k.barva}25`,color:k.barva,borderRadius:5,padding:"2px 9px",fontWeight:700,letterSpacing:1}}>SKUPAJ</span>
                            <span style={{fontSize:13,color:"#1A1D24",fontWeight:600}}>{k.naziv}</span>
                            {hasPosamezne&&<span style={{fontSize:11,color:"#F59E0B",background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:4,padding:"1px 6px"}}>seštevek postavk</span>}
                          </div>
                          <div style={{fontSize:13,fontWeight:700,color:np===100?"#10B981":k.barva}}>{np}%</div>
                        </div>
                        {/* Budget row */}
                        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:10}}>
                          <div style={{display:"flex",alignItems:"center",gap:5}}>
                            <span style={{fontSize:11,color:"#5A6472"}}>Budget:</span>
                            {anyIndBudD
                              ? <span style={{fontSize:13,fontWeight:700,color:"#1A1D24",display:"flex",alignItems:"center",gap:6}}>{fmt(effBud)}<span style={{fontSize:11,color:"#3B82F6",background:"rgba(59,130,246,0.1)",borderRadius:3,padding:"1px 5px"}}>seštevek postavk</span></span>
                              : <NumInput min={0} value={skupajP.budget} onChange={v=>setPost(k.id,skupajP.id,"budget",v)} style={{width:110,background:"rgba(0,0,0,0.056)",border:`1px solid ${k.barva}60`,borderRadius:7,color:"#1A1D24",fontFamily:"inherit",fontSize:13,fontWeight:600,textAlign:"right",padding:"4px 8px"}}/>
                            }
                            <span style={{fontSize:11,color:"#5A6472"}}>€</span>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:5}}>
                            <span style={{fontSize:11,color:"#5A6472"}}>Porabljeno:</span>
                            {anyIndPorD
                              ? <span style={{fontSize:13,fontWeight:700,color:effPor>effBud&&effBud>0?"#DC2626":"#059669",display:"flex",alignItems:"center",gap:6}}>{fmt(effPor)}<span style={{fontSize:11,color:"#059669",background:"rgba(52,211,153,0.1)",borderRadius:3,padding:"1px 5px"}}>seštevek postavk</span></span>
                              : <NumInput min={0} value={skupajP.porabljeno} onChange={v=>{
                                  const val = Number(v)||0;
                                  const newNapredek = val>0 && skupajP.napredek===0 ? 50
                                                    : val===0 && skupajP.napredek>0 && skupajP.napredek<100 ? 0
                                                    : skupajP.napredek;
                                  if (newNapredek !== skupajP.napredek) {
                                    updKat(k.id, k2=>({...k2, postavke:k2.postavke.map(p=>p.id===skupajP.id?{...p,porabljeno:val,napredek:newNapredek}:p)}));
                                  } else {
                                    setPost(k.id, skupajP.id, "porabljeno", v);
                                  }
                                }} style={{width:110,background:"rgba(0,0,0,0.056)",border:`1px solid ${skupajP.porabljeno>skupajP.budget&&skupajP.budget>0?"rgba(239,68,68,0.4)":"rgba(0,0,0,0.08)"}`,borderRadius:7,color:skupajP.porabljeno>skupajP.budget&&skupajP.budget>0?"#DC2626":"#059669",fontFamily:"inherit",fontSize:13,fontWeight:600,textAlign:"right",padding:"4px 8px"}}/>
                            }
                            <span style={{fontSize:11,color:"#5A6472"}}>€</span>
                          </div>
                          {bud_m2&&!hasPosamezne&&<span style={{fontSize:11,color:"#7C3AED"}}>📐 {fmtN(bud_m2)} €/m²</span>}
                        </div>
                        {/* Status: computed from postavke if any active, else manual */}
                        {(hasPosamezne || anyIndPorD) ? (
                          <div style={{display:"flex",alignItems:"center",gap:10}}>
                            <div style={{display:"flex",gap:5}}>
                              {[{key:"caka",label:"Ni začeto",color:"#5A6472",icon:"○"},{key:"v_teku",label:"V teku",color:"#F59E0B",icon:"◐"},{key:"dokoncano",label:"Zaključeno",color:"#10B981",icon:"✓"}].map(d=>{
                                const stFromKat = katSt(k);
                                const active = d.key === stFromKat;
                                return (
                                  <div key={d.key} style={{height:28,borderRadius:7,border:`2px solid ${active?d.color:"rgba(0,0,0,0.08)"}`,background:active?`${d.color}22`:"transparent",color:active?d.color:"#5A6472",padding:"0 10px",fontSize:11,fontWeight:active?700:400,display:"flex",alignItems:"center",gap:5}}>
                                    <span style={{fontSize:13}}>{d.icon}</span><span>{d.label}</span>
                                    {d.key==="v_teku"&&active&&<span style={{opacity:0.7}}>({np}%)</span>}
                                  </div>
                                );
                              })}
                            </div>
                            <span style={{fontSize:11,color:"#5A6472",fontStyle:"italic"}}>samodejno iz postavk</span>
                          </div>
                        ) : (
                          <StatusDot napredek={skupajP.napredek} barva={k.barva} onChange={v=>setPost(k.id,skupajP.id,"napredek",v)}/>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── POSAMEZNE POSTAVKE (zložljive) ── */}
                  <PostavkeSection
                    k={k}
                    ostaleP={ostaleP}
                    hasPosamezne={hasPosamezne}
                    project={project}
                    setPost={setPostWithAutoclear}
                    updKat={updKat}
                    addPost={addPost}
                    delPost={delPost}
                    setConfirm={setConfirm}
                    fmt={fmt}
                    fmtN={fmtN}
                  />

                </div>
              </div>
            );
          })()}

          {/* ── NAPREDEK ── */}
          {pogled==="napredek"&&(
            <div>
              <div style={{marginBottom:16}}>
                <h2 style={{fontSize:18,fontWeight:700,color:"#1A1D24"}}>Napredek del</h2>
                <p style={{fontSize:13,color:"#5A6472",marginTop:3}}>Kliknite kategorijo za vnos napredka postavk</p>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {kats.map(k=>{
                  const np=katNp(k);const bud=katBud(k);const por=katPor(k);
                  const st=katSt(k);
                  return (
                    <div key={k.id} className="hov-row T" style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:10,padding:"12px 16px",cursor:"pointer"}}
                      onClick={()=>{setAktivnaKat(k.id);setPogled("stroskovnik");}}>
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                        <span style={{fontSize:18,flexShrink:0}}>{k.ikona}</span>
                        <span style={{fontSize:13,fontWeight:600,color:"#1A1D24",flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={k.naziv}>{k.naziv}</span>
                        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                          <div style={{background:statusColor[st]+"22",color:statusColor[st],fontSize:11,padding:"2px 7px",borderRadius:5,fontWeight:600,whiteSpace:"nowrap"}}>{statusLabel[st]}</div>
                          <span style={{fontSize:13,fontWeight:700,color:np===100?"#10B981":np>0?k.barva:"#9AA3B0",minWidth:36,textAlign:"right"}}>{np}%</span>
                        </div>
                      </div>
                      <div style={{background:"rgba(0,0,0,0.048)",height:5,borderRadius:3}}>
                        <div style={{width:np+"%",height:"100%",background:np===100?"#10B981":k.barva,borderRadius:3,transition:"width 0.4s"}}/>
                      </div>
                      {bud>0&&(
                        <div style={{display:"flex",gap:12,marginTop:6,fontSize:11,color:"#5A6472"}}>
                          <span>Budget: <b style={{color:"#5A6472"}}>{fmt(bud)}</b></span>
                          {por>0&&<span>Porabljeno: <b style={{color:por>bud?"#DC2626":"#059669"}}>{fmt(por)}</b></span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── PREGLED ── */}
          {pogled==="pregled"&&(()=>{
            const skupajBud = skupniBud;
            const skupajPor = skupniPor;

            const izvozExcel = () => {
              const XLSX = window.XLSX;
              if (!XLSX) { alert('Excel knjižnica se nalaga, poskusite čez sekundo.'); return; }
              const wb = XLSX.utils.book_new();

              // Sheet 1: Povzetek
              const povzetek = [
                ['MOJA GRADNJA — IZVOZ', '', '', ''],
                ['Projekt:', project.naziv, '', ''],
                ['Lokacija:', project.lokacija||'', '', ''],
                ['Datum izvoza:', new Date().toLocaleDateString('sl-SI'), '', ''],
                ['', '', '', ''],
                ['FINANČNI POVZETEK', '', '', ''],
                ['Skupni budget:', skupajBud, '', ''],
                ['Lastna sredstva:', project.lastna||0, '', ''],
                ['Kredit:', project.kredit||0, '', ''],
                ['Porabljeno:', skupajPor, '', ''],
                ['Ostalo:', skupajBud - skupajPor, '', ''],
                ['Napredek:', projNp(project) + '%', '', ''],
                ...(project.povrsina>0?[['Površina:', project.povrsina + ' m²', '', '']]:[]),
              ];
              const ws1 = XLSX.utils.aoa_to_sheet(povzetek);
              ws1['!cols'] = [{wch:25},{wch:18},{wch:18},{wch:18}];
              XLSX.utils.book_append_sheet(wb, ws1, 'Povzetek');

              // Sheet 2: Kategorije
              const katHeaders = ['Kategorija', 'Budget (€)', 'Porabljeno (€)', 'Ostalo (€)', '% Porabe', 'Napredek %', 'Status'];
              const katRows = kats.map(k => {
                const bud = katBud(k), por = katPor(k), np = katNp(k), st = katSt(k);
                return [
                  k.naziv,
                  bud,
                  por,
                  bud - por,
                  bud > 0 ? Math.round(por/bud*100) : 0,
                  np,
                  st === 'dokoncano' ? 'Zaključeno' : st === 'v_teku' ? 'V teku' : 'Čaka'
                ];
              });
              katRows.push(['SKUPAJ', skupajBud, skupajPor, skupajBud-skupajPor, skupajBud>0?Math.round(skupajPor/skupajBud*100):0, projNp(project), '']);
              const ws2 = XLSX.utils.aoa_to_sheet([katHeaders, ...katRows]);
              ws2['!cols'] = [{wch:30},{wch:15},{wch:15},{wch:15},{wch:12},{wch:12},{wch:12}];
              XLSX.utils.book_append_sheet(wb, ws2, 'Kategorije');

              // Sheet 3: Postavke
              const postHeaders = ['Kategorija', 'Postavka', 'Budget (€)', 'Porabljeno (€)', 'Napredek %'];
              const postRows = kats.flatMap(k =>
                k.postavke.filter(p => !p.isSkupaj).map(p => [
                  k.naziv, p.naziv, p.budget||0, p.porabljeno||0, p.napredek||0
                ])
              );
              const ws3 = XLSX.utils.aoa_to_sheet([postHeaders, ...postRows]);
              ws3['!cols'] = [{wch:30},{wch:35},{wch:15},{wch:15},{wch:12}];
              XLSX.utils.book_append_sheet(wb, ws3, 'Postavke');

              // Shrani
              XLSX.writeFile(wb, `${project.naziv} — Moja Gradnja.xlsx`);
            };

            return (
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                <h2 style={{fontSize:18,fontWeight:700,color:"#1A1D24"}}>Finančni pregled</h2>
                <button onClick={izvozExcel} style={{background:"rgba(52,211,153,0.15)",border:"1px solid rgba(52,211,153,0.3)",color:"#059669",borderRadius:8,padding:"7px 16px",fontSize:13,cursor:"pointer",fontFamily:"inherit",fontWeight:600,display:"flex",alignItems:"center",gap:7}}>
                  📊 Izvoz Excel
                </button>
              </div>

              {/* ── Kartice ── */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10,marginBottom:14}}>
                {/* Skupni budget — primarna kartica */}
                <div style={{background:"#FFFFFF",border:"1px solid rgba(96,165,250,0.25)",borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:11,color:"#5A6472",letterSpacing:1,marginBottom:5}}>SKUPNI BUDGET</div>
                  <div style={{fontSize:15,fontWeight:700,color:"#3B82F6"}}>{fmt(skupajBud)}</div>
                </div>
                {/* Porabljeno + % */}
                <div style={{background:"#FFFFFF",border:`1px solid ${skupajPor>skupajBud?"rgba(239,68,68,0.3)":"rgba(0,0,0,0.056)"}`,borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:11,color:"#5A6472",letterSpacing:1,marginBottom:5}}>PORABLJENO</div>
                  <div style={{fontSize:15,fontWeight:700,color:skupajPor>skupajBud?"#EF4444":"#10B981"}}>{fmt(skupajPor)}</div>
                  {skupajBud>0&&<div style={{fontSize:11,color:"#5A6472",marginTop:3}}>{Math.round(skupajPor/skupajBud*100)}% sredstev</div>}
                </div>
                {/* Na voljo */}
                <div style={{background:"#FFFFFF",border:"1px solid rgba(167,139,250,0.2)",borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:11,color:"#5A6472",letterSpacing:1,marginBottom:5}}>NA VOLJO</div>
                  <div style={{fontSize:15,fontWeight:700,color:skupajBud-skupajPor<0?"#EF4444":"#A78BFA"}}>{fmt(skupajBud-skupajPor)}</div>
                  {skupajBud>0&&<div style={{fontSize:11,color:"#5A6472",marginTop:3}}>{Math.round((skupajBud-skupajPor)/skupajBud*100)}% ostalo</div>}
                </div>
                {/* €/m² budget */}
                {cenaM2>0&&<div style={{background:"#FFFFFF",border:"1px solid rgba(196,181,253,0.2)",borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:11,color:"#5A6472",letterSpacing:1,marginBottom:5}}>BUDGET €/M²</div>
                  <div style={{fontSize:15,fontWeight:700,color:"#7C3AED"}}>{fmtN(cenaM2)} €</div>
                </div>}
                {/* €/m² porabljeno */}
                {porM2>0&&<div style={{background:"#FFFFFF",border:"1px solid rgba(110,231,183,0.2)",borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:11,color:"#5A6472",letterSpacing:1,marginBottom:5}}>PORABLJENO €/M²</div>
                  <div style={{fontSize:15,fontWeight:700,color:"#059669"}}>{fmtN(porM2)} €</div>
                </div>}
              </div>

              {/* ── Progress bar porabe ── */}
              {skupajBud>0&&(
                <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:10,padding:"14px 16px",marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <span style={{fontSize:11,fontWeight:600,color:"#1A1D24"}}>Poraba sredstev</span>
                    <span style={{fontSize:13,fontWeight:700,color:skupajPor>skupajBud?"#EF4444":skupajPor/skupajBud>0.8?"#F59E0B":"#10B981"}}>
                      {Math.min(Math.round(skupajPor/skupajBud*100),999)}%
                    </span>
                  </div>
                  <div style={{background:"rgba(0,0,0,0.064)",height:12,borderRadius:6,overflow:"hidden"}}>
                    <div style={{
                      width:Math.min(100,Math.round(skupajPor/skupajBud*100))+"%",
                      height:"100%",
                      background:skupajPor>skupajBud?"#EF4444":skupajPor/skupajBud>0.8?"#F59E0B":"#10B981",
                      borderRadius:6,
                      transition:"width 0.6s"
                    }}/>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",marginTop:6,fontSize:11,color:"#5A6472"}}>
                    <span>Porabljeno {fmt(skupajPor)}</span>
                    <span>Skupaj {fmt(skupajBud)}</span>
                  </div>
                </div>
              )}

              {/* ── Opozorilo poraba vs napredek ── */}
              {skupajBud>0&&skupajPor>0&&(()=>{
                const porPct = Math.round(skupajPor/skupajBud*100);

                // Ponderiran napredek — vsaka kategorija prispeva glede na svoj budget
                const ponderiraniNp = skupajBud > 0
                  ? Math.round(kats.reduce((sum, k) => {
                      const b = katBud(k);
                      const np = katNp(k);
                      return sum + (b * np / 100);
                    }, 0) / skupajBud * 100)
                  : 0;

                const npPct = ponderiraniNp;

                // Pričakovana poraba glede na ponderiran napredek
                const pricakovanoPorabljeno = Math.round(skupajBud * npPct / 100);
                const diffEurSkupaj = skupajPor - pricakovanoPorabljeno;
                const diffPct = porPct - npPct;

                // Največje odstopanje — ignoriraj male kategorije (< 5% skupnega budgeta)
                const minKatBud = skupajBud * 0.05;
                const katOdstopanja = kats.map(k=>{
                  const b=katBud(k), por=katPor(k), np=katNp(k);
                  if(b < minKatBud || por<=0) return null;
                  const diffEur = por - Math.round(b*np/100);
                  return {naziv:k.naziv, diffEur};
                }).filter(Boolean).filter(x=>x.diffEur>0).sort((a,b)=>b.diffEur-a.diffEur);
                const najvecje = katOdstopanja[0];

                // Pragovi relativni glede na skupni budget
                const eurWarn = skupajBud * 0.05;  // 5% budgeta
                const eurProb = skupajBud * 0.15;  // 15% budgeta

                const jeProb = diffPct > 15 || diffEurSkupaj > eurProb;
                const jeWarn = !jeProb && (diffPct > 5 || diffEurSkupaj > eurWarn);
                const jeOk = !jeProb && !jeWarn;

                const color  = jeOk ? "#10B981" : jeWarn ? "#F59E0B" : "#EF4444";
                const bg     = jeOk ? "rgba(52,211,153,0.08)" : jeWarn ? "rgba(245,158,11,0.08)" : "rgba(239,68,68,0.08)";
                const border = jeOk ? "rgba(52,211,153,0.25)" : jeWarn ? "rgba(245,158,11,0.25)" : "rgba(239,68,68,0.25)";
                const ikona  = <span style={{width:10,height:10,borderRadius:"50%",background:color,display:"inline-block",flexShrink:0}}/>;

                const naslov = jeOk
                  ? "V okviru tolerance"
                  : jeWarn
                  ? "Poraba prehiteva napredek"
                  : "Tveganje prekoračitve budgeta";

                const tolerancaMsg = jeWarn
                  ? diffPct <= 10
                    ? "Zmerno odstopanje — še v varnem območju."
                    : "Povišano odstopanje — priporočamo pregled."
                  : null;

                const opis = jeOk
                  ? diffPct > 0
                    ? `Poraba nekoliko odstopa (+${diffPct}%), vendar brez večjega vpliva.`
                    : `Poraba ${porPct}% je usklajena s ponderiranim napredkom ${npPct}%.`
                  : jeWarn
                  ? `Poraba ${porPct}% prehiteva ponderiran napredek ${npPct}% za +${diffPct}% (+${fmt(diffEurSkupaj)}).`
                  : `Poraba ${porPct}% močno prehiteva ponderiran napredek ${npPct}% za +${diffPct}% (+${fmt(diffEurSkupaj)}).`;

                return (
                  <div style={{background:bg,border:`1px solid ${border}`,borderRadius:10,padding:"14px 16px",marginBottom:12}}>
                    {/* Naslov — samo label */}
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                      <span style={{fontSize:15}}>{ikona}</span>
                      <span style={{fontSize:13,fontWeight:700,color}}>{naslov}</span>
                    </div>

                    {/* Razlaga s številkami */}
                    {!jeOk&&(
                      <div style={{fontSize:12,color:"#5A6472",lineHeight:1.7,marginBottom:8}}>
                        {jeWarn
                          ? <>Poraba <b style={{color:"#1A1D24"}}>{porPct}%</b> prehiteva ponderiran napredek <b style={{color:"#1A1D24"}}>{npPct}%</b> za <b style={{color}}> +{diffPct}% (+{fmt(diffEurSkupaj)})</b>.<br/><span style={{fontSize:11,fontStyle:"italic"}}>Ponderiran napredek upošteva budget vsake kategorije — ne le povprečje %.</span></>
                          : <>Poraba <b style={{color:"#1A1D24"}}>{porPct}%</b> močno prehiteva ponderiran napredek <b style={{color:"#1A1D24"}}>{npPct}%</b> za <b style={{color}}> +{diffPct}% (+{fmt(diffEurSkupaj)})</b>.<br/><span style={{fontSize:11,fontStyle:"italic"}}>Ponderiran napredek upošteva budget vsake kategorije — ne le povprečje %.</span></>
                        }
                      </div>
                    )}

                    {jeOk&&<div style={{fontSize:12,color:"#5A6472"}}>{opis}</div>}

                    {/* Toleranca label */}
                    {tolerancaMsg&&(
                      <div style={{fontSize:11,color:"#5A6472",fontStyle:"italic",marginBottom:8}}>{tolerancaMsg}</div>
                    )}

                    {/* Največje odstopanje */}
                    {!jeOk&&najvecje&&(
                      <div style={{fontSize:11,color:"#5A6472",marginBottom:10}}>
                        Največje odstopanje: <span style={{color,fontWeight:600}}>{najvecje.naziv} +{fmt(najvecje.diffEur)}</span>
                      </div>
                    )}

                    {/* CTA — samo pri rumeni */}
                    {jeWarn&&(
                      <div style={{borderTop:`1px solid ${border}`,paddingTop:10}}>
                        <button onClick={()=>setPogled("napredek")} style={{background:"rgba(245,158,11,0.15)",border:"1px solid rgba(245,158,11,0.3)",color:"#D97706",borderRadius:6,padding:"6px 14px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
                          Preveri napredek →
                        </button>
                        <span style={{fontSize:11,color:"#5A6472",marginLeft:10}}>Posodobi stanje projekta</span>
                      </div>
                    )}
                  </div>
                );
              })()}
              {(project.lastna>0||project.kredit>0)&&skupajBud>0&&(
                <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:10,padding:"12px 16px",marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#1A1D24",marginBottom:8}}>Struktura financiranja</div>
                  <div style={{height:10,borderRadius:5,display:"flex",overflow:"hidden",gap:1}}>
                    {project.lastna>0&&<div style={{flex:project.lastna,background:"#10B981"}}/>}
                    {project.kredit>0&&<div style={{flex:project.kredit,background:"#F59E0B"}}/>}
                  </div>
                  <div style={{display:"flex",gap:14,marginTop:6,fontSize:11,flexWrap:"wrap"}}>
                    {project.lastna>0&&<span style={{color:"#059669"}}>💚 Lastna: {fmt(project.lastna)} ({Math.round(project.lastna/skupajBud*100)}%)</span>}
                    {project.kredit>0&&<span style={{color:"#D97706"}}>🏦 Kredit: {fmt(project.kredit)} ({Math.round(project.kredit/skupajBud*100)}%)</span>}
                  </div>
                </div>
              )}
              {/* Category table - horizontally scrollable */}
              <div style={{overflowX:"auto"}}>
                <div style={{minWidth:580,background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:12,overflow:"hidden"}}>
                  {/* Header */}
                  <div style={{display:"grid",gridTemplateColumns:"minmax(140px,1fr) 80px 100px 100px 70px 70px 70px",padding:"8px 14px",borderBottom:"1px solid rgba(0,0,0,0.048)",fontSize:11,color:"#5A6472",letterSpacing:1,fontFamily:"'Syne Mono',monospace",gap:4}}>
                    <span>KATEGORIJA</span>
                    <span style={{textAlign:"right"}}>BUDGET</span>
                    <span style={{textAlign:"right"}}>PORABLJENO</span>
                    <span style={{textAlign:"right"}}>OSTALO</span>
                    <span style={{textAlign:"right"}}>% BUD.</span>
                    <span style={{textAlign:"right"}}>% DEJ.</span>
                    <span style={{textAlign:"right"}}>NAPRED.</span>
                  </div>
                  {/* Rows */}
                  {kats.map(k=>{
                    const b=katBud(k);const por=katPor(k);const np=katNp(k);
                    const pctBud = skupajBud>0 ? (b/skupajBud*100).toFixed(1) : "—";
                    const pctDej = skupajPor>0 ? (por/skupajPor*100).toFixed(1) : "—";
                    return (
                      <div key={k.id} className="hov-row" onClick={()=>{setAktivnaKat(k.id);setPogled("stroskovnik");}}
                        style={{display:"grid",gridTemplateColumns:"minmax(140px,1fr) 80px 100px 100px 70px 70px 70px",padding:"9px 14px",borderBottom:"1px solid rgba(0,0,0,0.032)",cursor:"pointer",alignItems:"center",gap:4}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,minWidth:0}}>
                          <span style={{fontSize:13,flexShrink:0}}>{k.ikona}</span>
                          <span style={{fontSize:11,color:"#1A1D24",fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={k.naziv}>{k.naziv}</span>
                        </div>
                        <span style={{textAlign:"right",fontSize:11,color:"#4B5563",whiteSpace:"nowrap"}}>{b>0?fmt(b):"—"}</span>
                        <span style={{textAlign:"right",fontSize:11,color:por>b&&b>0?"#DC2626":por>0?"#059669":"#9AA3B0",whiteSpace:"nowrap"}}>{por>0?fmt(por):"—"}</span>
                        <span style={{textAlign:"right",fontSize:11,color:b>por?"#5A6472":"#EF4444",whiteSpace:"nowrap"}}>{b>0?fmt(b-por):"—"}</span>
                        <span style={{textAlign:"right",fontSize:11,color:"#3B82F6",fontFamily:"'Syne Mono',monospace",fontWeight:600}}>{pctBud!=="—"?pctBud+"%":"—"}</span>
                        <span style={{textAlign:"right",fontSize:11,color:"#F59E0B",fontFamily:"'Syne Mono',monospace",fontWeight:600}}>{pctDej!=="—"?pctDej+"%":"—"}</span>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:4}}>
                          <div style={{width:32,background:"rgba(0,0,0,0.056)",height:4,borderRadius:2,flexShrink:0}}>
                            <div style={{width:np+"%",height:"100%",background:np===100?"#10B981":k.barva,borderRadius:2}}/>
                          </div>
                          <span style={{fontSize:11,fontWeight:700,color:np===100?"#10B981":k.barva,minWidth:24,textAlign:"right"}}>{np}%</span>
                        </div>
                      </div>
                    );
                  })}
                  {/* Footer */}
                  <div style={{display:"grid",gridTemplateColumns:"minmax(140px,1fr) 80px 100px 100px 70px 70px 70px",padding:"10px 14px",background:"rgba(59,130,246,0.06)",borderTop:"1px solid rgba(59,130,246,0.2)",fontSize:11,fontWeight:700,gap:4,alignItems:"center"}}>
                    <span style={{color:"#5A6472"}}>SKUPAJ</span>
                    <span style={{textAlign:"right",color:"#1A1D24",whiteSpace:"nowrap"}}>{fmt(skupajBud)}</span>
                    <span style={{textAlign:"right",color:skupajPor>skupajBud?"#DC2626":"#059669",whiteSpace:"nowrap"}}>{fmt(skupajPor)}</span>
                    <span style={{textAlign:"right",color:"#5A6472",whiteSpace:"nowrap"}}>{fmt(skupajBud-skupajPor)}</span>
                    <span style={{textAlign:"right",color:"#3B82F6",fontSize:11,fontFamily:"'Syne Mono',monospace"}}>100%</span>
                    <span style={{textAlign:"right",color:"#F59E0B",fontSize:11,fontFamily:"'Syne Mono',monospace"}}>100%</span>
                    <span style={{textAlign:"right",color:"#3B82F6"}}>{skupniNp}%</span>
                  </div>
                </div>
              </div>
              {/* Legend */}
              <div style={{display:"flex",gap:14,marginTop:10,fontSize:11,color:"#5A6472"}}>
                <span><span style={{color:"#3B82F6"}}>% Bud.</span> = delež od skupnega budgeta</span>
                <span><span style={{color:"#F59E0B"}}>% Dej.</span> = delež od skupno porabljenih stroškov</span>
              </div>
            </div>
            );
          })()}

          {/* ── DOKUMENTI ── */}
          {pogled==="dokumenti"&&(
            <DocsView
              docs={docs}
              activeDokKat={activeDokKat}
              setActiveDokKat={setActiveDokKat}
              dragOver={dragOver}
              setDragOver={setDragOver}
              handleFiles={handleFiles}
              removeDoc={removeDoc}
              updateDoc={updateDoc}
              fileInputRef={docInputRef}
              project={project}
              contacts={project.contacts||[]}
            />
          )}

          {/* ── KONTAKTI ── */}
          {pogled==="analiza"&&(
            <AnalizaView project={project} kats={kats} onKlikniFazo={(katId)=>{setPogled("stroskovnik");setAktivnaKat(katId);}}/>
          )}

          {pogled==="terminski"&&(
            <TerminskiPlan project={project} onUpdate={upd} kats={kats}/>
          )}

          {pogled==="foto"&&(
            <FotoView project={project} userId={userId}/>
          )}
          {pogled==="kontakti"&&(
            <KontaktiView
              contacts={project.contacts||[]}
              onUpdate={updated=>upd(()=>({contacts:updated}))}
            />
          )}
        </main>
      </div>

      {/* bottom nav — dostopno s palcem na telefonu, boljši kontrast */}
      <div className="bottom-nav-wrap">
        <nav className="bottom-nav" style={{background:"#FFFFFF",borderTop:"1px solid rgba(0,0,0,0.072)",overflowX:"auto",display:"flex",paddingBottom:"env(safe-area-inset-bottom)"}}>
        <div style={{display:"flex",gap:4,padding:"7px 10px",margin:"0 auto",minWidth:"max-content"}}>
          {[["stroskovnik",Wallet,"Stroškovnik"],["napredek",BarChart3,"Napredek"],["pregled",LayoutDashboard,"Pregled"],["terminski",CalendarDays,"Terminski"],["analiza",PieChart,"Analiza"],["dokumenti",FolderOpen,`Dokumenti${totalDocs>0?` (${totalDocs})`:""}`],["kontakti",Users,"Kontakti"],["foto",Camera,"Foto"]].map(([k,Ico,l])=>(
            <button key={k} onClick={()=>{setPogled(k);setAktivnaKat(null);}} className="T" style={{
              background:pogled===k?"#3B82F6":"transparent",
              color:pogled===k?"#1A1D24":"#4B5563",
              border:"none",
              borderRadius:11,padding:"7px 13px",fontSize:12,fontFamily:"inherit",
              fontWeight:pogled===k?700:500,cursor:"pointer",
              display:"flex",flexDirection:"column",alignItems:"center",gap:3,whiteSpace:"nowrap",minWidth:66,flexShrink:0,
              transition:"all 0.15s"
            }}><Ico size={18} strokeWidth={pogled===k?2.2:1.8}/>{l}</button>
          ))}
        </div>
      </nav>
      </div>
    </div>
  );
}

// Originalni nazivi kategorij — te ne moremo brisati iz terminskega plana
const ORIGINALNI_NAZIVI = new Set([
  "Parcela & Komunalna ureditev","Dokumentacija & Projektiranje","Nadzor & Vodenje",
  "Gradbena dela","Streha & Kritina","Fasada & Toplotna izolacija","Stavbno pohištvo",
  "Vodovodne inštalacije","Ogrevanje & Prezračevanje","Elektro inštalacije",
  "Sanitarna oprema","Keramika","Suhomontažna dela","Slikopleskarska dela",
  "Parket & Talne obloge","Pohištvo & Oprema","Zunanja ureditev","🟡 Nerazporejen budget",
  "Nakup nepremičnine","Pregled & Ocena stanja","Projektna dokumentacija",
  "Rušitvena & pripravljalna dela","Gradbena & konstruktivna dela",
  "Vodovodne & kanalizacijske inštalacije","Keramika & Obloge",
  "Okna & Vrata (stavbno pohištvo)","Slikopleskarska dela",
]);

// ─── Analiza ──────────────────────────────────────────────────────────────────
function AnalizaView({ project, kats, onKlikniFazo }) {
  const [pogled, setPogled] = useState("budget"); // "budget" | "porabljeno"

  const skupajBud = kats.reduce((a,k)=>a+katBud(k),0);
  const skupajPor = kats.reduce((a,k)=>a+katPor(k),0);
  const skupaj = pogled==="budget" ? skupajBud : skupajPor;

  // Filtriraj kategorije z vrednostjo > 0
  const aktivne = kats.map(k=>({
    naziv: k.naziv,
    ikona: k.ikona,
    barva: k.barva||"#9AA3B0",
    katId: k.id,
    vrednost: pogled==="budget" ? katBud(k) : katPor(k),
  })).filter(k=>k.vrednost>0).sort((a,b)=>b.vrednost-a.vrednost).map(k=>({
    ...k,
    delež: skupaj>0 ? k.vrednost/skupaj : 0,
  }));

  // SVG tortni graf
  const r = 120;
  const cx = 160;
  const cy = 160;
  let kot = -Math.PI/2;

  const segmenti = aktivne.map(k => {
    const start = kot;
    kot += k.delež * 2 * Math.PI;
    const end = kot;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const large = k.delež > 0.5 ? 1 : 0;
    return { ...k, path:`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z` };
  });

  const [hovered, setHovered] = useState(null);

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:700,color:"#1A1D24"}}>Analiza investicije</h2>
          <p style={{fontSize:13,color:"#5A6472",marginTop:3}}>Razdelitev stroškov po kategorijah</p>
        </div>
        <div style={{display:"flex",background:"rgba(0,0,0,0.04)",borderRadius:8,padding:3,gap:2}}>
          {[["budget","Po budgetu"],["porabljeno","Po porabi"]].map(([k,l])=>(
            <button key={k} onClick={()=>setPogled(k)}
              style={{padding:"5px 14px",borderRadius:6,border:"none",background:pogled===k?"rgba(59,130,246,0.2)":"transparent",color:pogled===k?"#2563EB":"#9AA3B0",fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:pogled===k?600:400}}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {skupaj===0&&<div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.25)",borderRadius:10,padding:"14px 16px",fontSize:13,color:"#D97706"}}>
        Ni podatkov za prikaz. Vnesite budget ali porabljene zneske v Stroškovniku.
      </div>}

      {skupaj>0&&(
        <div style={{display:"grid",gridTemplateColumns:"minmax(280px,320px) 1fr",gap:20,alignItems:"start"}}>
          {/* Tortni graf */}
          <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:12,padding:20,display:"flex",flexDirection:"column",alignItems:"center"}}>
            <svg width="320" height="320" viewBox="0 0 320 320">
              {segmenti.map((s,i)=>(
                <path key={i} d={s.path} fill={s.barva}
                  opacity={hovered===null||hovered===i?0.9:0.4}
                  stroke="#FFFFFF" strokeWidth="2"
                  style={{cursor:"pointer",transition:"opacity 0.2s"}}
                  onMouseEnter={()=>setHovered(i)}
                  onMouseLeave={()=>setHovered(null)}
                  onClick={()=>onKlikniFazo&&onKlikniFazo(s.katId)}/>
              ))}
              {/* Center text */}
              <text x={cx} y={cy-12} textAnchor="middle" fill="#1A1D24" fontSize="14" fontWeight="700">
                {hovered!==null ? aktivne[hovered]?.ikona : "💰"}
              </text>
              <text x={cx} y={cy+8} textAnchor="middle" fill="#1A1D24" fontSize="13" fontWeight="700">
                {hovered!==null ? `${(aktivne[hovered]?.delež*100).toFixed(1)}%` : "Skupaj"}
              </text>
              <text x={cx} y={cy+26} textAnchor="middle" fill="#5A6472" fontSize="11">
                {hovered!==null ? `${Number(aktivne[hovered]?.vrednost).toLocaleString("sl-SI")} €` : `${Number(skupaj).toLocaleString("sl-SI")} €`}
              </text>
            </svg>
          </div>

          {/* Seznam */}
          <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:12,overflow:"hidden"}}>
            <div style={{padding:"10px 16px",borderBottom:"1px solid rgba(0,0,0,0.056)",fontSize:11,color:"#5A6472",letterSpacing:1,display:"grid",gridTemplateColumns:"1fr 80px 80px 60px"}}>
              <span>KATEGORIJA</span><span style={{textAlign:"right"}}>ZNESEK</span><span style={{textAlign:"right"}}>DELEŽ</span><span style={{textAlign:"right"}}>% OD SKU.</span>
            </div>
            {aktivne.map((k,i)=>(
              <div key={i} className="hov-row"
                style={{padding:"8px 16px",borderBottom:"1px solid rgba(0,0,0,0.032)",display:"grid",gridTemplateColumns:"1fr 80px 80px 60px",alignItems:"center",background:hovered===i?"rgba(0,0,0,0.024)":"transparent",cursor:"pointer"}}
                onMouseEnter={()=>setHovered(i)} onMouseLeave={()=>setHovered(null)}
                onClick={()=>onKlikniFazo&&onKlikniFazo(k.katId)}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{display:"inline-block",width:10,height:10,borderRadius:"50%",background:k.barva,flexShrink:0}}/>
                  <span style={{fontSize:11,color:"#1A1D24",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{k.ikona} {k.naziv}</span>
                </div>
                <span style={{fontSize:12,color:"#1A1D24",textAlign:"right",fontFamily:"monospace"}}>{Number(k.vrednost).toLocaleString("sl-SI")} €</span>
                <div style={{paddingLeft:8}}>
                  <div style={{background:"rgba(0,0,0,0.064)",height:6,borderRadius:3,overflow:"hidden"}}>
                    <div style={{width:`${k.delež*100}%`,height:"100%",background:k.barva,borderRadius:3}}/>
                  </div>
                </div>
                <span style={{fontSize:12,color:k.barva,textAlign:"right",fontWeight:600}}>{(k.delež*100).toFixed(1)}%</span>
              </div>
            ))}
            <div style={{padding:"10px 16px",borderTop:"1px solid rgba(0,0,0,0.08)",display:"grid",gridTemplateColumns:"1fr 80px 80px 60px",alignItems:"center"}}>
              <span style={{fontSize:11,fontWeight:700,color:"#1A1D24"}}>SKUPAJ</span>
              <span style={{fontSize:12,fontWeight:700,color:"#1A1D24",textAlign:"right",fontFamily:"monospace"}}>{Number(skupaj).toLocaleString("sl-SI")} €</span>
              <span/>
              <span style={{fontSize:12,fontWeight:700,color:"#1A1D24",textAlign:"right"}}>100%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Terminski plan ───────────────────────────────────────────────────────────
const ODVISNOSTI = {
  "Parcela & Komunalna ureditev":  [],
  "Dokumentacija & Projektiranje": [{ od: "Parcela & Komunalna ureditev", pct: 50 }],
  "Nadzor & Vodenje":              [{ od: "Dokumentacija & Projektiranje", pct: 100 }],
  "Gradbena dela":                 [{ od: "Dokumentacija & Projektiranje", pct: 100 }],
  "Streha & Kritina":              [{ od: "Gradbena dela", pct: 90 }],
  "Elektro inštalacije":           [{ od: "Gradbena dela", pct: 30 }],  // priprava cevi zgodaj
  "Vodovodne inštalacije":         [{ od: "Streha & Kritina", pct: 80 }],
  "Ogrevanje & Prezračevanje":     [{ od: "Vodovodne inštalacije", pct: 50 }],
  "Stavbno pohištvo":              [{ od: "Streha & Kritina", pct: 100 }],
  "Suhomontažna dela":             [{ od: "Stavbno pohištvo", pct: 80 }, { od: "Elektro inštalacije", pct: 80 }],
  "Keramika":                      [{ od: "Suhomontažna dela", pct: 60 }],
  "Sanitarna oprema":              [{ od: "Keramika", pct: 80 }],
  "Slikopleskarska dela":          [{ od: "Suhomontažna dela", pct: 90 }],
  "Parket & Talne obloge":         [{ od: "Slikopleskarska dela", pct: 50 }],
  "Pohištvo & Oprema":             [{ od: "Parket & Talne obloge", pct: 80 }],
  "Fasada & Toplotna izolacija":   [{ od: "Streha & Kritina", pct: 100 }, { od: "Stavbno pohištvo", pct: 80 }],
  "Zunanja ureditev":              [{ od: "Fasada & Toplotna izolacija", pct: 80 }],
  "🟡 Nerazporejen budget":        [],
};

function TerminskiPlan({ project, onUpdate, kats }) {
  const [zacetek, setZacetek] = useState(project.terminZacetek || "");
  const [editFaza, setEditFaza] = useState(null);
  const [dodajFazoPoId, setDodajFazoPoId] = useState(null);
  const [confirmDelFaza, setConfirmDelFaza] = useState(null);
  const [skrite, setSkrite] = useState(new Set());
  const [collapsed, setCollapsed] = useState(new Set());
  const [zoom, setZoom] = useState("meseci");
  const [tooltip, setTooltip] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [novaFazaNaziv, setNovaFazaNaziv] = useState("");
  const [novaFazaTrajanje, setNovaFazaTrajanje] = useState(28);
  const [novaFazaEnota, setNovaFazaEnota] = useState("dnevi");
  const [novaFazaZacetek, setNovaFazaZacetek] = useState("");

  // terminKats — ločen seznam faz, inicializiran iz stroškovnika
  // Hranimo v project.terminKats, neodvisno od project.kats
  const getTerminKats = () => {
    if (project.terminKats && project.terminKats.length > 0) return project.terminKats;
    return kats.map(k => ({ id: k.id, naziv: k.naziv, ikona: k.ikona, barva: k.barva, izStroskovnika: true }));
  };
  const terminKats = getTerminKats();

  const saveZacetek = (val) => {
    setZacetek(val);
    if (val && (!project.terminKats || project.terminKats.length === 0)) {
      // Prvič — kopiraj iz stroškovnika
      onUpdate(p => ({
        terminZacetek: val,
        terminKats: kats.map(k => ({ id: k.id, naziv: k.naziv, ikona: k.ikona, barva: k.barva, izStroskovnika: true }))
      }));
    } else {
      onUpdate(() => ({ terminZacetek: val }));
    }
  };

  const posodobiIzStroskovnika = () => {
    onUpdate(p => {
      const obstojeciIds = new Set((p.terminKats||[]).map(k => k.id));
      const nove = kats.filter(k => !obstojeciIds.has(k.id))
        .map(k => ({ id: k.id, naziv: k.naziv, ikona: k.ikona, barva: k.barva, izStroskovnika: true }));
      return { terminKats: [...(p.terminKats||[]), ...nove] };
    });
  };

  const moveTerminKat = (kid, smer) => {
    onUpdate(p => {
      const ks = [...(p.terminKats||getTerminKats())];
      const i = ks.findIndex(k => k.id === kid);
      if (i < 0) return {};
      if (smer === "gor" && i === 0) return {};
      if (smer === "dol" && i === ks.length - 1) return {};
      const j = smer === "gor" ? i - 1 : i + 1;
      [ks[i], ks[j]] = [ks[j], ks[i]];
      return { terminKats: ks };
    });
  };

  const terminFaze = project.terminFaze || {};

  const izracunajDate = () => {
    if (!zacetek) return {};
    const rezultati = {};
    const obdelane = new Set();
    const izracunaj = (naziv) => {
      if (obdelane.has(naziv)) return rezultati[naziv];
      const k = terminKats.find(k => k.naziv === naziv);
      if (!k) return null;
      const faza = terminFaze[k.id] || {};
      const trajanje = faza.trajanje || 28;
      const odv = ODVISNOSTI[naziv] || [];
      let najZgodnejsiZacetek = new Date(zacetek);
      for (const o of odv) {
        const odvisnaFaza = izracunaj(o.od);
        if (odvisnaFaza) {
          const odvisniKonec = new Date(odvisnaFaza.konec);
          if (odvisniKonec > najZgodnejsiZacetek) najZgodnejsiZacetek = new Date(odvisniKonec);
        }
      }
      const rocniZacetek = faza.zacetek ? new Date(faza.zacetek) : null;
      const dejanskiZacetek = rocniZacetek ? rocniZacetek : najZgodnejsiZacetek;
      const konec = new Date(dejanskiZacetek);
      // Če je datum konca določen direktno, ga uporabimo
      if (faza.konecDatum) {
        const konecD = new Date(faza.konecDatum);
        if (konecD > dejanskiZacetek) {
          const dejTrajanje = Math.round((konecD - dejanskiZacetek)/(1000*60*60*24));
          rezultati[naziv] = { zacetek: dejanskiZacetek, konec: konecD, trajanje: dejTrajanje };
          obdelane.add(naziv);
          return rezultati[naziv];
        }
      }
      konec.setDate(konec.getDate() + trajanje);
      rezultati[naziv] = { zacetek: dejanskiZacetek, konec, trajanje };
      obdelane.add(naziv);
      return rezultati[naziv];
    };
    terminKats.forEach(k => izracunaj(k.naziv));
    return rezultati;
  };

  const datumi = izracunajDate();
  const vsiDatumi = Object.values(datumi);
  const minDate = vsiDatumi.length ? new Date(Math.min(...vsiDatumi.map(d => d.zacetek))) : new Date();
  const maxDate = vsiDatumi.length ? new Date(Math.max(...vsiDatumi.map(d => d.konec))) : new Date();
  const danes = new Date();
  const danesPct = Math.min(100, Math.max(0, (danes - minDate) / (maxDate - minDate) * 100));
  const fmtDatum = d => d ? new Date(d).toLocaleDateString("sl-SI", { day:"2-digit", month:"2-digit", year:"numeric" }) : "—";

  const genZoomHeader = () => {
    const headers = [];
    const cur = new Date(minDate);
    if (zoom === "tedni") {
      while (cur <= maxDate) { headers.push({ label: `T${Math.ceil((cur - minDate) / (7*24*3600*1000))+1}`, date: new Date(cur) }); cur.setDate(cur.getDate() + 7); }
    } else if (zoom === "meseci") {
      cur.setDate(1);
      while (cur <= maxDate) { headers.push({ label: cur.toLocaleDateString("sl-SI",{month:"short",year:"2-digit"}), date: new Date(cur) }); cur.setMonth(cur.getMonth() + 1); }
    } else {
      cur.setDate(1); cur.setMonth(Math.floor(cur.getMonth()/3)*3);
      while (cur <= maxDate) { headers.push({ label: `Q${Math.floor(cur.getMonth()/3)+1} ${cur.getFullYear()}`, date: new Date(cur) }); cur.setMonth(cur.getMonth() + 3); }
    }
    return headers;
  };

  const zoomHeaders = zacetek ? genZoomHeader() : [];
  const vidneKats = terminKats.filter(k => !skrite.has(k.id));
  const skriteFaze = terminKats.filter(k => skrite.has(k.id));

  const saveFaza = (katId, data) => {
    onUpdate(p => ({ terminFaze: { ...(p.terminFaze||{}), [katId]: { ...(p.terminFaze?.[katId]||{}), ...data } } }));
    setEditFaza(null);
  };

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:700,color:"#1A1D24"}}>Terminski plan</h2>
          <p style={{fontSize:13,color:"#5A6472",marginTop:3}}>Usklajevanje z mojstri po fazah gradnje</p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          {skriteFaze.length>0&&<button onClick={()=>setSkrite(new Set())}
            style={{background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",color:"#D97706",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
            Prikaži vse ({skriteFaze.length})
          </button>}
          {zacetek&&<button onClick={posodobiIzStroskovnika}
            style={{background:"rgba(0,0,0,0.04)",border:"1px solid rgba(0,0,0,0.08)",color:"#5A6472",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
            Posodobi kategorije iz stroškovnika
          </button>}
          {zacetek&&<div style={{display:"flex",background:"rgba(0,0,0,0.04)",borderRadius:8,padding:3,gap:2}}>
            {[["tedni","Tedni"],["meseci","Meseci"],["kvartali","Kvartali"]].map(([k,l])=>(
              <button key={k} onClick={()=>setZoom(k)}
                style={{padding:"5px 10px",borderRadius:6,border:"none",background:zoom===k?"rgba(59,130,246,0.2)":"transparent",color:zoom===k?"#2563EB":"#9AA3B0",fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:zoom===k?600:400}}>
                {l}
              </button>
            ))}
          </div>}
        </div>
      </div>

      {/* Začetek projekta */}
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:10,padding:"14px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <span style={{fontSize:13,color:"#5A6472",whiteSpace:"nowrap"}}>📅 Začetek projekta:</span>
        <input type="date" value={zacetek} onChange={e=>saveZacetek(e.target.value)}
          style={{background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.14)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"6px 10px"}}/>
        {zacetek&&<span style={{fontSize:12,color:"#5A6472"}}>Predviden zaključek: <b style={{color:"#1A1D24"}}>{fmtDatum(maxDate)}</b></span>}
      </div>

      {!zacetek&&<div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.25)",borderRadius:10,padding:"14px 16px",marginBottom:16,fontSize:13,color:"#D97706"}}>
        Vnesite datum začetka projekta za prikaz terminskega plana.
      </div>}

      {zacetek&&(<>
        {/* Gantt */}
        <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:10,overflow:"auto",marginBottom:16}}>
          <div style={{minWidth: 32 + 180 + 96 + zoomHeaders.length * (zoom==="tedni"?50:zoom==="meseci"?70:100)}}>
            {/* Header */}
            <div style={{display:"flex",borderBottom:"1px solid rgba(0,0,0,0.056)",position:"sticky",top:0,background:"#FFFFFF",zIndex:10}}>
              <div style={{width:32,flexShrink:0}}/>
              <div style={{width:180,flexShrink:0,padding:"8px 10px",fontSize:11,color:"#5A6472",letterSpacing:1,borderRight:"1px solid rgba(0,0,0,0.056)"}}>FAZA</div>
              <div style={{flex:1,display:"flex",position:"relative"}}>
                {zoomHeaders.map((h,i)=>(
                  <div key={i} style={{width:zoom==="tedni"?50:zoom==="meseci"?70:100,flexShrink:0,padding:"8px 4px",fontSize:11,textAlign:"center",borderLeft:i>0?"1px solid rgba(0,0,0,0.032)":"none",color:h.date.getMonth()===danes.getMonth()&&h.date.getFullYear()===danes.getFullYear()?"#3B82F6":"#5A6472",background:h.date.getMonth()===danes.getMonth()&&h.date.getFullYear()===danes.getFullYear()?"rgba(59,130,246,0.06)":"transparent",fontWeight:600}}>
                    {h.label}
                  </div>
                ))}
                <div style={{position:"absolute",left:`${danesPct}%`,top:0,bottom:0,width:2,background:"rgba(239,68,68,0.6)",zIndex:5,pointerEvents:"none"}}/>
              </div>
              <div style={{width:96,flexShrink:0,fontSize:11,color:"#5A6472",padding:"8px 4px",textAlign:"center"}}>AKCIJE</div>
            </div>

            {/* Vrstice */}
            {vidneKats.map((k, i) => {
              const d = datumi[k.naziv];
              const faza = terminFaze[k.id] || {};
              const np = project.kats.find(x=>x.id===k.id) ? katNp(project.kats.find(x=>x.id===k.id)) : (faza.status==="zakljuceno" ? 100 : faza.napredek||0);
              if (!d) return null;
              const startPct = Math.max(0, (d.zacetek - minDate) / (maxDate - minDate) * 100);
              const widthPct = Math.max(0.5, (d.konec - d.zacetek) / (maxDate - minDate) * 100);
              const jeZamuda = danes > d.konec && np < 100 && faza.status !== "zakljuceno" && faza.status !== "odpovedano";
              const barColor = faza.status==="zakljuceno"?"#10B981":faza.status==="odpovedano"?"#EF4444":faza.status==="cakanje"?"#F59E0B":faza.status==="v_teku"?(k.barva||"#3B82F6"):jeZamuda?"#EF4444":"#9AA3B0";

              return (
                <div key={k.id} style={{display:"flex",borderBottom:"1px solid rgba(0,0,0,0.032)",background:i%2===0?"transparent":"rgba(0,0,0,0.008)",alignItems:"center"}}>
                  {/* Puščice */}
                  <div style={{width:32,flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:1,padding:"3px 2px",borderRight:"1px solid rgba(0,0,0,0.04)"}}>
                    <button onClick={()=>moveTerminKat(k.id,"gor")} disabled={i===0}
                      style={{background:"transparent",border:"none",color:i===0?"#C2C8D0":"#9AA3B0",cursor:i===0?"default":"pointer",lineHeight:0,padding:0,display:"flex"}}><ChevronUp size={14}/></button>
                    <button onClick={()=>moveTerminKat(k.id,"dol")} disabled={i===vidneKats.length-1}
                      style={{background:"transparent",border:"none",color:i===vidneKats.length-1?"#C2C8D0":"#9AA3B0",cursor:i===vidneKats.length-1?"default":"pointer",lineHeight:0,padding:0,display:"flex"}}><ChevronDown size={14}/></button>
                  </div>
                  {/* Naziv */}
                  <div style={{width:180,flexShrink:0,padding:"7px 10px",display:"flex",alignItems:"center",gap:6,borderRight:"1px solid rgba(0,0,0,0.04)"}}>
                    <span style={{fontSize:12,flexShrink:0}}>{k.ikona}</span>
                    {renameId===k.id ? (
                      <input autoFocus value={renameVal} onChange={e=>setRenameVal(e.target.value)}
                        onKeyDown={e=>{
                          if(e.key==="Enter"){ if(renameVal.trim()) onUpdate(p=>({terminKats:(p.terminKats||[]).map(x=>x.id===k.id?{...x,naziv:renameVal.trim()}:x)})); setRenameId(null); }
                          if(e.key==="Escape") setRenameId(null);
                        }}
                        onBlur={()=>{ if(renameVal.trim()) onUpdate(p=>({terminKats:(p.terminKats||[]).map(x=>x.id===k.id?{...x,naziv:renameVal.trim()}:x)})); setRenameId(null); }}
                        style={{flex:1,background:"rgba(0,0,0,0.08)",border:"1px solid #3B82F6",borderRadius:4,color:"#1A1D24",fontFamily:"inherit",fontSize:11,padding:"2px 6px",outline:"none"}}/>
                    ) : (
                      <span style={{fontSize:11,color:"#1A1D24",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1,cursor:!ORIGINALNI_NAZIVI.has(k.naziv)?"text":"default"}}
                        onDoubleClick={()=>{if(!ORIGINALNI_NAZIVI.has(k.naziv)){setRenameId(k.id);setRenameVal(k.naziv);}}}
                        title={!ORIGINALNI_NAZIVI.has(k.naziv)?"Dvojni klik za preimenovanje":""}>
                        {k.naziv}
                      </span>
                    )}
                  </div>
                  {/* Bar */}
                  <div style={{flex:1,height:32,position:"relative",cursor:"pointer"}} onMouseLeave={()=>setTooltip(null)}>
                    <div style={{position:"absolute",left:`${danesPct}%`,top:0,bottom:0,width:2,background:"rgba(239,68,68,0.4)",zIndex:3,pointerEvents:"none"}}/>
                    <div style={{position:"absolute",top:"50%",transform:"translateY(-50%)",left:`${startPct}%`,width:`${widthPct}%`,height:20,background:barColor,borderRadius:4,opacity:0.85,minWidth:4,zIndex:2,overflow:"hidden",cursor:"pointer"}}
                      onClick={()=>setEditFaza(k.id)}
                      onMouseEnter={(e)=>{const rect=e.currentTarget.getBoundingClientRect();setTooltip({x:rect.left,y:rect.top,k,d,np,jeZamuda,opomba:faza.opomba||"",status:faza.status||"nacrtovano"});}}>
                      {np>0&&<div style={{width:`${np}%`,height:"100%",background:"rgba(0,0,0,0.14)",borderRadius:4}}/>}
                    </div>
                  </div>
                  {/* Akcije */}
                  <div style={{width:96,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"flex-end",gap:5,padding:"0 8px",borderLeft:"1px solid rgba(0,0,0,0.04)"}} onClick={e=>e.stopPropagation()}>
                    <button onClick={()=>setSkrite(prev=>{const n=new Set(prev);n.has(k.id)?n.delete(k.id):n.add(k.id);return n;})}
                      style={{background:skrite.has(k.id)?"rgba(59,130,246,0.15)":"transparent",border:`1px solid ${skrite.has(k.id)?"rgba(59,130,246,0.4)":"rgba(0,0,0,0.08)"}`,color:skrite.has(k.id)?"#3B82F6":"#9AA3B0",borderRadius:4,fontSize:11,cursor:"pointer",padding:"2px 6px",fontWeight:600,whiteSpace:"nowrap"}}>
                      {skrite.has(k.id)?"Pokaži":"Skrij"}
                    </button>
                    <button onClick={()=>setDodajFazoPoId(k.id)}
                      style={{background:"transparent",border:"1px solid rgba(0,0,0,0.08)",color:"#9AA3B0",borderRadius:4,fontSize:11,cursor:"pointer",padding:"2px 6px",fontWeight:600,whiteSpace:"nowrap"}}>
                      Dodaj
                    </button>
                    {!ORIGINALNI_NAZIVI.has(k.naziv)&&(
                      <button onClick={()=>setConfirmDelFaza(k.id)}
                        style={{background:"transparent",border:"1px solid rgba(239,68,68,0.25)",color:"#DC2626",borderRadius:4,fontSize:11,cursor:"pointer",padding:"2px 5px"}}>✕</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legenda */}
        <div style={{display:"flex",gap:12,flexWrap:"wrap",fontSize:11,color:"#5A6472",marginBottom:16}}>
          {[["#10B981","Zaključeno"],["#3B82F6","V teku"],["#F59E0B","Čakanje"],["#9AA3B0","Načrtovano"],["#EF4444","Zamuda/Odpovedano"]].map(([c,l])=>(
            <span key={l} style={{display:"flex",alignItems:"center",gap:4}}><span style={{display:"inline-block",width:14,height:10,background:c,borderRadius:2}}/>{l}</span>
          ))}
          <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{display:"inline-block",width:2,height:10,background:"rgba(239,68,68,0.7)"}}/> Danes</span>
        </div>

        {/* Tabela */}
        <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:10,overflow:"hidden",marginBottom:16}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 110px 110px 70px 70px 100px",padding:"8px 14px",borderBottom:"1px solid rgba(0,0,0,0.056)",fontSize:11,color:"#5A6472",letterSpacing:1}}>
            <span>FAZA</span><span>ZAČETEK</span><span>KONEC</span><span>TRAJANJE</span><span>NAPREDEK</span><span>STATUS</span>
          </div>
          {terminKats.map(k => {
            const d = datumi[k.naziv];
            const faza = terminFaze[k.id] || {};
            const np = project.kats.find(x=>x.id===k.id) ? katNp(project.kats.find(x=>x.id===k.id)) : (faza.status==="zakljuceno" ? 100 : faza.napredek||0);
            const jeZamuda = d && danes > d.konec && np < 100;
            if (!d) return null;
            return (
              <div key={k.id} className="hov-row" style={{display:"grid",gridTemplateColumns:"1fr 110px 110px 70px 70px 100px",padding:"8px 14px",borderBottom:"1px solid rgba(0,0,0,0.032)",alignItems:"center",cursor:"pointer"}}
                onClick={()=>setEditFaza(k.id)}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span>{k.ikona}</span>
                  <div>
                    <div style={{fontSize:12,color:"#1A1D24"}}>{k.naziv}</div>
                    {(ODVISNOSTI[k.naziv]||[]).length>0&&<div style={{fontSize:11,color:"#9AA3B0"}}>čaka: {ODVISNOSTI[k.naziv].map(o=>o.od.split(" ")[0]).join(", ")}</div>}
                  </div>
                </div>
                <span style={{fontSize:11,color:"#5A6472"}}>{fmtDatum(d.zacetek)}</span>
                <span style={{fontSize:11,color:"#5A6472"}}>{fmtDatum(d.konec)}</span>
                <span style={{fontSize:11,color:"#1A1D24"}}>{faza.trajanje||28} dni</span>
                <span style={{fontSize:11,color:np===100?"#10B981":np>0?"#3B82F6":"#9AA3B0"}}>{np}%</span>
                <span style={{fontSize:11,color:faza.status==="zakljuceno"?"#10B981":jeZamuda?"#EF4444":faza.status==="v_teku"?"#3B82F6":faza.status==="cakanje"?"#F59E0B":faza.status==="odpovedano"?"#EF4444":"#5A6472",fontWeight:600}}>
                  {faza.status==="zakljuceno"?"Zaključeno":faza.status==="v_teku"?"V teku":faza.status==="cakanje"?"Čakanje":faza.status==="odpovedano"?"Odpovedano":jeZamuda?"Zamuda":"Načrtovano"}
                </span>
              </div>
            );
          })}
        </div>

        {/* Mesečni koledar */}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:14,fontWeight:600,color:"#1A1D24",marginBottom:12}}>📅 Mesečni pregled</div>
          {(()=>{
            const meseci = [];
            const cur = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
            const end = new Date(maxDate.getFullYear(), maxDate.getMonth()+1, 1);
            while (cur < end) { meseci.push(new Date(cur)); cur.setMonth(cur.getMonth()+1); }
            return (
              <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.056)",borderRadius:10,overflow:"auto"}}>
                <div style={{minWidth:Math.max(600, meseci.length*70+180)}}>
                  <div style={{display:"flex",borderBottom:"1px solid rgba(0,0,0,0.056)"}}>
                    <div style={{width:180,flexShrink:0,padding:"8px 14px",fontSize:11,color:"#5A6472",letterSpacing:1}}>FAZA</div>
                    {meseci.map((m,i)=>(
                      <div key={i} style={{width:70,flexShrink:0,padding:"8px 4px",fontSize:11,textAlign:"center",borderLeft:"1px solid rgba(0,0,0,0.032)",color:m.getMonth()===danes.getMonth()&&m.getFullYear()===danes.getFullYear()?"#3B82F6":"#5A6472",fontWeight:m.getMonth()===danes.getMonth()&&m.getFullYear()===danes.getFullYear()?700:400,background:m.getMonth()===danes.getMonth()&&m.getFullYear()===danes.getFullYear()?"rgba(59,130,246,0.05)":"transparent"}}>
                        {m.toLocaleDateString("sl-SI",{month:"short"})}<br/><span style={{fontSize:8,opacity:0.7}}>{m.getFullYear()}</span>
                      </div>
                    ))}
                  </div>
                  {terminKats.map((k,ri)=>{
                    const d = datumi[k.naziv];
                    if (!d) return null;
                    const faza = terminFaze[k.id] || {};
                    const np = project.kats.find(x=>x.id===k.id) ? katNp(project.kats.find(x=>x.id===k.id)) : (faza.status==="zakljuceno" ? 100 : faza.napredek||0);
                    const jeZamuda = danes > d.konec && np < 100;
                    return (
                      <div key={k.id} style={{display:"flex",borderBottom:"1px solid rgba(0,0,0,0.024)",background:ri%2===0?"transparent":"rgba(0,0,0,0.008)"}}>
                        <div style={{width:180,flexShrink:0,padding:"5px 14px",display:"flex",alignItems:"center",gap:6,borderRight:"1px solid rgba(0,0,0,0.04)"}}>
                          <span style={{fontSize:11}}>{k.ikona}</span>
                          <span style={{fontSize:11,color:"#1A1D24",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={k.naziv}>{k.naziv}</span>
                        </div>
                        {meseci.map((m,mi)=>{
                          const mesStart = new Date(m.getFullYear(), m.getMonth(), 1);
                          const mesEnd = new Date(m.getFullYear(), m.getMonth()+1, 0);
                          const aktivna = d.zacetek <= mesEnd && d.konec >= mesStart;
                          const isDanes = m.getMonth()===danes.getMonth()&&m.getFullYear()===danes.getFullYear();
                          const barva = faza.status==="zakljuceno"?"rgba(52,211,153,0.35)":jeZamuda?"rgba(239,68,68,0.35)":"rgba(59,130,246,0.3)";
                          return (
                            <div key={mi} style={{width:70,flexShrink:0,padding:"4px 3px",borderLeft:"1px solid rgba(0,0,0,0.032)",background:isDanes?"rgba(59,130,246,0.03)":"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                              {aktivna&&<div style={{width:"85%",height:14,background:barva,borderRadius:3,overflow:"hidden"}}>
                                {np>0&&<div style={{width:`${np}%`,height:"100%",background:"rgba(0,0,0,0.14)"}}/>}
                              </div>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  <div style={{display:"flex",gap:14,padding:"10px 14px",borderTop:"1px solid rgba(0,0,0,0.056)",flexWrap:"wrap"}}>
                    {[["rgba(59,130,246,0.3)","V teku"],["rgba(52,211,153,0.35)","Zaključeno"],["rgba(239,68,68,0.35)","Zamuda"]].map(([c,l])=>(
                      <span key={l} style={{fontSize:11,color:"#5A6472",display:"flex",alignItems:"center",gap:5}}>
                        <span style={{display:"inline-block",width:20,height:10,background:c,borderRadius:2}}/>{l}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </>)}

      {/* Tooltip */}
      {tooltip&&(
        <div style={{position:"fixed",left:Math.min(tooltip.x, window.innerWidth-220),top:Math.max(10,tooltip.y-130),background:"#EEF0F4",border:"1px solid rgba(0,0,0,0.12)",borderRadius:10,padding:"12px 14px",zIndex:9999,pointerEvents:"none",minWidth:200,boxShadow:"0 8px 24px rgba(0,0,0,0.12)"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#1A1D24",marginBottom:8}}>{tooltip.k.ikona} {tooltip.k.naziv}</div>
          <div style={{fontSize:11,color:"#5A6472",display:"flex",flexDirection:"column",gap:4}}>
            <span>📅 Začetek: <b style={{color:"#1A1D24"}}>{fmtDatum(tooltip.d.zacetek)}</b></span>
            <span>Konec: <b style={{color:"#1A1D24"}}>{fmtDatum(tooltip.d.konec)}</b></span>
            <span>⏱ Trajanje: <b style={{color:"#1A1D24"}}>{tooltip.d.trajanje} dni</b></span>
            <span>📊 Napredek: <b style={{color:tooltip.np===100?"#10B981":tooltip.np>0?"#3B82F6":"#5A6472"}}>{tooltip.np}%</b></span>
            <span>Status: <b style={{color:tooltip.status==="zakljuceno"?"#10B981":tooltip.status==="v_teku"?"#3B82F6":tooltip.status==="cakanje"?"#D97706":tooltip.status==="odpovedano"?"#EF4444":tooltip.jeZamuda?"#EF4444":"#5A6472"}}>{tooltip.status==="zakljuceno"?"Zaključeno":tooltip.status==="v_teku"?"V teku":tooltip.status==="cakanje"?"Čakanje":tooltip.status==="odpovedano"?"Odpovedano":tooltip.jeZamuda?"Zamuda":"Načrtovano"}</b></span>
            {tooltip.opomba&&<span style={{marginTop:4,paddingTop:4,borderTop:"1px solid rgba(0,0,0,0.08)",color:"#D97706"}}>{tooltip.opomba}</span>}
          </div>
        </div>
      )}

      {/* Confirm briši */}
      {confirmDelFaza&&(()=>{
        const k = terminKats.find(k=>k.id===confirmDelFaza);
        return (
          <ConfirmModal
            msg={<><b style={{color:"#DC2626"}}>Izbriši fazo "{k?.naziv}"?</b><br/><span style={{fontSize:12,color:"#5A6472"}}>Faza se bo trajno izbrisala iz terminskega plana.</span></>}
            onOk={()=>{ onUpdate(p=>({terminKats:(p.terminKats||[]).filter(x=>x.id!==confirmDelFaza)})); setConfirmDelFaza(null); }}
            onCancel={()=>setConfirmDelFaza(null)}
          />
        );
      })()}

      {/* Dodaj fazo modal */}
      {dodajFazoPoId&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.12)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
          <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:16,padding:28,width:"min(420px,95vw)"}}>
            <div style={{fontSize:15,fontWeight:700,color:"#1A1D24",marginBottom:4}}>＋ Nova faza</div>
            <div style={{fontSize:12,color:"#5A6472",marginBottom:16}}>Doda se takoj za izbrano fazo</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:"#5A6472",marginBottom:5,letterSpacing:1}}>IME FAZE</div>
              <input autoFocus value={novaFazaNaziv} onChange={e=>setNovaFazaNaziv(e.target.value)}
                placeholder="npr. Elektro — cevi plata 1"
                style={{width:"100%",background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.14)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"8px 12px"}}/>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:"#5A6472",marginBottom:5,letterSpacing:1}}>TRAJANJE</div>
              <div style={{display:"flex",gap:8}}>
                <input type="number" min={1} max={365} value={novaFazaTrajanje} onChange={e=>setNovaFazaTrajanje(Number(e.target.value))}
                  style={{flex:1,background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.14)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"8px 12px"}}/>
                <select value={novaFazaEnota} onChange={e=>setNovaFazaEnota(e.target.value)}
                  style={{background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.14)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"8px 10px",cursor:"pointer"}}>
                  <option value="dnevi">dnevi</option>
                  <option value="tedni">tedni</option>
                </select>
              </div>
            </div>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,color:"#5A6472",marginBottom:5,letterSpacing:1}}>ZAČETEK (opcijsko)</div>
              <input type="date" value={novaFazaZacetek} onChange={e=>setNovaFazaZacetek(e.target.value)}
                style={{width:"100%",background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.14)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"8px 12px"}}/>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>{setDodajFazoPoId(null);setNovaFazaNaziv("");setNovaFazaTrajanje(28);setNovaFazaZacetek("");}}
                style={{flex:1,background:"transparent",border:"1px solid rgba(0,0,0,0.08)",color:"#5A6472",borderRadius:8,padding:"9px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Prekliči</button>
              <button onClick={()=>{
                if(!novaFazaNaziv.trim()) return;
                const trajanje = novaFazaEnota==="tedni" ? novaFazaTrajanje*7 : novaFazaTrajanje;
                const novaKat = { id: uid(), naziv: novaFazaNaziv.trim(), ikona:"📋", barva:"#3B82F6", izStroskovnika: false };
                onUpdate(p => {
                  const tkats = p.terminKats && p.terminKats.length > 0 ? p.terminKats : kats.map(k=>({id:k.id,naziv:k.naziv,ikona:k.ikona,barva:k.barva,izStroskovnika:true}));
                  const idx = tkats.findIndex(k=>k.id===dodajFazoPoId);
                  const noveKats = [...tkats];
                  noveKats.splice(idx+1, 0, novaKat);
                  return { terminKats: noveKats, terminFaze: { ...(p.terminFaze||{}), [novaKat.id]: { trajanje, zacetek: novaFazaZacetek||null } } };
                });
                setDodajFazoPoId(null);
                setNovaFazaNaziv("");
                setNovaFazaTrajanje(28);
                setNovaFazaZacetek("");
              }} style={{flex:1,background:"rgba(59,130,246,0.2)",border:"1px solid rgba(59,130,246,0.4)",color:"#2563EB",borderRadius:8,padding:"9px",fontSize:13,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Dodaj fazo</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editFaza&&(()=>{
        const k = terminKats.find(k=>k.id===editFaza);
        const faza = terminFaze[editFaza] || {};
        const d = datumi[k?.naziv];
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.12)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
            <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:16,padding:28,width:"min(420px,95vw)",maxHeight:"90vh",overflowY:"auto"}}>
              <div style={{fontSize:15,fontWeight:700,color:"#1A1D24",marginBottom:4}}>{k?.ikona} {k?.naziv}</div>
              {d&&<div style={{fontSize:12,color:"#5A6472",marginBottom:16}}>Izračunan: {fmtDatum(d.zacetek)} — {fmtDatum(d.konec)}</div>}
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:"#5A6472",marginBottom:8,letterSpacing:1}}>STATUS</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {[{id:"nacrtovano",label:"Načrtovano",color:"#5A6472"},{id:"v_teku",label:"V teku",color:"#3B82F6"},{id:"cakanje",label:"Čakanje",color:"#D97706"},{id:"zakljuceno",label:"Zaključeno",color:"#10B981"},{id:"odpovedano",label:"Odpovedano",color:"#EF4444"}].map(s=>(
                    <button key={s.id} onClick={()=>saveFaza(editFaza,{...faza,status:s.id})}
                      style={{fontSize:11,padding:"4px 10px",borderRadius:20,border:`1px solid ${(faza.status||"nacrtovano")===s.id?s.color:"rgba(0,0,0,0.08)"}`,background:"transparent",color:(faza.status||"nacrtovano")===s.id?s.color:"#5A6472",cursor:"pointer",fontFamily:"inherit"}}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Napredek za ročno dodane faze */}
              {!project.kats.find(x=>x.id===editFaza) && (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:11,color:"#5A6472",marginBottom:8,letterSpacing:1}}>NAPREDEK</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {[0,25,50,75,100].map(pct=>(
                      <button key={pct} onClick={()=>saveFaza(editFaza,{...faza,napredek:pct,status:pct===100?"zakljuceno":faza.status})}
                        style={{fontSize:12,padding:"4px 12px",borderRadius:20,border:`1px solid ${(faza.napredek||0)===pct?"#3B82F6":"rgba(0,0,0,0.08)"}`,background:(faza.napredek||0)===pct?"rgba(59,130,246,0.2)":"transparent",color:(faza.napredek||0)===pct?"#2563EB":"#5A6472",cursor:"pointer",fontFamily:"inherit",fontWeight:(faza.napredek||0)===pct?600:400}}>
                        {pct}%
                      </button>
                    ))}
                  </div>
                  <div style={{marginTop:8,background:"rgba(0,0,0,0.04)",borderRadius:4,height:6,overflow:"hidden"}}>
                    <div style={{width:`${faza.napredek||0}%`,height:"100%",background:"#3B82F6",borderRadius:4,transition:"width 0.3s"}}/>
                  </div>
                </div>
              )}

              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:"#5A6472",marginBottom:5,letterSpacing:1}}>TRAJANJE ALI DATUM KONCA</div>
                <div style={{display:"flex",gap:8,marginBottom:6}}>
                  <input type="number" min={1} max={365} defaultValue={faza.trajanje||28} id="trajanje-input" name="trajanje-input"
                    style={{flex:1,background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.14)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"8px 12px"}}/>
                  <select id="trajanje-enota" name="trajanje-enota" defaultValue="dnevi"
                    style={{background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.14)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"8px 10px",cursor:"pointer"}}>
                    <option value="dnevi">dnevi</option>
                    <option value="tedni">tedni</option>
                  </select>
                </div>
                <div style={{fontSize:11,color:"#5A6472",marginBottom:5}}>ali vnesite datum konca:</div>
                <input type="date" id="konec-input" name="konec-input"
                  defaultValue={faza.konecDatum||""}
                  onChange={e=>{
                    if(e.target.value && faza.zacetek) {
                      const zac = new Date(faza.zacetek);
                      const kon = new Date(e.target.value);
                      const dni = Math.max(1, Math.round((kon-zac)/(1000*60*60*24)));
                      const el = document.getElementById("trajanje-input");
                      if(el) el.value = dni;
                    }
                  }}
                  style={{width:"100%",background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.14)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"8px 12px"}}/>
                <div style={{fontSize:11,color:"#9AA3B0",marginTop:4}}>Datum konca samodejno izračuna trajanje</div>
              </div>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:"#5A6472",marginBottom:5,letterSpacing:1}}>ROČNI ZAČETEK (opcijsko)</div>
                <input type="date" defaultValue={faza.zacetek||""} id="zacetek-input" name="zacetek-input"
                  style={{width:"100%",background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.14)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"8px 12px"}}/>
                <div style={{fontSize:11,color:"#9AA3B0",marginTop:4}}>Pusti prazno za samodejni izračun</div>
              </div>
              <div style={{marginBottom:20}}>
                <div style={{fontSize:11,color:"#5A6472",marginBottom:5,letterSpacing:1}}>OPOMBA (opcijsko)</div>
                <textarea id="opomba-input" name="opomba-input" defaultValue={faza.opomba||""} rows={2}
                  placeholder="npr. Kontakt: Kovač 041 123 456..."
                  style={{width:"100%",background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.14)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"8px 12px",resize:"vertical"}}/>
              </div>
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>setEditFaza(null)}
                  style={{flex:1,background:"transparent",border:"1px solid rgba(0,0,0,0.08)",color:"#5A6472",borderRadius:8,padding:"9px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Prekliči</button>
                <button onClick={()=>{
                  const trajanjeVal = Number(document.getElementById("trajanje-input")?.value)||28;
                  const enota = document.getElementById("trajanje-enota")?.value;
                  const trajanje = enota==="tedni" ? trajanjeVal*7 : trajanjeVal;
                  const z = document.getElementById("zacetek-input")?.value;
                  const konecDatum = document.getElementById("konec-input")?.value||null;
                  const opomba = document.getElementById("opomba-input")?.value||"";
                  saveFaza(editFaza, { ...faza, trajanje, zacetek: z||null, konecDatum, opomba });
                }} style={{flex:1,background:"rgba(59,130,246,0.2)",border:"1px solid rgba(59,130,246,0.4)",color:"#2563EB",borderRadius:8,padding:"9px",fontSize:13,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Shrani</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Foto galerija ────────────────────────────────────────────────────────────
function FotoView({ project, userId }) {
  const [selectedKat, setSelectedKat] = useState(null);
  const [photos, setPhotos] = useState({});
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileInputRef = useRef(null);

  const fotoKats = project.kats.filter(k => 
    k.naziv !== "Dokumentacija & Projektiranje" && 
    k.naziv !== "Nadzor & Vodenje"
  );

  // Naloži seznam slik ob zagonu
  useEffect(() => {
    if (!supabase || !userId) return;
    loadAllPhotos();
  }, []);

  const loadAllPhotos = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase.storage.from('project-photos').list(`${userId}/${project.id}`, { limit: 500 });
      if (error || !data) return;
      // Pridobi signed URLs za vsako sliko
      const grouped = {};
      for (const f of data) {
        const parts = f.name.split('__');
        const katId = parts[0];
        const { data: urlData } = await supabase.storage.from('project-photos')
          .createSignedUrl(`${userId}/${project.id}/${f.name}`, 3600);
        if (!grouped[katId]) grouped[katId] = [];
        grouped[katId].push({ name: f.name, url: urlData?.signedUrl });
      }
      setPhotos(grouped);
    } catch(e) { console.error('Load photos error:', e); }
  };

  const uploadPhoto = async (files, katId) => {
    if (!supabase || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split('.').pop();
        const fname = `${katId}__${Date.now()}__${Math.random().toString(36).slice(2)}.${ext}`;
        const path = `${userId}/${project.id}/${fname}`;
        const { data, error } = await supabase.storage.from('project-photos').upload(path, file);
      }
      await loadAllPhotos();
    } finally { setUploading(false); }
  };

  const deletePhoto = async (filename) => {
    if (!supabase) return;
    await supabase.storage.from('project-photos').remove([`${userId}/${project.id}/${filename}`]);
    await loadAllPhotos();
  };

  const totalPhotos = Object.values(photos).reduce((a, v) => a + v.length, 0);

  return (
    <div style={{padding:"20px 16px",overflowY:"auto",flex:1}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:700,color:"#1A1D24"}}>Foto dnevnik</h2>
          <p style={{fontSize:13,color:"#5A6472",marginTop:3}}>Fotografirajte napredek po fazah gradnje — {totalPhotos} {totalPhotos===1?"fotografija":totalPhotos<5?"fotografije":"fotografij"}</p>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox&&(
        <div onClick={()=>setLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.12)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <img src={lightbox} style={{maxWidth:"90vw",maxHeight:"90vh",borderRadius:8,objectFit:"contain"}} onClick={e=>e.stopPropagation()}/>
          <button onClick={()=>setLightbox(null)} style={{position:"absolute",top:20,right:20,background:"rgba(0,0,0,0.08)",border:"none",color:"#1A1D24",fontSize:24,cursor:"pointer",borderRadius:8,padding:"4px 12px"}}>✕</button>
        </div>
      )}

      {/* Kategorije */}
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {fotoKats.map(k => {
          const katPhotos = photos[k.id] || [];
          const isOpen = selectedKat === k.id;
          return (
            <div key={k.id} style={{background:"#FFFFFF",border:`1px solid ${katPhotos.length>0?"rgba(59,130,246,0.25)":"rgba(0,0,0,0.056)"}`,borderRadius:12,overflow:"hidden"}}>
              {/* Header */}
              <div onClick={()=>setSelectedKat(isOpen?null:k.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",cursor:"pointer"}}>
                <div style={{width:32,height:32,background:`${k.barva}18`,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,border:`1px solid ${k.barva}30`,flexShrink:0}}>{k.ikona}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:"#1A1D24"}}>{k.naziv}</div>
                  <div style={{fontSize:11,color:"#5A6472",marginTop:1}}>{katPhotos.length>0?`${katPhotos.length} ${katPhotos.length===1?"fotografija":katPhotos.length<5?"fotografije":"fotografij"}`:"Ni fotografij"}</div>
                </div>
                {katPhotos.length>0&&<div style={{display:"flex",gap:4}}>
                  {katPhotos.slice(0,3).map((f,i)=>(
                    <img key={i} src={f.url} style={{width:32,height:32,borderRadius:5,objectFit:"cover",border:"1px solid rgba(0,0,0,0.08)"}}/>
                  ))}
                  {katPhotos.length>3&&<div style={{width:32,height:32,borderRadius:5,background:"rgba(0,0,0,0.064)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#5A6472"}}>+{katPhotos.length-3}</div>}
                </div>}
                <span style={{fontSize:13,color:"#5A6472",transform:isOpen?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.2s"}}>▾</span>
              </div>

              {/* Galerija */}
              {isOpen&&(
                <div style={{padding:"0 16px 16px",borderTop:"1px solid rgba(0,0,0,0.04)"}}>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:8,marginTop:12}}>
                    {katPhotos.map((f,i)=>(
                      <div key={i} style={{position:"relative",aspectRatio:"1",borderRadius:8,overflow:"hidden",border:"1px solid rgba(0,0,0,0.08)"}}>
                        <img src={f.url} style={{width:"100%",height:"100%",objectFit:"cover",cursor:"pointer"}} onClick={()=>setLightbox(f.url)}/>
                        <button onClick={()=>deletePhoto(f.name)} style={{position:"absolute",top:4,right:4,background:"rgba(0,0,0,0.12)",border:"none",color:"#DC2626",borderRadius:4,padding:"2px 6px",fontSize:11,cursor:"pointer"}}><Trash2 size={14}/></button>
                      </div>
                    ))}
                    {/* Upload gumb */}
                    <div onClick={()=>{fileInputRef.current.dataset.katid=k.id;fileInputRef.current.click();}}
                      style={{aspectRatio:"1",borderRadius:8,border:"2px dashed rgba(59,130,246,0.3)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",gap:4,background:"rgba(59,130,246,0.05)"}}>
                      <span style={{fontSize:20,display:"inline-flex"}}>{uploading?"…":<Camera size={20}/>}</span>
                      <span style={{fontSize:11,color:"#3B82F6"}}>{uploading?"Nalagam...":"Dodaj foto"}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" multiple style={{display:"none"}}
        onChange={e=>{ const katId=fileInputRef.current.dataset.katid; uploadPhoto(e.target.files, katId); e.target.value=""; }}/>
    </div>
  );
}

// ─── Privacy Policy Modal ─────────────────────────────────────────────────────
function PrivacyModal({ onClose }) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.12)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000,padding:"20px",overflowY:"auto"}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:16,padding:32,width:"min(560px,95vw)",maxHeight:"85vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={{fontSize:15,fontWeight:700,color:"#1A1D24"}}>Politika zasebnosti</div>
          <button onClick={onClose} style={{background:"transparent",border:"none",color:"#5A6472",fontSize:18,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
        </div>
        <div style={{fontSize:11,color:"#5A6472",marginBottom:24}}>Upravljavec podatkov: <b style={{color:"#5A6472"}}>Rok Marolt</b> · <a href="mailto:rok.marolt@mojagradnja.si" style={{color:"#3B82F6"}}>rok.marolt@mojagradnja.si</a></div>

        {[
          {title:"Katere podatke zbiramo", items:[
            "Email naslov (za prijavo in identifikacijo)",
            "Podatki o projektu (stroški, napredek, kategorije, budget)",
            "Kontakti izvajalcev, ki jih uporabnik sam vnese",
          ]},
          {title:"Zakaj zbiramo podatke", items:[
            "Delovanje aplikacije in shranjevanje vaših projektov",
            "Identifikacija uporabnika (prijava v račun)",
            "Ni oglaševanja in ne prodajamo podatkov tretjim osebam",
          ], note:"Pravna podlaga: izvajanje storitve (člen 6(1)(b) GDPR)"},
          {title:"Kje se hranijo podatki", items:[
            "Supabase — EU strežniki (Frankfurt, Nemčija)",
            "Vercel — hosting aplikacije (EU regija)",
            "Podatki so zaščiteni med prenosom (HTTPS)",
          ]},
          {title:"Kako dolgo hranimo podatke", items:[
            "Dokler imate aktiven račun",
            "Ob izbrisu računa se vsi podatki trajno izbrišejo",
          ]},
          {title:"Vaše pravice (GDPR)", items:[
            "Dostop do svojih podatkov",
            "Popravek podatkov",
            "Izbris računa in vseh podatkov (gumb v aplikaciji ali na zahtevo)",
            "Pritožba pri nadzornem organu (Informacijski pooblaščenec RS)",
          ]},
          {title:"Piškotki", items:[
            "Aplikacija ne uporablja sledilnih piškotkov",
            "Uporabljajo se samo nujni tehnični piškotki za prijavo (seja)",
          ]},
          {title:"Odgovornost uporabnika", items:[
            "Uporabnik je odgovoren za podatke, ki jih vnese v aplikacijo (npr. kontaktni podatki izvajalcev)",
          ]},
        ].map((s,i)=>(
          <div key={i} style={{marginBottom:18}}>
            <div style={{fontSize:11,fontWeight:700,color:"#3B82F6",marginBottom:7,letterSpacing:0.5}}>{s.title.toUpperCase()}</div>
            {s.items.map((c,j)=>(
              <div key={j} style={{display:"flex",gap:8,marginBottom:4}}>
                <span style={{color:"#5A6472",flexShrink:0}}>–</span>
                <span style={{fontSize:12,color:"#5A6472",lineHeight:1.6}}>{c}</span>
              </div>
            ))}
            {s.note&&<div style={{fontSize:11,color:"#5A6472",marginTop:5,fontStyle:"italic"}}>{s.note}</div>}
          </div>
        ))}

        <div style={{borderTop:"1px solid rgba(0,0,0,0.056)",paddingTop:14,marginTop:4}}>
          <div style={{fontSize:11,fontWeight:700,color:"#3B82F6",marginBottom:6,letterSpacing:0.5}}>KONTAKT</div>
          <div style={{fontSize:12,color:"#5A6472"}}>Za vprašanja glede zasebnosti: <a href="mailto:rok.marolt@mojagradnja.si" style={{color:"#3B82F6"}}>rok.marolt@mojagradnja.si</a></div>
          <div style={{fontSize:11,color:"#5A6472",marginTop:8}}>Zadnja posodobitev: 4. 5. 2026</div>
        </div>

        <button onClick={onClose} style={{marginTop:20,width:"100%",background:"rgba(59,130,246,0.15)",border:"1px solid rgba(59,130,246,0.3)",color:"#2563EB",borderRadius:8,padding:"10px",fontSize:13,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Zapri</button>
      </div>
    </div>
  );
}

// ─── Reset gesla ekran ────────────────────────────────────────────────────────
function ResetGesloScreen({ onDone }) {
  const [geslo, setGeslo] = useState("");
  const [geslo2, setGeslo2] = useState("");
  const [napaka, setNapaka] = useState("");
  const [loading, setLoading] = useState(false);
  const [uspeh, setUspeh] = useState(false);

  const submit = async () => {
    setNapaka("");
    if (geslo.length < 6) { setNapaka("Geslo mora imeti vsaj 6 znakov."); return; }
    if (geslo !== geslo2) { setNapaka("Gesli se ne ujemata."); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: geslo });
      if (error) setNapaka("Napaka: " + error.message);
      else setUspeh(true);
    } finally { setLoading(false); }
  };

  if (uspeh) return (
    <div style={{fontFamily:"'Syne',sans-serif",background:"#F5F6F8",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(52,211,153,0.3)",borderRadius:16,padding:40,width:"min(400px,90vw)",textAlign:"center"}}>
        <div style={{fontSize:36,marginBottom:16}}>✅</div>
        <div style={{fontSize:15,fontWeight:700,color:"#1A1D24",marginBottom:10}}>Geslo posodobljeno!</div>
        <div style={{fontSize:13,color:"#5A6472",marginBottom:20}}>Vaše geslo je bilo uspešno posodobljeno.</div>
        <button onClick={onDone} style={{background:"rgba(59,130,246,0.15)",border:"1px solid rgba(59,130,246,0.3)",color:"#2563EB",borderRadius:8,padding:"8px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Nadaljuj v aplikacijo →</button>
      </div>
    </div>
  );

  return (
    <div style={{fontFamily:"'Syne',sans-serif",background:"#F5F6F8",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:16,padding:36,width:"min(400px,90vw)"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:28}}>
          <div style={{width:40,height:40,background:"linear-gradient(135deg,#3B82F6,#8B5CF6)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,color:"#fff"}}><Lock size={20}/></div>
          <div><div style={{fontSize:15,fontWeight:700,color:"#1A1D24"}}>Novo geslo</div><div style={{fontSize:11,color:"#5A6472"}}>Vnesite novo geslo za vaš račun</div></div>
        </div>

        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,color:"#5A6472",marginBottom:5,letterSpacing:1}}>NOVO GESLO</div>
          <input type="password" value={geslo} onChange={e=>setGeslo(e.target.value)}
            placeholder="najmanj 6 znakov" autoComplete="new-password"
            onKeyDown={e=>e.key==="Enter"&&submit()}
            style={{width:"100%",background:"rgba(0,0,0,0.056)",border:`1px solid ${geslo&&geslo.length<6?"rgba(239,68,68,0.4)":"rgba(0,0,0,0.096)"}`,borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"10px 12px"}}/>
          {geslo&&geslo.length<6&&<div style={{fontSize:11,color:"#DC2626",marginTop:4}}>Vsaj 6 znakov</div>}
        </div>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:11,color:"#5A6472",marginBottom:5,letterSpacing:1}}>POTRDI NOVO GESLO</div>
          <input type="password" value={geslo2} onChange={e=>setGeslo2(e.target.value)}
            placeholder="ponovite geslo" autoComplete="new-password"
            onKeyDown={e=>e.key==="Enter"&&submit()}
            style={{width:"100%",background:"rgba(0,0,0,0.056)",border:`1px solid ${geslo2&&geslo!==geslo2?"rgba(239,68,68,0.4)":geslo2&&geslo===geslo2?"rgba(52,211,153,0.4)":"rgba(0,0,0,0.096)"}`,borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"10px 12px"}}/>
          {geslo2&&geslo===geslo2&&<div style={{fontSize:11,color:"#10B981",marginTop:4}}>✓ Gesli se ujemata</div>}
          {geslo2&&geslo!==geslo2&&<div style={{fontSize:11,color:"#DC2626",marginTop:4}}>✗ Gesli se ne ujemata</div>}
        </div>

        {napaka&&<div style={{fontSize:12,color:"#DC2626",marginBottom:14,padding:"8px 12px",background:"rgba(239,68,68,0.1)",borderRadius:6}}>{napaka}</div>}

        <button onClick={submit} disabled={loading} style={{width:"100%",background:"rgba(59,130,246,0.2)",border:"1px solid rgba(59,130,246,0.4)",color:"#2563EB",borderRadius:8,padding:"11px",fontSize:13,fontFamily:"inherit",cursor:"pointer",fontWeight:600,opacity:loading?0.7:1}}>
          {loading?"Shranjujem...":"Shrani novo geslo →"}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// LandingPage — nova verzija (prirejeno iz ChatGPT HTML predloge)
// ============================================================
// Slike: vse slike spodaj se naložijo iz `/assets/...`, kar pomeni
// da morajo fizično obstajati v `public/assets/` mapi tvojega
// React projekta (torej: ~/Desktop/gradnja-app/public/assets/).
// Seznam vseh potrebnih datotek je na koncu te datoteke v komentarju.
// ============================================================

const TABS = [
  { id: "stroskovnikPanel", label: "💰 Stroškovnik", small: "računi, poraba, prekoračitve", img: "/stroskovnik.jpg", alt: "Stroškovnik" },
  { id: "napredekPanel", label: "📈 Napredek", small: "faze in odstotki dokončanja", img: "/napredek.jpg", alt: "Napredek del" },
  { id: "pregledPanel", label: "📊 Finančni pregled", small: "proračun, poraba, ostanek", img: "/pregled.jpg", alt: "Finančni pregled" },
  { id: "terminskiPanel", label: "📅 Terminski plan", small: "časovnica del in izvajalcev", img: "/terminski.jpg", alt: "Terminski plan" },
  { id: "analizaPanel", label: "📊 Analiza", small: "struktura investicije", img: "/analiza.jpg", alt: "Analiza investicije" },
  { id: "dokumentiPanel", label: "📁 Dokumenti", small: "računi, pogodbe, načrti", img: "/dokumenti.jpg", alt: "Dokumenti" },
  { id: "kontaktiPanel", label: "👥 Kontakti", small: "izvajalci in komunikacija", img: "/kontakti.jpg", alt: "Kontakti izvajalcev" },
];

const GALLERY = [
  { src: "/gradnja-temelji.jpg", alt: "Temelji" },
  { src: "/gradnja-beton.jpg", alt: "Betoniranje" },
  { src: "/gradnja-opaz.jpg", alt: "Opaž" },
  { src: "/gradnja-zerjav.jpg", alt: "Žerjav" },
  { src: "/gradnja-elektro.jpg", alt: "Elektro priklop" },
  { src: "/gradnja-jama.jpg", alt: "Izkop" },
  { src: "/gradnja-plosca.jpg", alt: "Temeljna plošča" },
  { src: "/gradnja-zidava.jpg", alt: "Zidava" },
];

const MILESTONES = [
  { img: "/gradnja-jama.jpg", alt: "Izkop", num: 1, tag: "Začetek", title: "Izkop in priprava terena", text: "Prvi stroški, prvi izvajalci in začetek časovnice projekta." },
  { img: "/faza-priprava-temeljne-plosce.jpg", alt: "Priprava temeljne plošče", num: 2, tag: "Temelji", title: "Priprava temeljne plošče", text: "Stroški materiala in izvajalcev se začnejo povezovati z dejanskimi fazami." },
  { img: "/faza-zakljucena-plosca.jpg", alt: "Zaključena temeljna plošča", num: 3, tag: "Betoniranje", title: "Zaključena plošča", text: "Faza se označi kot zaključena, budget pa se primerja z dejansko porabo." },
  { img: "/gradnja-opaz.jpg", alt: "Opaž", num: 4, tag: "Konstrukcija", title: "Opaž in armatura", text: "Fotografije ostanejo dokaz, kaj je bilo narejeno pred nadaljevanjem del." },
  { img: "/faza-zidava-rast-objekta.jpg", alt: "Zidava in rast objekta", num: 5, tag: "Napredek", title: "Zidava in rast objekta", text: "Ko projekt napreduje, imaš hkrati pregled nad stroški, izvajalci in terminskim planom." },
];

function LandingPage({ onStart, onLogin }) {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("mg-in");
        });
      },
      { threshold: 0.12 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addReveal = (el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };

  const goRegister = (e) => {
    e.preventDefault();
    onStart && onStart();
  };
  const goLogin = (e) => {
    e.preventDefault();
    onLogin && onLogin();
  };

  return (
    <div className="mg-landing">
      <style>{LANDING_CSS}</style>

      <header className="mg-nav">
        <div className="mg-wrap mg-nav-inner">
          <a className="mg-brand" href="#top" aria-label="MojaGradnja">
            <img src="/logo-header.png" alt="MojaGradnja logotip" />
            <span>MojaGradnja</span>
          </a>
          <nav className="mg-menu" aria-label="Glavna navigacija">
            <a href="#problem">Zakaj</a>
            <a href="#funkcije">Funkcije</a>
            <a href="#finance">Stroški</a>
            <a href="#pot">Zgodba</a>
            <a href="#kako">Kako deluje</a>
          </nav>
          <div className="mg-nav-actions">
            <a className="mg-btn" href="/login" onClick={goLogin}>Prijava</a>
            <a className="mg-btn mg-btn-primary" href="/register" onClick={goRegister}>Začni brezplačno</a>
          </div>
        </div>
      </header>

      <main id="top">
        {/* 1. HERO */}
        <section className="mg-hero">
          <div className="mg-wrap mg-hero-grid">
            <div ref={addReveal} className="mg-reveal">
              <div className="mg-eyebrow">Od ideje do vselitve</div>
              <h1>Tvoj osebni vodič skozi <span className="mg-accent">gradnjo ali prenovo</span> doma.</h1>
              <p className="mg-lead">Gradnja ima na stotine odločitev, različnih faz in rokov. Ni treba, da jih držiš v glavi.</p>
              <p className="mg-lead mg-lead-sub">Na enem mestu spremljaj stroške, napredek, izvajalce, ponudbe, dokumente, fotografije in pomembne odločitve.</p>
              <div className="mg-hero-actions">
                <a className="mg-btn mg-btn-primary" href="/register" onClick={goRegister}>Začni brezplačno</a>
                <a className="mg-btn mg-btn-gold" href="#funkcije">Poglej aplikacijo</a>
              </div>
              <div className="mg-trust-note">14 dni brezplačno · Brez kreditne kartice</div>
            </div>
            <div ref={addReveal} className="mg-hero-card mg-reveal">
              <div className="mg-live-badge"><span className="mg-pulse"></span> Projekt v živo</div>
              <div className="mg-screen-frame">
                <img src="/stroskovnik.jpg" alt="Pregled projekta v aplikaciji MojaGradnja" />
              </div>
            </div>
          </div>
        </section>

        {/* 2. PROBLEM */}
        <section id="problem" className="mg-section-tight">
          <div ref={addReveal} className="mg-small-wrap mg-section-head mg-reveal">
            <div className="mg-eyebrow">Zakaj to rabiš?</div>
            <h2>Gradnja je velik projekt. Pregled nad njo je lahko preprost.</h2>
            <p>Pri gradnji ali prenovi se hitro nabere ogromno informacij — razpršenih med ponudbami, e-pošto, sporočili, fotografijami in tabelami.</p>
          </div>
          <div className="mg-small-wrap mg-q-grid">
            <div ref={addReveal} className="mg-q-card mg-reveal">Koliko smo že porabili?</div>
            <div ref={addReveal} className="mg-q-card mg-reveal">Kdaj pridejo okna?</div>
            <div ref={addReveal} className="mg-q-card mg-reveal">Katero ponudbo smo potrdili?</div>
            <div ref={addReveal} className="mg-q-card mg-reveal">Koliko moramo še plačati izvajalcu?</div>
            <div ref={addReveal} className="mg-q-card mg-reveal">Kaj moramo urediti pred naslednjo fazo?</div>
            <div ref={addReveal} className="mg-q-card mg-reveal">Kje je račun za toplotno črpalko?</div>
          </div>
          <div ref={addReveal} className="mg-small-wrap mg-reveal" style={{textAlign:"center",marginTop:34}}>
            <p className="mg-punch">MojaGradnja jih poveže v en projekt. Da vedno veš:</p>
            <p className="mg-punch-big">Kje si. Koliko si porabil. Kaj je narejeno. In kaj sledi.</p>
          </div>
        </section>

        {/* 3. FUNKCIONALNOSTI — velike sekcije s screenshoti */}
        <section id="funkcije">
          <div ref={addReveal} className="mg-small-wrap mg-section-head mg-reveal">
            <div className="mg-eyebrow">Vse za tvojo gradnjo</div>
            <h2>Na enem mestu.</h2>
            <p>MojaGradnja ni samo stroškovnik. Je prostor, kjer skozi celoten projekt spremljaš svojo gradnjo ali prenovo.</p>
          </div>

          {/* Stroški */}
          <div className="mg-wrap mg-split mg-reverse">
            <div ref={addReveal} className="mg-image-card mg-reveal">
              <img src="/stroskovnik.jpg" alt="Stroški in proračun po kategorijah" />
              <span className="mg-label">Stroški po kategorijah</span>
            </div>
            <div ref={addReveal} className="mg-copy-card mg-reveal">
              <div className="mg-eyebrow">💶 Stroški in proračun</div>
              <h3>Vedno veš, koliko si načrtoval in koliko si dejansko porabil.</h3>
              <p>Postavi začetni proračun in spremljaj stroške po posameznih kategorijah: predvideno → dogovorjeno → plačano → preostanek.</p>
              <div className="mg-checks">
                <div className="mg-check">Skupni proračun in poraba</div>
                <div className="mg-check">Preostanek po kategorijah</div>
                <div className="mg-check">Strošek na m²</div>
                <div className="mg-check">Odstopanja od načrta</div>
              </div>
            </div>
          </div>

          {/* Faze / napredek */}
          <div className="mg-wrap mg-split">
            <div ref={addReveal} className="mg-copy-card mg-reveal">
              <div className="mg-eyebrow">🏗️ Faze in napredek</div>
              <h3>Vedno veš, kje se tvoj projekt trenutno nahaja.</h3>
              <p>Razdeli gradnjo na faze — od dokumentacije in zemeljskih del do notranje opreme in okolice. Na prvi pogled vidiš, kaj je zaključeno, kaj poteka in kaj še čaka.</p>
              <div className="mg-checks">
                <div className="mg-check">Kaj je zaključeno</div>
                <div className="mg-check">Kaj trenutno poteka</div>
                <div className="mg-check">Kaj še čaka</div>
                <div className="mg-check">Koliko je že narejenega</div>
              </div>
            </div>
            <div ref={addReveal} className="mg-image-card mg-reveal">
              <img src="/napredek.jpg" alt="Faze in napredek gradnje" />
              <span className="mg-label">Napredek po fazah</span>
            </div>
          </div>

          {/* Časovnica */}
          <div className="mg-wrap mg-split mg-reverse">
            <div ref={addReveal} className="mg-image-card mg-reveal">
              <img src="/terminski.jpg" alt="Časovnica in pomembni roki" />
              <span className="mg-label">Terminski plan</span>
            </div>
            <div ref={addReveal} className="mg-copy-card mg-reveal">
              <div className="mg-eyebrow">📅 Časovnica in roki</div>
              <h3>Ni pomembno samo kaj. Pomembno je tudi kdaj.</h3>
              <p>Načrtuj pomembne dogodke in spremljaj potek projekta skozi čas: začetek faze, prihod izvajalca, izmere oken, dobava, montaža, plačilo, zaključek — vse povezano s tvojim projektom.</p>
              <p className="mg-mini">Manj stvari, ki jih moraš držati v glavi.</p>
            </div>
          </div>

          {/* Preostale funkcije kot manjše kartice */}
          <div ref={addReveal} className="mg-wrap mg-features mg-reveal">
            <div className="mg-feature-card"><div className="mg-ico">👷</div><h3>Izvajalci in kontakti</h3><p>Vsi izvajalci zbrani ob projektu — povezani z deli, ponudbami in stroški. Nič več iskanja po telefonu.</p></div>
            <div className="mg-feature-card"><div className="mg-ico">📑</div><h3>Ponudbe</h3><p>Shrani več ponudb na enem mestu in primerjaj izvajalca, ceno, vsebino in status. Vedno veš, katero si izbral.</p></div>
            <div className="mg-feature-card"><div className="mg-ico">📄</div><h3>Dokumenti</h3><p>Predračuni, računi, pogodbe, načrti in garancije shranjeni neposredno k projektu — tam, kjer jih pričakuješ.</p></div>
            <div className="mg-feature-card"><div className="mg-ico">📸</div><h3>Fotografije po fazah</h3><p>Dokumentiraj instalacije, cevi in vode, preden se zaprejo stene. Čez leta natančno veš, kaj je pod tlemi.</p></div>
          </div>
        </section>

        {/* 4. FINANČNI PREGLED */}
        <section id="finance">
          <div ref={addReveal} className="mg-small-wrap mg-section-head mg-reveal">
            <div className="mg-eyebrow">Finančna slika projekta</div>
            <h2>Koliko bo moj dom dejansko stal?</h2>
            <p>Na začetku imaš ocene. Potem pridejo ponudbe, spremembe, dodatna dela in računi. MojaGradnja ti omogoča, da ves čas spremljaš dejansko finančno sliko.</p>
          </div>
          <div ref={addReveal} className="mg-small-wrap mg-fin-grid mg-reveal">
            <div className="mg-stat"><span>Proračun</span><b>250.000&nbsp;€</b></div>
            <div className="mg-stat mg-gold"><span>Dogovorjeno</span><b>187.500&nbsp;€</b></div>
            <div className="mg-stat mg-green"><span>Plačano</span><b>142.300&nbsp;€</b></div>
            <div className="mg-stat"><span>Preostanek</span><b>62.500&nbsp;€</b></div>
            <div className="mg-stat mg-wide"><span>Strošek na m²</span><b>1.584&nbsp;€/m²</b></div>
          </div>
          <div ref={addReveal} className="mg-small-wrap mg-reveal" style={{textAlign:"center",marginTop:30}}>
            <p className="mg-punch">Ne vidiš samo, koliko si že plačal.</p>
            <p className="mg-punch-big">Vidiš tudi, kam gre tvoj projekt.</p>
          </div>
        </section>

        {/* 5. ZGODBA GRADNJE — timeline */}
        <section id="pot">
          <div ref={addReveal} className="mg-small-wrap mg-section-head mg-reveal">
            <div className="mg-eyebrow">Od ideje do vselitve</div>
            <h2>Tvoja gradnja skozi čas.</h2>
            <p>Danes je samo parcela. Čez nekaj mesecev stojijo zidovi, potem streha, okna, inštalacije, fasada, oprema — in nekega dne se vseliš.</p>
          </div>
          <div className="mg-small-wrap mg-timeline">
            {MILESTONES.map((m) => (
              <article key={m.num} ref={addReveal} className="mg-milestone mg-reveal">
                <div className="mg-m-photo"><img src={m.img} alt={m.alt} /></div>
                <div className="mg-dot">{m.num}</div>
                <div className="mg-m-copy"><b>{m.tag}</b><h3>{m.title}</h3><p>{m.text}</p></div>
              </article>
            ))}
          </div>
        </section>

        {/* APLIKACIJA - TABS (ohranjen interaktivni prikaz) */}
        <section id="aplikacija">
          <div ref={addReveal} className="mg-small-wrap mg-section-head mg-reveal">
            <div className="mg-eyebrow">Aplikacija v praksi</div>
            <h2>Pravi zasloni. Pravi pregled.</h2>
            <p>Spodaj so zasloni aplikacije, ki pokažejo, kako se posamezni deli projekta povežejo v celoto.</p>
          </div>
          <div ref={addReveal} className="mg-wrap mg-app-showcase mg-reveal">
            <div className="mg-tabs" role="tablist">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  className={`mg-tab ${activeTab === tab.id ? "mg-active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                  type="button"
                >
                  {tab.label}<small>{tab.small}</small>
                </button>
              ))}
            </div>
            <div className="mg-app-panel">
              {TABS.map((tab) => (
                <div key={tab.id} className={`mg-app-image ${activeTab === tab.id ? "mg-active" : ""}`}>
                  <img src={tab.img} alt={tab.alt} />
                </div>
              ))}
              <div className="mg-app-copy">
                <p><strong>Vse je povezano:</strong> budget, faze gradnje, časovnica, dokumenti, kontakti in dokazila.</p>
                <span className="mg-pill">✓ dejanski zasloni aplikacije</span>
              </div>
            </div>
          </div>
        </section>

        {/* 6. ZA KOGA */}
        <section>
          <div ref={addReveal} className="mg-small-wrap mg-section-head mg-reveal">
            <div className="mg-eyebrow">Za koga je?</div>
            <h2>Narejeno za ljudi, ki gradijo. Ne za gradbena podjetja.</h2>
            <p>Ne potrebuješ znanja projektnega vodenja in ni ti treba postati gradbenik. MojaGradnja je narejena za investitorja, ki gradi ali prenavlja svoj dom.</p>
          </div>
          <div ref={addReveal} className="mg-wrap mg-audience mg-reveal">
            <div className="mg-audience-card"><div className="mg-ico">🔍</div><h3>Kaj se dogaja?</h3><p>Na prvi pogled vidiš stanje celotnega projekta.</p></div>
            <div className="mg-audience-card"><div className="mg-ico">💶</div><h3>Koliko me stane?</h3><p>Dejanska finančna slika, ne ugibanje.</p></div>
            <div className="mg-audience-card"><div className="mg-ico">📋</div><h3>Kaj moram urediti?</h3><p>Roki in naloge pred vsako naslednjo fazo.</p></div>
            <div className="mg-audience-card"><div className="mg-ico">➡️</div><h3>Kaj sledi?</h3><p>Vedno veš, kaj je naslednji korak.</p></div>
          </div>
        </section>

        {/* 7. EXCEL PRIMERJAVA (nižje na strani) */}
        <section>
          <div ref={addReveal} className="mg-small-wrap mg-section-head mg-reveal">
            <div className="mg-eyebrow">Excel ali MojaGradnja?</div>
            <h2>Excel je odličen. Gradnja pa je več kot tabela.</h2>
            <p>Veliko gradenj se začne z Excelom — in s tem ni nič narobe. A ko pridejo ponudbe, računi, izvajalci, dokumenti, fotografije, roki in spremembe, postane gradnja več kot tabela.</p>
          </div>
          <div ref={addReveal} className="mg-small-wrap mg-comparison mg-reveal">
            <div className="mg-compare-card mg-bad">
              <h3>V Excelu</h3>
              <div className="mg-compare-list mg-half">
                <div>Proračun</div>
                <div>Stroški</div>
                <div className="mg-manual">Strošek na m² (ročno)</div>
                <div className="mg-x">Faze gradnje</div>
                <div className="mg-x">Napredek projekta</div>
                <div className="mg-x">Časovnica</div>
                <div className="mg-x">Izvajalci in ponudbe</div>
                <div className="mg-x">Dokumenti in fotografije</div>
                <div className="mg-x">Celoten projekt na enem mestu</div>
              </div>
            </div>
            <div className="mg-compare-card mg-good">
              <h3>V MojaGradnja</h3>
              <div className="mg-compare-list">
                <div>Proračun in stroški</div>
                <div>Strošek na m² samodejno</div>
                <div>Faze in napredek gradnje</div>
                <div>Časovnica in roki</div>
                <div>Izvajalci in ponudbe</div>
                <div>Dokumenti in fotografije</div>
                <div>Celoten projekt na enem mestu</div>
              </div>
            </div>
          </div>
          <div ref={addReveal} className="mg-small-wrap mg-reveal" style={{textAlign:"center",marginTop:30}}>
            <p className="mg-punch">Excel ti pomaga voditi številke.</p>
            <p className="mg-punch-big">MojaGradnja ti pomaga voditi gradnjo.</p>
          </div>
        </section>

        {/* 8. KAKO DELUJE */}
        <section id="kako">
          <div ref={addReveal} className="mg-small-wrap mg-section-head mg-reveal">
            <div className="mg-eyebrow">Kako deluje?</div>
            <h2>Štirje koraki do pregleda.</h2>
          </div>
          <div ref={addReveal} className="mg-small-wrap mg-steps mg-reveal">
            <div className="mg-step"><div className="mg-step-num">1</div><h3>Ustvari svoj projekt</h3><p>Vnesi osnovne podatke: površino, okvirni proračun in informacije o gradnji ali prenovi.</p></div>
            <div className="mg-step"><div className="mg-step-num">2</div><h3>Postavi finančni okvir</h3><p>Dodaj kategorije in določi, koliko nameravaš porabiti. Ni treba, da je vse natančno — prilagajaš sproti.</p></div>
            <div className="mg-step"><div className="mg-step-num">3</div><h3>Dodajaj podatke med gradnjo</h3><p>Stroške, ponudbe, izvajalce, dokumente, fotografije in dogodke. MojaGradnja raste skupaj s tvojim domom.</p></div>
            <div className="mg-step"><div className="mg-step-num">4</div><h3>Vedno veš, kje si</h3><p>Odpreš projekt in takoj vidiš najpomembnejše: koliko si porabil, kaj je narejeno, kaj manjka in kaj prihaja.</p></div>
          </div>
        </section>

        {/* 9. ZGODBA NASTANKA */}
        <section>
          <div className="mg-wrap mg-split">
            <div ref={addReveal} className="mg-copy-card mg-reveal">
              <div className="mg-eyebrow">Zakaj je nastala?</div>
              <h3>Nastala je med pravo gradnjo.</h3>
              <p>Ko gradiš svoj dom, hitro ugotoviš, da informacije končajo povsod: proračun v tabeli, ponudbe v e-pošti, računi v mapah, fotografije na telefonu, dogovori v sporočilih — roki pa kar v glavi.</p>
              <p className="mg-punch-big" style={{marginTop:22}}>Želel sem eno mesto, kjer bi lahko spremljal celoten projekt.</p>
              <p className="mg-mini">Tako je nastala MojaGradnja — najprej za lastno gradnjo, danes za vsakogar, ki želi svoj projekt voditi bolj pregledno.</p>
            </div>
            <div ref={addReveal} className="mg-photo-grid mg-reveal">
              <a className="mg-photo-tile mg-large" href="/gradnja-zidava.jpg" onClick={(e) => { e.preventDefault(); setLightboxSrc("/gradnja-zidava.jpg"); }}>
                <img src="/gradnja-zidava.jpg" alt="Zidava na gradbišču" />
                <div className="mg-caption"><strong>Prava gradnja</strong><span>vir ideje</span></div>
              </a>
              <a className="mg-photo-tile" href="/gradnja-plosca.jpg" onClick={(e) => { e.preventDefault(); setLightboxSrc("/gradnja-plosca.jpg"); }}>
                <img src="/gradnja-plosca.jpg" alt="Temeljna plošča" />
                <div className="mg-caption"><strong>Temeljna plošča</strong><span>faza projekta</span></div>
              </a>
              <a className="mg-photo-tile" href="/gradnja-armatura.jpg" onClick={(e) => { e.preventDefault(); setLightboxSrc("/gradnja-armatura.jpg"); }}>
                <img src="/gradnja-armatura.jpg" alt="Armatura" />
                <div className="mg-caption"><strong>Armatura</strong><span>dokaz izvedbe</span></div>
              </a>
            </div>
          </div>
        </section>

        {/* 10. FINAL CTA */}
        <section>
          <div ref={addReveal} className="mg-wrap mg-cta mg-reveal">
            <div>
              <h2>Ne rabiš imeti vsega v glavi.</h2>
              <p>Na stotine odločitev, desetine izvajalcev, ponudbe, računi, roki, dokumenti in fotografije — naj bo vse na enem mestu.</p>
              <p className="mg-cta-slogan">MojaGradnja. Od ideje do vselitve.</p>
            </div>
            <div className="mg-cta-action">
              <a className="mg-btn mg-btn-primary" href="/register" onClick={goRegister}>Začni brezplačno</a>
              <span className="mg-trust-note">14 dni brezplačno · Brez kreditne kartice</span>
            </div>
          </div>
        </section>
      </main>

      <footer className="mg-footer">
        <div className="mg-wrap">
          <div className="mg-brand"><img src="/logo-header.png" alt="" /><span>MojaGradnja</span></div>
          <div>© 2026 MojaGradnja. Od ideje do vselitve.</div>
        </div>
      </footer>

      {lightboxSrc && (
        <div className="mg-lightbox mg-open" onClick={() => setLightboxSrc(null)}>
          <button aria-label="Zapri" onClick={() => setLightboxSrc(null)}>×</button>
          <img src={lightboxSrc} alt="" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

// ============================================================
// CSS — vse razrede sem preimenoval s predpono "mg-", da se ne
// prekrivajo z obstojočimi razredi v tvoji aplikaciji.
// ============================================================
const LANDING_CSS = `
.mg-landing{
  --bg:#05080d; --bg2:#080d15; --panel:#0d131d; --panel2:#111927;
  --line:rgba(255,255,255,.105); --line2:rgba(255,255,255,.16);
  --text:#f5f7fb; --muted:#a5afbf; --muted2:#768296;
  --blue:#3388ff; --blue2:#8fc1ff; --green:#32e6a5; --gold:#f6b32b; --red:#ff4f57;
  --radius:28px; --shadow:0 32px 110px rgba(0,0,0,.52);
  background:var(--bg); color:var(--text);
  font-family:Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  line-height:1.55; overflow-x:hidden; position:relative;
}
.mg-landing *{box-sizing:border-box}
.mg-landing a{color:inherit;text-decoration:none}
.mg-landing img{display:block;max-width:100%}
.mg-landing::before{
  content:""; position:fixed; inset:0; pointer-events:none; z-index:-2;
  background:
    radial-gradient(circle at 12% 4%, rgba(51,136,255,.24), transparent 32%),
    radial-gradient(circle at 88% 0%, rgba(246,179,43,.14), transparent 32%),
    radial-gradient(circle at 50% 38%, rgba(50,230,165,.08), transparent 36%),
    linear-gradient(rgba(255,255,255,.026) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.026) 1px, transparent 1px);
  background-size:auto,auto,auto,120px 120px,120px 120px;
  -webkit-mask-image:linear-gradient(to bottom, black 0%, black 62%, transparent 100%);
  mask-image:linear-gradient(to bottom, black 0%, black 62%, transparent 100%);
}
.mg-wrap{width:min(1440px,92vw);margin-inline:auto}
.mg-small-wrap{width:min(1180px,92vw);margin-inline:auto}
.mg-nav{position:fixed;top:0;left:0;right:0;z-index:1000;border-bottom:1px solid rgba(255,255,255,.075);background:rgba(5,8,13,.75);backdrop-filter:blur(22px)}
.mg-nav-inner{height:78px;display:flex;align-items:center;justify-content:space-between;gap:24px}
.mg-brand{display:flex;align-items:center;gap:12px;font-weight:900;letter-spacing:-.03em;white-space:nowrap}
.mg-brand img{width:42px;height:42px;object-fit:contain}
.mg-brand span{font-size:20px}
.mg-menu{display:flex;align-items:center;gap:28px;color:#aeb7c6;font-size:14px}
.mg-menu a{transition:.18s ease}.mg-menu a:hover{color:white}
.mg-nav-actions{display:flex;gap:10px;align-items:center}
.mg-btn{display:inline-flex;align-items:center;justify-content:center;gap:10px;min-height:48px;padding:0 20px;border-radius:14px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.045);color:#fff;font-weight:850;box-shadow:inset 0 1px 0 rgba(255,255,255,.06);transition:.22s ease;cursor:pointer}
.mg-btn:hover{transform:translateY(-2px);border-color:rgba(255,255,255,.26);background:rgba(255,255,255,.075)}
.mg-btn-primary{background:linear-gradient(135deg,#3990ff,#0f61dd);border-color:rgba(96,165,250,.72);box-shadow:0 20px 55px rgba(51,136,255,.30),inset 0 1px 0 rgba(255,255,255,.22)}
.mg-btn-gold{background:linear-gradient(135deg,rgba(246,179,43,.28),rgba(246,179,43,.10));border-color:rgba(246,179,43,.42);color:#ffe2a0}
.mg-eyebrow{display:inline-flex;align-items:center;gap:10px;padding:10px 16px;border:1px solid rgba(51,136,255,.34);border-radius:999px;background:rgba(51,136,255,.09);color:#8fc1ff;font-weight:950;letter-spacing:.065em;font-size:13px;text-transform:uppercase}
.mg-hero{padding:138px 0 44px;position:relative}
.mg-hero-grid{display:grid;grid-template-columns:1fr 1.16fr;gap:64px;align-items:center}
.mg-hero h1{font-size:clamp(48px,6.4vw,104px);line-height:.94;letter-spacing:-.084em;margin:24px 0 24px;max-width:850px}
.mg-hero h1 .mg-accent{color:#4d9cff}
.mg-lead{font-size:clamp(18px,1.42vw,23px);color:#aeb8c8;max-width:760px;margin:0}
.mg-hero-actions{display:flex;gap:14px;margin:34px 0 22px;flex-wrap:wrap}
.mg-trust-row{display:flex;gap:16px;flex-wrap:wrap;color:#93a0b3;font-size:14px}.mg-trust-row span:before{content:"✓";color:var(--green);margin-right:8px;font-weight:900}
.mg-quick-stats{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px;margin-top:28px;max-width:640px}
.mg-stat{min-width:0;min-height:148px;padding:22px 26px;border:1px solid rgba(255,255,255,.11);border-radius:24px;background:rgba(255,255,255,.044);display:flex;flex-direction:column;justify-content:center}
.mg-stat span{display:block;font-size:15px;color:#8e99aa;margin-bottom:10px;line-height:1.25}
.mg-stat b{display:block;font-size:clamp(32px,3vw,46px);line-height:1.02;letter-spacing:-.045em;white-space:nowrap;color:#fff}
.mg-stat.mg-green b{color:var(--green)}.mg-stat.mg-gold b{color:var(--gold)}
.mg-hero-card{position:relative;padding:14px;border:1px solid rgba(255,255,255,.12);border-radius:34px;background:linear-gradient(180deg,rgba(255,255,255,.08),rgba(255,255,255,.025));box-shadow:var(--shadow)}
.mg-hero-card::after{content:"";position:absolute;inset:auto 9% -24px 9%;height:50px;background:rgba(51,136,255,.26);filter:blur(44px);z-index:-1}
.mg-screen-frame{overflow:hidden;border-radius:24px;border:1px solid rgba(255,255,255,.12);background:#0b0f16}
.mg-screen-frame img{width:100%;height:auto}
.mg-live-badge{position:absolute;right:28px;top:28px;z-index:5;display:flex;align-items:center;gap:9px;padding:10px 14px;border-radius:999px;background:rgba(7,13,20,.78);border:1px solid rgba(50,230,165,.28);color:#baf9df;font-size:13px;font-weight:850;backdrop-filter:blur(16px)}
.mg-pulse{width:8px;height:8px;border-radius:99px;background:var(--green);box-shadow:0 0 0 8px rgba(50,230,165,.12)}
.mg-landing section{padding:68px 0}.mg-section-tight{padding:48px 0}.mg-section-head{text-align:center;margin-bottom:34px}.mg-section-head .mg-eyebrow{margin-bottom:18px}
.mg-section-head h2{font-size:clamp(38px,5vw,82px);line-height:1;letter-spacing:-.074em;margin:0 0 18px}
.mg-section-head p{font-size:clamp(17px,1.34vw,22px);color:#aeb8c8;max-width:920px;margin:0 auto}
.mg-pain-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
.mg-pain-card{position:relative;overflow:hidden;padding:26px;border:1px solid var(--line);border-radius:24px;background:linear-gradient(180deg,rgba(255,255,255,.055),rgba(255,255,255,.022));min-height:250px}
.mg-pain-card::before{content:"";position:absolute;inset:0 0 auto 0;height:2px;background:linear-gradient(90deg,var(--blue),transparent)}
.mg-pain-card h3{font-size:23px;margin:16px 0 10px;letter-spacing:-.03em}.mg-pain-card p{color:#aeb8c8;margin:0}
.mg-pain-icon{width:52px;height:52px;border-radius:18px;background:rgba(51,136,255,.1);border:1px solid rgba(51,136,255,.24);display:grid;place-items:center;font-size:24px}
.mg-split{display:grid;grid-template-columns:.86fr 1.14fr;gap:36px;align-items:center}.mg-split.mg-reverse{grid-template-columns:1.14fr .86fr}
.mg-copy-card{padding:36px;border:1px solid var(--line);border-radius:var(--radius);background:linear-gradient(180deg,rgba(255,255,255,.065),rgba(255,255,255,.025));box-shadow:0 18px 70px rgba(0,0,0,.22)}
.mg-copy-card h3{font-size:clamp(31px,3.2vw,56px);line-height:1;letter-spacing:-.058em;margin:0 0 18px}.mg-copy-card p{color:#aab4c5;font-size:18px;margin:0}.mg-copy-card p+p{margin-top:14px}.mg-copy-card .mg-mini{margin-top:24px}
.mg-checks{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:28px}.mg-check{padding:12px 14px;border-radius:14px;background:rgba(255,255,255,.045);border:1px solid rgba(255,255,255,.08);color:#d9e1ee;font-weight:750}.mg-check::before{content:"✓";color:var(--green);margin-right:9px;font-weight:950}
.mg-callout{margin-top:18px;padding:18px 20px;border-radius:18px;border:1px solid rgba(50,230,165,.28);background:rgba(50,230,165,.07);color:#cfe9dc;font-size:16px;line-height:1.55}
.mg-callout strong{color:#a9f5d7;display:block;margin-bottom:4px;font-size:17px}
.mg-image-card{position:relative;overflow:hidden;border-radius:30px;border:1px solid rgba(255,255,255,.12);background:#0c1119;box-shadow:var(--shadow)}.mg-image-card img,.mg-image-card video{width:100%;height:auto;display:block}.mg-image-card.mg-portrait{max-width:480px;margin-inline:auto;max-height:560px;display:flex;align-items:center;justify-content:center}.mg-image-card.mg-portrait img,.mg-image-card.mg-portrait video{max-height:560px;width:auto;object-fit:contain}
.mg-image-card .mg-label{position:absolute;left:18px;bottom:18px;padding:11px 14px;border-radius:999px;background:rgba(5,8,13,.76);border:1px solid rgba(255,255,255,.11);backdrop-filter:blur(16px);font-weight:850;color:#e8eef8;font-size:13px}
.mg-features{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
.mg-feature-card{position:relative;overflow:hidden;padding:28px;border:1px solid var(--line);border-radius:24px;background:linear-gradient(180deg,rgba(255,255,255,.055),rgba(255,255,255,.022));min-height:245px}
.mg-feature-card h3{font-size:24px;margin:18px 0 9px}.mg-feature-card p{color:#aeb8c8;margin:0}
.mg-audience{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
.mg-audience-card{position:relative;overflow:hidden;padding:26px;border:1px solid var(--line);border-radius:22px;background:linear-gradient(180deg,rgba(255,255,255,.05),rgba(255,255,255,.02));text-align:center}
.mg-audience-card .mg-ico{margin:0 auto 14px}
.mg-audience-card h3{font-size:19px;margin:0 0 8px;letter-spacing:-.02em}
.mg-audience-card p{color:#aeb8c8;margin:0;font-size:14px}
.mg-ico{width:54px;height:54px;border-radius:18px;display:grid;place-items:center;background:rgba(51,136,255,.10);border:1px solid rgba(51,136,255,.22);font-size:25px}
.mg-feature-card:nth-child(2n) .mg-ico{background:rgba(50,230,165,.09);border-color:rgba(50,230,165,.22)}
.mg-feature-card:nth-child(3n) .mg-ico{background:rgba(246,179,43,.10);border-color:rgba(246,179,43,.24)}
.mg-app-showcase{display:grid;grid-template-columns:340px 1fr;gap:24px;align-items:start}
.mg-tabs{position:sticky;top:100px;display:grid;gap:10px}
.mg-tab{width:100%;text-align:left;padding:16px 18px;border-radius:17px;border:1px solid rgba(255,255,255,.09);background:rgba(255,255,255,.035);color:#b8c2d1;font-weight:900;cursor:pointer;font-family:inherit}
.mg-tab.mg-active{background:linear-gradient(135deg,rgba(51,136,255,.22),rgba(51,136,255,.08));border-color:rgba(51,136,255,.44);color:white}
.mg-tab small{display:block;color:#8793a5;font-weight:600;margin-top:3px}
.mg-app-panel{border:1px solid rgba(255,255,255,.12);border-radius:30px;background:rgba(255,255,255,.035);box-shadow:var(--shadow);overflow:hidden}
.mg-app-image{display:none}.mg-app-image.mg-active{display:block}.mg-app-image img{width:100%}
.mg-app-copy{padding:22px 24px;border-top:1px solid rgba(255,255,255,.08);display:flex;justify-content:space-between;gap:20px;align-items:center}
.mg-app-copy p{color:#aeb8c8;margin:0}
.mg-pill{display:inline-flex;align-items:center;gap:8px;padding:8px 12px;border-radius:999px;border:1px solid rgba(50,230,165,.24);color:#a9f5d7;background:rgba(50,230,165,.08);font-weight:800;font-size:13px;white-space:nowrap}
.mg-comparison{display:grid;grid-template-columns:1fr 1fr;gap:18px}
.mg-compare-card{border:1px solid var(--line);border-radius:28px;padding:30px;background:linear-gradient(180deg,rgba(255,255,255,.055),rgba(255,255,255,.022))}
.mg-compare-card h3{font-size:32px;margin:0 0 20px;letter-spacing:-.04em}
.mg-compare-list{display:grid;gap:13px}.mg-compare-list div{padding:14px 16px;border-radius:16px;background:rgba(255,255,255,.04);color:#d5ddeb}
.mg-bad .mg-compare-list>div::before{content:"×";color:var(--red);margin-right:10px;font-weight:950}
.mg-good .mg-compare-list>div::before{content:"✓";color:var(--green);margin-right:10px;font-weight:950}
.mg-timeline{position:relative;display:grid;gap:24px}
.mg-timeline::before{content:"";position:absolute;left:50%;top:20px;bottom:20px;width:1px;background:linear-gradient(transparent,rgba(255,255,255,.18),transparent)}
.mg-milestone{display:grid;grid-template-columns:1fr 78px 1fr;gap:24px;align-items:center}
.mg-milestone:nth-child(even) .mg-m-photo{grid-column:3}
.mg-milestone:nth-child(even) .mg-m-copy{grid-column:1;grid-row:1;text-align:right}
.mg-dot{grid-column:2;grid-row:1;width:58px;height:58px;border-radius:999px;background:#0d1420;border:1px solid rgba(51,136,255,.4);display:grid;place-items:center;justify-self:center;color:#8fc1ff;font-weight:900;box-shadow:0 0 0 8px rgba(51,136,255,.08)}
.mg-m-photo{overflow:hidden;border-radius:24px;border:1px solid rgba(255,255,255,.12);box-shadow:0 25px 70px rgba(0,0,0,.35);height:300px}
.mg-m-photo img{width:100%;height:100%;object-fit:cover}
.mg-m-copy{padding:26px;border-radius:24px;border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.035)}
.mg-m-copy b{color:var(--gold);letter-spacing:.08em;text-transform:uppercase;font-size:13px}
.mg-m-copy h3{font-size:30px;margin:6px 0 10px;letter-spacing:-.035em}.mg-m-copy p{color:#aeb8c8;margin:0}
.mg-photo-grid{display:grid;grid-template-columns:1.15fr .85fr;gap:16px}
.mg-photo-tile{position:relative;overflow:hidden;border-radius:24px;border:1px solid rgba(255,255,255,.1);background:#0c1119;min-height:235px;display:block}
.mg-photo-tile img{width:100%;height:100%;object-fit:cover;transition:transform .7s ease}
.mg-photo-tile:hover img{transform:scale(1.05)}
.mg-photo-tile.mg-large{grid-row:span 2;min-height:520px}
.mg-caption{position:absolute;left:16px;right:16px;bottom:16px;display:flex;justify-content:space-between;gap:12px;align-items:end;padding:14px;border-radius:16px;background:linear-gradient(180deg,rgba(9,13,20,.2),rgba(9,13,20,.78));border:1px solid rgba(255,255,255,.08);backdrop-filter:blur(14px)}
.mg-caption strong{display:block}.mg-caption span{color:#b5bfce;font-size:13px}
.mg-gallery{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
.mg-gallery a{height:230px;overflow:hidden;border-radius:22px;border:1px solid rgba(255,255,255,.1);position:relative;display:block}
.mg-gallery img{width:100%;height:100%;object-fit:cover;transition:.55s ease}
.mg-gallery a:hover img{transform:scale(1.06);filter:saturate(1.08)}
.mg-cta{padding:92px 64px;border:1px solid rgba(51,136,255,.32);border-radius:36px;background:radial-gradient(circle at right,rgba(246,179,43,.18),transparent 35%),linear-gradient(135deg,rgba(51,136,255,.22),rgba(51,136,255,.06));display:flex;align-items:center;justify-content:space-between;gap:28px;box-shadow:var(--shadow)}
.mg-cta h2{font-size:clamp(36px,4.4vw,72px);line-height:1;letter-spacing:-.07em;margin:0 0 14px}
.mg-cta p{font-size:20px;color:#c5cfde;margin:0;max-width:760px}
.mg-footer{padding:34px 0;border-top:1px solid rgba(255,255,255,.08);color:#8390a4}
.mg-footer .mg-wrap{display:flex;align-items:center;justify-content:space-between;gap:20px}
.mg-footer .mg-brand img{width:34px;height:34px}.mg-footer .mg-brand span{font-size:18px;color:#fff}
.mg-reveal{opacity:0;transform:translateY(28px);transition:opacity .8s ease,transform .8s ease}
.mg-reveal.mg-in{opacity:1;transform:none}
.mg-lightbox{position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,.86);display:none;align-items:center;justify-content:center;padding:32px}
.mg-lightbox.mg-open{display:flex}
.mg-lightbox img{max-width:min(1200px,92vw);max-height:86vh;border-radius:22px;box-shadow:0 30px 120px #000}
.mg-lightbox button{position:absolute;top:24px;right:24px;width:48px;height:48px;border-radius:999px;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.08);color:white;font-size:30px;cursor:pointer}
@media (max-width:1100px){
  .mg-menu{display:none}
  .mg-hero-grid,.mg-split,.mg-split.mg-reverse{grid-template-columns:1fr}
  .mg-quick-stats{grid-template-columns:repeat(2,1fr)}
  .mg-pain-grid,.mg-features,.mg-audience{grid-template-columns:repeat(2,1fr)}
  .mg-app-showcase{grid-template-columns:1fr}
  .mg-tabs{position:static;grid-template-columns:repeat(2,1fr)}
  .mg-comparison{grid-template-columns:1fr}
  .mg-photo-grid{grid-template-columns:1fr}
  .mg-photo-tile.mg-large{min-height:380px}
  .mg-gallery{grid-template-columns:repeat(2,1fr)}
  .mg-timeline::before{left:29px}
  .mg-milestone,.mg-milestone:nth-child(even){grid-template-columns:58px 1fr;gap:16px}
  .mg-dot,.mg-milestone:nth-child(even) .mg-dot{grid-column:1}
  .mg-m-photo,.mg-milestone:nth-child(even) .mg-m-photo{grid-column:2}
  .mg-m-copy,.mg-milestone:nth-child(even) .mg-m-copy{grid-column:2;grid-row:auto;text-align:left}
}

/* — sekundarni hero lead — */
.mg-lead-sub{font-size:clamp(16px,1.15vw,19px);color:#93a0b3;margin-top:16px;max-width:720px}
.mg-trust-note{display:inline-flex;align-items:center;gap:8px;margin-top:22px;color:#93a0b3;font-size:14px}
.mg-trust-note:before{content:"✓";color:var(--green);font-weight:900}
/* — PROBLEM vprašanja — */
.mg-q-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-top:8px}
.mg-q-card{padding:22px 24px;border:1px solid var(--line);border-radius:18px;background:linear-gradient(180deg,rgba(255,255,255,.05),rgba(255,255,255,.02));font-size:18px;font-weight:700;color:#e8eef8;letter-spacing:-.02em;position:relative;padding-left:48px}
.mg-q-card:before{content:"?";position:absolute;left:20px;top:50%;transform:translateY(-50%);width:24px;height:24px;border-radius:999px;background:rgba(51,136,255,.16);border:1px solid rgba(51,136,255,.34);color:#8fc1ff;font-weight:900;display:flex;align-items:center;justify-content:center;font-size:14px}
.mg-punch{font-size:clamp(18px,1.5vw,24px);color:#aeb8c8;margin:0}
.mg-punch-big{font-size:clamp(24px,3vw,44px);font-weight:800;letter-spacing:-.05em;line-height:1.08;margin:10px 0 0;background:linear-gradient(120deg,#fff,#8fc1ff);-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent}
/* — FINANČNI pregled — */
.mg-fin-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
.mg-fin-grid .mg-stat.mg-wide{grid-column:span 4}
/* — KAKO DELUJE koraki — */
.mg-steps{display:grid;grid-template-columns:repeat(4,1fr);gap:18px}
.mg-step{position:relative;padding:30px 26px 26px;border:1px solid var(--line);border-radius:24px;background:linear-gradient(180deg,rgba(255,255,255,.055),rgba(255,255,255,.022))}
.mg-step-num{width:44px;height:44px;border-radius:14px;background:linear-gradient(135deg,#3990ff,#0f61dd);color:#fff;font-weight:900;font-size:20px;display:flex;align-items:center;justify-content:center;margin-bottom:18px;box-shadow:0 12px 30px rgba(51,136,255,.3)}
.mg-step h3{font-size:21px;margin:0 0 10px;letter-spacing:-.03em}
.mg-step p{color:#aeb8c8;margin:0;font-size:15px}
/* — Excel primerjava dodatki — */
.mg-bad .mg-compare-list>div.mg-manual::before{content:"~"!important;color:var(--gold)!important}
/* — FINAL CTA slogan — */
.mg-cta-slogan{font-size:clamp(20px,2vw,28px);font-weight:800;letter-spacing:-.04em;margin-top:20px!important;color:#fff}
.mg-cta-action{display:flex;flex-direction:column;align-items:flex-end;gap:2px;flex-shrink:0}
.mg-cta-action .mg-trust-note{margin-top:10px;color:#c5cfde}

@media (max-width:700px){
  .mg-nav-inner{height:70px}
  .mg-brand span{font-size:17px}
  .mg-nav-actions .mg-btn:not(.mg-btn-primary){display:none}
  .mg-hero{padding-top:108px}
  .mg-quick-stats,.mg-pain-grid,.mg-features,.mg-audience,.mg-checks,.mg-tabs,.mg-gallery{grid-template-columns:1fr}
  .mg-stat b{font-size:38px}
  .mg-section-head h2{letter-spacing:-.055em}
  .mg-app-copy,.mg-cta,.mg-footer .mg-wrap{display:block}
  .mg-cta .mg-btn{margin-top:24px;width:100%}
  .mg-m-photo{height:235px}
  .mg-photo-tile,.mg-gallery a{height:230px}
  .mg-copy-card{padding:26px}
  .mg-hero h1{letter-spacing:-.06em}
  .mg-q-grid,.mg-fin-grid,.mg-steps{grid-template-columns:1fr}
  .mg-fin-grid .mg-stat.mg-wide{grid-column:span 1}
  .mg-cta-action{align-items:stretch;margin-top:24px}
  .mg-cta-action .mg-btn{width:100%}
}
`;

// ============================================================
// SEZNAM SLIK, KI JIH MORAŠ DODATI v public/ (root, brez assets podmape):
//
// Logo / favicon:
//   logo-header.png
//
// Screenshoti aplikacije (real screenshots iz Moja Gradnja):
//   stroskovnik.jpg, napredek.jpg, pregled.jpg, terminski.jpg,
//   analiza.jpg, dokumenti.jpg, kontakti.jpg
//
// Video (avtomatska zanka v "Simulacija" sekciji):
//   quick-setup-demo.mp4
//
// Fotografije gradbišča:
//   gradnja-temelji.jpg, gradnja-beton.jpg, gradnja-opaz.jpg,
//   gradnja-zerjav.jpg, gradnja-elektro.jpg, gradnja-jama.jpg,
//   gradnja-plosca.jpg, gradnja-zidava.jpg, gradnja-armatura.jpg,
//   faza-priprava-temeljne-plosce.jpg, faza-zakljucena-plosca.jpg,
//   faza-zidava-rast-objekta.jpg
// ============================================================


// ─── Login ekran ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [geslo, setGeslo] = useState("");
  const [geslo2, setGeslo2] = useState("");
  const [nacin, setNacin] = useState("prijava");
  const [napaka, setNapaka] = useState("");
  const [loading, setLoading] = useState(false);
  const [potrditevPoslana, setPotrditevPoslana] = useState(false);
  const [resetPoslan, setResetPoslan] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const submit = async () => {
    setNapaka("");
    if (!email.trim()) { setNapaka("Vnesite email."); return; }
    if (nacin === "reset") {
      setLoading(true);
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
        if (error) setNapaka("Napaka: " + error.message);
        else setResetPoslan(true);
      } finally { setLoading(false); }
      return;
    }
    if (!geslo.trim()) { setNapaka("Vnesite geslo."); return; }
    if (geslo.length < 6) { setNapaka("Geslo mora imeti vsaj 6 znakov."); return; }
    if (nacin === "registracija") {
      if (!geslo2.trim()) { setNapaka("Potrdite geslo."); return; }
      if (geslo !== geslo2) { setNapaka("Gesli se ne ujemata."); return; }
    }
    setLoading(true);
    try {
      if (nacin === "prijava") {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password: geslo });
        if (error) setNapaka("Napačen email ali geslo.");
        else onLogin(data.user);
      } else {
        const { error } = await supabase.auth.signUp({ email, password: geslo });
        if (error) setNapaka(error.message);
        else setPotrditevPoslana(true);
      }
    } finally { setLoading(false); }
  };

  if (resetPoslan) return (
    <div style={{fontFamily:"'Syne',sans-serif",background:"#F5F6F8",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(59,130,246,0.3)",borderRadius:16,padding:40,width:"min(400px,90vw)",textAlign:"center"}}>
        <div style={{fontSize:36,marginBottom:16}}>📧</div>
        <div style={{fontSize:15,fontWeight:700,color:"#1A1D24",marginBottom:10}}>Email poslan!</div>
        <div style={{fontSize:13,color:"#5A6472",lineHeight:1.6}}>Poslali smo link za reset gesla na <b style={{color:"#1A1D24"}}>{email}</b>.<br/>Preverite email in sledite navodilom.<br/><span style={{fontSize:11,color:"#9AA3B0"}}>Če emaila ne vidite, preverite tudi mapo Spam ali Junk.</span></div>
        <button onClick={()=>{setResetPoslan(false);setNacin("prijava");}} style={{marginTop:20,background:"rgba(59,130,246,0.15)",border:"1px solid rgba(59,130,246,0.3)",color:"#2563EB",borderRadius:8,padding:"8px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Nazaj na prijavo</button>
      </div>
    </div>
  );

  if (potrditevPoslana) return (
    <div style={{fontFamily:"'Syne',sans-serif",background:"#F5F6F8",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(52,211,153,0.3)",borderRadius:16,padding:40,width:"min(400px,90vw)",textAlign:"center"}}>
        <div style={{fontSize:36,marginBottom:16}}>📧</div>
        <div style={{fontSize:15,fontWeight:700,color:"#1A1D24",marginBottom:10}}>Preverite email</div>
        <div style={{fontSize:13,color:"#5A6472",lineHeight:1.6}}>Poslali smo potrditveni email na <b style={{color:"#1A1D24"}}>{email}</b>.<br/>Kliknite na link v emailu in se nato prijavite.<br/><span style={{fontSize:11,color:"#9AA3B0"}}>Če emaila ne vidite, preverite tudi mapo Spam ali Junk.</span></div>
        <button onClick={()=>setPotrditevPoslana(false)} style={{marginTop:20,background:"rgba(59,130,246,0.15)",border:"1px solid rgba(59,130,246,0.3)",color:"#2563EB",borderRadius:8,padding:"8px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Nazaj na prijavo</button>
      </div>
    </div>
  );

  return (
    <div style={{fontFamily:"'Syne',sans-serif",background:"#F5F6F8",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:16,padding:36,width:"min(400px,90vw)"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:28}}>
          <img src="/logo-header.png" alt="Moja Gradnja" style={{width:40,height:40,objectFit:"contain"}}/>
          <div><div style={{fontSize:15,fontWeight:700,color:"#1A1D24"}}>MOJA GRADNJA</div><div style={{fontSize:11,color:"#5A6472"}}>Sledenje vaše gradnje</div></div>
        </div>

        {nacin !== "reset" && (
          <div style={{display:"flex",background:"rgba(0,0,0,0.04)",borderRadius:8,padding:3,marginBottom:24}}>
            {[["prijava","Prijava"],["registracija","Registracija"]].map(([k,l])=>(
              <button key={k} onClick={()=>{setNacin(k);setNapaka("");setGeslo("");setGeslo2("");}} style={{flex:1,padding:"7px",borderRadius:6,border:"none",background:nacin===k?"rgba(59,130,246,0.2)":"transparent",color:nacin===k?"#2563EB":"#9AA3B0",fontSize:13,fontFamily:"inherit",cursor:"pointer",fontWeight:nacin===k?600:400}}>{l}</button>
            ))}
          </div>
        )}
        {nacin === "reset" && <div style={{fontSize:13,color:"#5A6472",marginBottom:20}}>Vnesite email — poslali vam bomo link za ponastavitev gesla.</div>}

        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,color:"#5A6472",marginBottom:5,letterSpacing:1}}>EMAIL</div>
          <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="vas@email.si"
            onKeyDown={e=>e.key==="Enter"&&submit()}
            style={{width:"100%",background:"rgba(0,0,0,0.056)",border:"1px solid rgba(0,0,0,0.096)",borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"10px 12px"}}/>
        </div>

        {nacin !== "reset" && (
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:"#5A6472",marginBottom:5,letterSpacing:1}}>GESLO</div>
            <input type="password" value={geslo} onChange={e=>setGeslo(e.target.value)} placeholder="najmanj 6 znakov"
              autoComplete={nacin==="prijava"?"current-password":"new-password"}
              onKeyDown={e=>e.key==="Enter"&&submit()}
              style={{width:"100%",background:"rgba(0,0,0,0.056)",border:`1px solid ${geslo&&geslo.length<6?"rgba(239,68,68,0.4)":"rgba(0,0,0,0.096)"}`,borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"10px 12px"}}/>
            {geslo&&geslo.length<6&&<div style={{fontSize:11,color:"#DC2626",marginTop:4}}>Vsaj 6 znakov</div>}
          </div>
        )}

        {nacin === "registracija" && (
          <div style={{marginBottom:20}}>
            <div style={{fontSize:11,color:"#5A6472",marginBottom:5,letterSpacing:1}}>POTRDI GESLO</div>
            <input type="password" value={geslo2} onChange={e=>setGeslo2(e.target.value)} placeholder="ponovite geslo"
              autoComplete="new-password" onKeyDown={e=>e.key==="Enter"&&submit()}
              style={{width:"100%",background:"rgba(0,0,0,0.056)",border:`1px solid ${geslo2&&geslo!==geslo2?"rgba(239,68,68,0.4)":geslo2&&geslo===geslo2?"rgba(52,211,153,0.4)":"rgba(0,0,0,0.096)"}`,borderRadius:8,color:"#1A1D24",fontFamily:"inherit",fontSize:13,padding:"10px 12px"}}/>
            {geslo2&&geslo===geslo2&&<div style={{fontSize:11,color:"#10B981",marginTop:4}}>✓ Gesli se ujemata</div>}
            {geslo2&&geslo!==geslo2&&<div style={{fontSize:11,color:"#DC2626",marginTop:4}}>✗ Gesli se ne ujemata</div>}
          </div>
        )}

        {nacin === "prijava" && <div style={{marginBottom:20}}/>}

        {napaka&&<div style={{fontSize:12,color:"#DC2626",marginBottom:14,padding:"8px 12px",background:"rgba(239,68,68,0.1)",borderRadius:6}}>{napaka}</div>}

        <button onClick={submit} disabled={loading} style={{width:"100%",background:"rgba(59,130,246,0.2)",border:"1px solid rgba(59,130,246,0.4)",color:"#2563EB",borderRadius:8,padding:"11px",fontSize:13,fontFamily:"inherit",cursor:"pointer",fontWeight:600,opacity:loading?0.7:1,marginBottom:12}}>
          {loading?"Nalaganje...":(nacin==="prijava"?"Prijava →":nacin==="registracija"?"Ustvari račun →":"Pošlji reset email →")}
        </button>

        {nacin==="prijava"&&<div style={{textAlign:"center"}}><button onClick={()=>{setNacin("reset");setNapaka("");}} style={{background:"transparent",border:"none",color:"#5A6472",fontSize:11,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>Pozabljeno geslo?</button></div>}
        {nacin==="reset"&&<div style={{textAlign:"center"}}><button onClick={()=>{setNacin("prijava");setNapaka("");}} style={{background:"transparent",border:"none",color:"#5A6472",fontSize:11,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>← Nazaj na prijavo</button></div>}

        <div style={{textAlign:"center",marginTop:16}}>
          <button onClick={()=>setShowPrivacy(true)} style={{background:"transparent",border:"none",color:"#5A6472",fontSize:11,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>Politika zasebnosti</button>
        </div>

        {showPrivacy&&<PrivacyModal onClose={()=>setShowPrivacy(false)}/>}
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────

export default function App() {
  const [projects, setProjects] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [showNewProj, setShowNewProj] = useState(false);
  const [confirmDelProj, setConfirmDelProj] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [user, setUser] = useState(null);
  const [resetMode, setResetMode] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // ── Prijava / nalaganje ob zagonu ──

  useEffect(() => {
    if (!supabase) {
      setProjects([mkProject("Stanovanjska hiša Kovač","Ljubljana, Šiška","🏠","#3B82F6",true)]);
      setLoading(false);
      return;
    }

    let loadingNow = false;
    let loadedOk = false;

    const doLoad = async (uid, attempt = 0) => {
      if (loadingNow || loadedOk || !uid) return;
      loadingNow = true;
      let ok = false;
      try {
        const { data, error } = await supabase.from('projects').select('id, data, user_id').order('created_at');
        if (!error && Array.isArray(data)) {
          setProjects(data.map(r => ({
            ...r.data, _dbId: r.id, _ownerId: r.user_id,
            _role: (r.user_id === uid) ? 'owner' : 'editor'
          })));
          loadedOk = true;
          ok = true;
        } else if (error) {
          console.warn('load error (attempt ' + attempt + '):', error.message);
        }
      } catch(e) { console.warn('load failed', e); }
      loadingNow = false;
      setLoading(false);
      // Če ni uspelo in nismo presegli poskusov, poskusi znova
      if (!ok && attempt < 2) {
        setTimeout(() => doLoad(uid, attempt + 1), 800);
      }
    };

    // Samo onAuthStateChange — sproži se z INITIAL_SESSION (token iz localStorage),
    // SIGNED_IN (prijava), TOKEN_REFRESHED. Brez ločenega getSession → ni auth locka.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setResetMode(true);
        setLoading(false);
        return;
      }
      if (session?.user) {
        setUser(session.user);
        setUserId(session.user.id);
        if (event === 'SIGNED_IN') loadedOk = false;
        await doLoad(session.user.id);
      } else {
        setUser(null); setUserId(null);
        if (event === 'SIGNED_OUT') { loadedOk = false; setProjects([]); }
      }
      setLoading(false);
    });

    const fallback = setTimeout(() => setLoading(false), 4000);
    return () => { subscription.unsubscribe(); clearTimeout(fallback); };
  }, []);

  // Tiho osveži seznam projektov ob vrnitvi na zavihek (npr. ko te nekdo doda na projekt)
  useEffect(() => {
    let refreshing = false;
    const refresh = async () => {
      if (refreshing || document.hidden || !supabase || !userId) return;
      refreshing = true;
      try {
        const myUid = userId;
        const { data, error } = await supabase.from('projects').select('id, data, user_id').order('created_at');
        if (!error && data) {
          setProjects(data.map(r => ({
            ...r.data, _dbId: r.id, _ownerId: r.user_id,
            _role: (r.user_id === myUid) ? 'owner' : 'editor'
          })));
        }
      } catch(_) {}
      refreshing = false;
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [userId]);

  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [userMenu, setUserMenu] = useState(false);

  // ── Shrani projekt v Supabase ──
  const saveToSupabase = async (project) => {
    if (!supabase) {
 return; }
    let uid = userId;
    if (!uid) {
      try {
        const st = new Promise(res => setTimeout(() => res({ data: { session: null } }), 2000));
        const { data: { session } } = await Promise.race([supabase.auth.getSession(), st]);
        uid = session?.user?.id;
      } catch(_) {}
    }
    if (!uid) {
      return;
    }
    setSaveStatus('saving');
    try {
      const { _dbId, ...data } = project;
      if (_dbId) {
        const { error } = await supabase.from('projects').update({ data, updated_at: new Date() }).eq('id', _dbId);
        if (error) {
 setSaveStatus('error'); return; }
      } else {
        const { data: row, error } = await supabase.from('projects').insert({ data, user_id: uid }).select('id').single();
        if (error) {
 setSaveStatus('error'); return; }
        if (row) setProjects(prev => prev.map(p => p.id === project.id ? { ...p, _dbId: row.id } : p));
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch(e) {
      setSaveStatus('error');
    }
  };

  const openProject = projects.find(p=>p.id===openId);

  const addProject = (naziv, lokacija, ikona, barva, demo, budgetSetup) => {
    const tip = budgetSetup?.tip || "gradnja";
    let p = mkProject(naziv, lokacija, ikona, barva, demo, tip);
    // Apply quick setup if provided
    if (budgetSetup && (budgetSetup.lastna > 0 || budgetSetup.kredit > 0 || budgetSetup.parcela > 0)) {
      const { lastna, kredit, povrsina, parcela } = budgetSetup;
      p = { ...p, lastna, kredit, povrsina, parcela };
      // Pri prenovi ne razporejamo % samodejno
      if (tip !== "prenova") {
        const total = lastna + kredit;
        const distributable = Math.max(0, total - parcela);
        const PARCELA_ID = p.kats.find(k=>k.naziv==="Parcela & Komunalna ureditev")?.id;
        p.kats = p.kats.map(k => {
          if (k.id === PARCELA_ID) {
            return { ...k, delez: 0, postavke: k.postavke.map(post => post.isSkupaj ? { ...post, budget: parcela } : { ...post, budget: 0 }) };
          }
          const d = KAT_DELEŽI[k.naziv] || 0;
          const katBudget = Math.round(distributable * d / 100);
          return { ...k, delez: d, postavke: k.postavke.map(post => post.isSkupaj ? { ...post, budget: katBudget } : { ...post, budget: 0 }) };
        });
      }
    }
    setProjects(prev => [...prev, p]);
    setOpenId(p.id);
    setShowNewProj(false);
    saveToSupabase(p);
  };

  const updateProject = (id, fn) => {
    setProjects(prev => {
      const next = prev.map(p => {
        if (p.id !== id) return p;
        const updated = fn(p);
        saveToSupabase(updated);
        return { ...updated };  // novi objekt da React zazna spremembo
      });
      return [...next];  // novi array da React zagotovo re-rendira
    });
  };

  const deleteProject = async (id) => {
    const p = projects.find(x => x.id === id);
    if (supabase && p?._dbId) {
      await supabase.from('projects').delete().eq('id', p._dbId);
    }
    setProjects(prev => prev.filter(p => p.id !== id));
    if (openId === id) setOpenId(null);
  };

  if (loading) return (
    <div style={{fontFamily:"'Syne',sans-serif",background:"#F5F6F8",minHeight:"100vh",color:"#1A1D24",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16}}>
      <div style={{fontSize:36}}>🏗️</div>
      <div style={{fontSize:15,fontWeight:600,color:"#1A1D24"}}>Nalaganje...</div>
    </div>
  );

  // Zaslon za vnos novega gesla po reset linku
  if (resetMode) return <ResetGesloScreen onDone={()=>setResetMode(false)}/>;

  // Če ni prijavljen in je Supabase aktiven → pokaži login
  if (supabase && !user && !showLogin) return <LandingPage onStart={()=>setShowLogin(true)} onLogin={()=>setShowLogin(true)}/>;
  if (supabase && !user) return <LoginScreen onLogin={u=>{setUser(u);setUserId(u.id);}}/>;

  return (
    <div style={{fontFamily:"'Syne',sans-serif",background:"#F5F6F8",minHeight:"100vh",color:"#1A1D24",display:"flex",flexDirection:"column"}}>
      <style>{CSS}</style>
      {confirm&&<ConfirmModal msg={confirm.msg} onOk={()=>{confirm.ok();setConfirm(null);}} onCancel={()=>setConfirm(null)}/>}
      {showPrivacy&&<PrivacyModal onClose={()=>setShowPrivacy(false)}/>}
      {showNewProj&&<NewProjectModal onSave={addProject} onClose={()=>setShowNewProj(false)}/>}
      {confirmDelProj&&<ConfirmModal msg={<>Izbriši projekt <b style={{color:"#fff"}}>{confirmDelProj.name}</b>?</>} onOk={()=>{deleteProject(confirmDelProj.id);setConfirmDelProj(null);}} onCancel={()=>setConfirmDelProj(null)}/>}

      {/* top bar */}
      <header style={{background:"#FFFFFF",borderBottom:"1px solid rgba(0,0,0,0.048)",minHeight:64,display:"flex",alignItems:"center",padding:"0 14px",gap:10,flexShrink:0,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",flexShrink:0}} onClick={()=>setOpenId(null)}>
          <div style={{width:40,height:40,borderRadius:12,background:"rgba(0,0,0,0.032)",border:"1px solid rgba(0,0,0,0.072)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><img src="/logo-header.png" alt="Moja Gradnja" style={{width:28,height:28,objectFit:"contain"}}/></div>
          <div style={{display:"none"}}><div style={{fontSize:13,fontWeight:700,color:"#1A1D24",letterSpacing:0.3}}>GRADNJA</div><div style={{fontSize:11,color:"#5A6472",letterSpacing:2}}>SLEDILNIK</div></div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,fontSize:15,flex:1,minWidth:0}}>
          <span style={{color:openId?"#9AA3B0":"#1A1D24",cursor:"pointer",fontWeight:openId?400:600,flexShrink:0}} onClick={()=>setOpenId(null)}>Projekti</span>
          {openProject&&<><span style={{color:"#5A6472"}}>›</span><span style={{color:"#1A1D24",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{openProject.naziv}</span></>}
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0,flexWrap:"wrap"}}>
          {saveStatus!=='idle'&&(
            <div style={{position:"fixed",bottom:"calc(84px + env(safe-area-inset-bottom))",left:"50%",transform:"translateX(-50%)",zIndex:3000,
              background:saveStatus==='error'?"rgba(45,12,14,0.96)":"rgba(13,19,26,0.96)",
              border:"1px solid "+(saveStatus==='error'?"rgba(239,68,68,0.45)":saveStatus==='saved'?"rgba(52,211,153,0.45)":"rgba(0,0,0,0.12)"),
              color:saveStatus==='error'?"#DC2626":saveStatus==='saved'?"#059669":"#4B5563",
              borderRadius:12,padding:"9px 16px",fontSize:13,fontWeight:600,display:"flex",gap:10,alignItems:"center",
              boxShadow:"0 10px 40px rgba(0,0,0,0.12)",pointerEvents:saveStatus==='error'?"auto":"none",whiteSpace:"nowrap"}}>
              {saveStatus==='saving'&&<><Save size={14}/> Shranjujem…</>}
              {saveStatus==='saved'&&<><Check size={14}/> Shranjeno</>}
              {saveStatus==='error'&&<><AlertTriangle size={14}/> Napaka pri shranjevanju
                <button onClick={()=>{const p=projects.find(x=>x.id===openId);if(p)saveToSupabase(p);}}
                  style={{background:"rgba(239,68,68,0.2)",border:"1px solid rgba(239,68,68,0.4)",color:"#DC2626",borderRadius:8,padding:"4px 10px",fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Poskusi znova</button></>}
            </div>
          )}
          {projects.length>0&&(
            <div style={{position:"relative"}}>
              <select onChange={e=>{if(e.target.value) setOpenId(e.target.value);}} value={openId||""}
                style={{background:"rgba(0,0,0,0.04)",border:"1px solid rgba(0,0,0,0.088)",color:"#1A1D24",borderRadius:999,padding:"0 30px 0 15px",height:38,fontSize:13,fontFamily:"inherit",cursor:"pointer",appearance:"none",maxWidth:170}}>
                <option value="">— Projekt —</option>
                {projects.map(p=><option key={p.id} value={p.id}>{p.ikona} {p.naziv}</option>)}
              </select>
              <span style={{position:"absolute",right:11,top:"50%",transform:"translateY(-50%)",color:"#5A6472",pointerEvents:"none",display:"inline-flex"}}><ChevronDown size={15}/></span>
            </div>
          )}
          <button onClick={()=>setShowNewProj(true)} className="T" style={{background:"linear-gradient(135deg,#3B82F6,#2563EB)",border:"1px solid rgba(96,165,250,0.5)",color:"#1A1D24",height:38,padding:"0 18px",fontSize:13,fontWeight:700,borderRadius:999,display:"inline-flex",alignItems:"center",gap:7,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",boxShadow:"0 4px 18px rgba(59,130,246,0.35)"}}><Plus size={16}/> Nov projekt</button>
          {supabase&&user?(
            <div style={{position:"relative"}}>
              <button onClick={()=>setUserMenu(v=>!v)} className="T" title={user.email}
                style={{width:38,height:38,borderRadius:"50%",background:userMenu?"rgba(59,130,246,0.3)":"rgba(59,130,246,0.15)",border:"1px solid rgba(59,130,246,0.4)",color:"#2563EB",fontWeight:700,fontSize:15,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {(user.email||"?")[0].toUpperCase()}
              </button>
              {userMenu&&(<>
                <div onClick={()=>setUserMenu(false)} style={{position:"fixed",inset:0,zIndex:1290}}/>
                <div style={{position:"absolute",right:0,top:42,zIndex:1300,background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.08)",borderRadius:14,boxShadow:"0 18px 50px rgba(0,0,0,0.12)",minWidth:220,padding:6}}>
                  <div style={{padding:"9px 11px 11px",fontSize:11,color:"#5A6472",borderBottom:"1px solid rgba(0,0,0,0.056)",marginBottom:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.email}</div>
                  <button className="hov-kat" onClick={()=>{setShowPrivacy(true);setUserMenu(false);}}
                    style={{display:"flex",alignItems:"center",gap:9,width:"100%",textAlign:"left",background:"transparent",border:"none",color:"#1A1D24",borderRadius:9,padding:"9px 11px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}><Lock size={15}/> Zasebnost</button>
                  <button className="hov-kat" onClick={async()=>{
                    setUserMenu(false);
                    try { await Promise.race([supabase.auth.signOut(), new Promise(r=>setTimeout(r,1500))]); } catch(_){}
                    try { localStorage.clear(); } catch(_){}
                    window.location.reload();
                  }}
                    style={{display:"flex",alignItems:"center",gap:9,width:"100%",textAlign:"left",background:"transparent",border:"none",color:"#1A1D24",borderRadius:9,padding:"9px 11px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}><LogOut size={15}/> Odjava</button>
                  <div style={{height:1,background:"rgba(0,0,0,0.056)",margin:"6px 0"}}/>
                  <button className="hov-kat" onClick={()=>{setUserMenu(false);setConfirm({
                    msg:<><b style={{color:"#DC2626"}}>Izbriši račun?</b><br/><span style={{fontSize:12,color:"#5A6472"}}>Izbrisali se bodo vsi vaši projekti in podatki. Tega dejanja ni mogoče razveljaviti.</span></>,
                    ok: async ()=>{
                      await supabase.from('projects').delete().eq('user_id', userId);
                      await supabase.auth.signOut();
                      setConfirm(null);
                    }
                  });}}
                    style={{display:"flex",alignItems:"center",gap:9,width:"100%",textAlign:"left",background:"transparent",border:"none",color:"#DC2626",borderRadius:9,padding:"9px 11px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}><Trash2 size={15}/> Izbriši račun</button>
                </div>
              </>)}
            </div>
          ):(
            <button onClick={()=>setShowPrivacy(true)} className="T" style={{background:"rgba(0,0,0,0.04)",border:"1px solid rgba(0,0,0,0.08)",color:"#5A6472",borderRadius:999,height:38,padding:"0 14px",fontSize:12,cursor:"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center",gap:6}}><Lock size={15}/> Zasebnost</button>
          )}
        </div>
      </header>

      <div style={{display:"flex",flex:1,overflow:"hidden"}}>
        {!openId ? (
          <Dashboard projects={projects} onOpen={setOpenId} onNew={()=>setShowNewProj(true)} onDelete={(id,name)=>setConfirmDelProj({id,name})}/>
        ) : openProject ? (
          <ProjectView key={openId} project={openProject} onUpdate={fn=>updateProject(openId,fn)} onBack={()=>setOpenId(null)} userId={userId}/>
        ) : null}
      </div>
    </div>
  );
}
